/**
 * API Client for Fias Platform
 */

import { traceApiRequest, traceApiResponse, traceApiError } from './telemetry';
import { maintenanceState } from './maintenance';
import { attachCsrfHeader } from './csrf';
import { refreshAuthSession, notifySessionExpired } from './auth-refresh';

// Use VITE_API_URL for production (separate API subdomain)
// Fall back to /v1 for local development (proxied by Vite)
//
// Codespaces override: when the browser is on *.app.github.dev, the user's
// laptop can't reach the Codespace's `localhost:4000`. Force relative URLs so
// Vite's dev-server proxy (see vite.config.ts) forwards the call server-side.
// Runs at runtime so a .env.local with VITE_API_URL=http://localhost:4000/v1
// stays correct on WSL/native dev where browser and API share localhost.
const isCodespacesHost =
  typeof window !== 'undefined' && window.location.hostname.endsWith('.app.github.dev');

export const API_BASE = isCodespacesHost ? '/v1' : import.meta.env.VITE_API_URL || '/v1';

// Same Codespaces override for WebSockets. The WS service is on port 4001
// inside the Codespace, but the laptop browser can't reach it directly.
// Connect to the page's own origin on path `/stream` and let Vite's proxy
// (with ws:true) upgrade and forward to ws://localhost:4001/stream.
// Falls back to VITE_WEBSOCKET_ENDPOINT on WSL/native dev (where the env
// var points directly at ws://localhost:4001/stream).
export const WEBSOCKET_ENDPOINT = (() => {
  const envValue = import.meta.env.VITE_WEBSOCKET_ENDPOINT || '';
  if (!isCodespacesHost) return envValue;
  const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${wsProtocol}//${window.location.host}/stream`;
})();

export class ApiError extends Error {
  /**
   * For INSUFFICIENT_CREDITS errors, true when the user's total balance
   * across every bucket cannot cover the request — i.e. they need to top
   * up before they can do anything else. The global request interceptor
   * uses this to auto-navigate to the credits store.
   */
  public readonly allBucketsExhausted?: boolean;
  public readonly isCoreSystem?: boolean;

  constructor(
    message: string,
    public readonly status: number,
    public readonly code?: string,
    extras?: { allBucketsExhausted?: boolean; isCoreSystem?: boolean },
  ) {
    super(message);
    this.name = 'ApiError';
    if (extras) {
      this.allBucketsExhausted = extras.allBucketsExhausted;
      this.isCoreSystem = extras.isCoreSystem;
    }
  }
}

/** Extracted fields from a response body that the API client raises as an `ApiError`. */
export interface ExtractedApiError {
  message: string;
  code?: string;
  allBucketsExhausted?: boolean;
  isCoreSystem?: boolean;
}

/**
 * Single source of truth for turning a non-OK response body into a
 * user-readable error. Covers every envelope the platform produces:
 *
 *   - Lambda / Fastify route handlers: `{ error: { code, message } }`.
 *   - Legacy APIs: `{ error: "string" }`.
 *   - Fastify default errors: `{ statusCode, error: "Bad Request", message: "real text" }`
 *     — `message` is the real explanation; `error` is just the status name. Prefer `message`.
 *   - API Gateway / Cognito / WAF: `{ message: "..." }`.
 *   - Non-JSON text bodies (nginx 502 HTML, ALB plain text, Cognito unauthorized, …):
 *     surfaced verbatim when short, replaced with a generic message for HTML.
 *
 * The default fallback names the HTTP status so even an unrecognized
 * envelope tells the user what went wrong.
 */
export function extractErrorFromResponse(
  data: unknown,
  responseText: string,
  status: number,
): ExtractedApiError {
  type ErrorEnvelope = {
    error?:
      | string
      | {
          code?: string;
          message?: string;
          allBucketsExhausted?: boolean;
          isCoreSystem?: boolean;
        };
    message?: string;
  };
  const errorData = data as ErrorEnvelope | null;

  let message = `Request failed (status ${status})`;
  let code: string | undefined;
  let allBucketsExhausted: boolean | undefined;
  let isCoreSystem: boolean | undefined;

  if (errorData) {
    if (typeof errorData.error === 'object' && errorData.error?.message) {
      message = errorData.error.message;
      code = errorData.error.code;
      allBucketsExhausted = errorData.error.allBucketsExhausted;
      isCoreSystem = errorData.error.isCoreSystem;
    } else if (typeof errorData.error === 'string' && errorData.error.length > 0) {
      // Fastify default errors put the human-readable text in `message` and
      // the status name in `error` — surface `message` when present so users
      // see "Body must be a valid JSON" instead of "Bad Request".
      message = errorData.message ?? errorData.error;
    } else if (errorData.message) {
      message = errorData.message;
    }
  } else if (responseText.trim().length > 0 && responseText.length < 500) {
    // Surface short non-JSON bodies verbatim, but skip HTML pages — a 400-byte
    // chunk of nginx HTML in a toast is ugly and unhelpful.
    message = /^\s*<[!a-z]/i.test(responseText)
      ? `Server error (status ${status})`
      : responseText.trim();
  }

  return { message, code, allBucketsExhausted, isCoreSystem };
}

/**
 * Reads a `Response` body once, returning both the raw text (always) and
 * a parsed JSON form (when the body looks like JSON). Returning both
 * lets the caller surface non-JSON bodies verbatim while still using
 * `data` to read structured error envelopes.
 */
async function readResponseBody(response: Response): Promise<{
  data: unknown;
  responseText: string;
}> {
  const responseText = await response.text();
  let data: unknown = null;
  if (responseText.length > 0) {
    try {
      data = JSON.parse(responseText);
    } catch {
      // Leave data null; the caller's error path will fall back to responseText.
    }
  }
  return { data, responseText };
}

class ApiClient {
  private async request<T>(
    method: string,
    path: string,
    body?: unknown,
    options?: { signal?: AbortSignal; isRetry?: boolean },
  ): Promise<T> {
    const url = `${API_BASE}${path}`;
    const traceId = traceApiRequest(method, url, body);

    const headers: Record<string, string> = {};

    // Only set Content-Type if there's a body to send
    if (body !== undefined) {
      headers['Content-Type'] = 'application/json';
    }

    // Cookie-based auth: cookies are auto-attached via credentials:'include'.
    // For unsafe verbs we also echo the CSRF cookie value as a header so
    // the backend's double-submit check passes (see lib/csrf.ts).
    attachCsrfHeader(method, headers);

    let response: Response;
    try {
      response = await fetch(url, {
        method,
        headers,
        credentials: 'include',
        body: body !== undefined ? JSON.stringify(body) : undefined,
        signal: options?.signal,
      });
    } catch (err) {
      traceApiError(traceId, err instanceof Error ? err : new Error(String(err)));
      throw err;
    }

    // Any successful response from the API means maintenance is no longer
    // blocking us — clear the global flag so the SPA stops showing the
    // maintenance page on the next render.
    if (response.ok && maintenanceState.getState().isActive) {
      maintenanceState.clearActive();
    }

    // Handle 204 No Content responses (e.g., DELETE operations)
    if (response.status === 204) {
      traceApiResponse(traceId, response.status, null);
      return undefined as T;
    }

    const { data, responseText } = await readResponseBody(response);
    traceApiResponse(traceId, response.status, data ?? responseText);

    // Transparent recovery from session-cookie expiry. Try one refresh
    // and replay the request before surfacing the 401 to the caller.
    // Skipped for /auth/* (login/refresh have their own flow — looping
    // would interfere with bootstrap), and for the retry attempt
    // itself (single retry, no loops).
    if (
      response.status === 401 &&
      !options?.isRetry &&
      !path.startsWith('/auth/') &&
      !(options?.signal?.aborted ?? false)
    ) {
      const refreshed = await refreshAuthSession();
      if (refreshed) {
        return this.request<T>(method, path, body, { ...options, isRetry: true });
      }
      // Refresh-token rejected: prompt the user to sign in again. The
      // throw below still happens so the caller's existing error
      // handling fires — the modal is purely additive UX.
      notifySessionExpired();
    }

    if (!response.ok) {
      this.throwApiErrorFromBody(data, responseText, response);
    }

    // OK status but body wasn't valid JSON — preserve the long-standing
    // contract that JSON endpoints throw a parse error rather than
    // silently returning null. (The 204 path above already covers
    // intentionally-empty success bodies.)
    if (responseText.length > 0 && data === null) {
      const requestSize = body ? JSON.stringify(body).length : 0;
      traceApiError(traceId, `Failed to parse JSON response (status: ${response.status})`, {
        status: response.status,
        responseText,
        responseSize: responseText.length,
        requestSize,
      });
      throw new Error(`Failed to parse response (status: ${response.status})`);
    }

    return data as T;
  }

  /**
   * Builds an `ApiError` from a non-OK response body, fires the
   * platform-wide side effects (maintenance-mode banner, credit-exhaustion
   * navigation event), and throws. Both `request` and `requestBinary`
   * delegate here so every endpoint surfaces errors the same way.
   */
  private throwApiErrorFromBody(data: unknown, responseText: string, response: Response): never {
    const extracted = extractErrorFromResponse(data, responseText, response.status);

    // Detect maintenance mode — set global state so the UI can react.
    if (response.status === 503 && extracted.code === 'MAINTENANCE_MODE') {
      maintenanceState.setActive(extracted.message);
    }

    // Dispatch a custom event so a top-level React component can navigate
    // via react-router (preserving SPA state) instead of doing a hard
    // browser redirect from this non-component context. The listener
    // (CreditExhaustionListener mounted under the router) is the single
    // point that decides whether to navigate. We only fire on the most
    // decisive case — total exhaustion — so partial-balance scenarios
    // don't trigger an unwanted navigation.
    if (
      response.status === 402 &&
      extracted.code === 'INSUFFICIENT_CREDITS' &&
      extracted.allBucketsExhausted === true &&
      typeof window !== 'undefined'
    ) {
      window.dispatchEvent(new CustomEvent('fias:credits-exhausted'));
    }

    throw new ApiError(extracted.message, response.status, extracted.code, {
      allBucketsExhausted: extracted.allBucketsExhausted,
      isCoreSystem: extracted.isCoreSystem,
    });
  }

  /**
   * Public transport for arche-local API modules. Thin wrapper around the
   * private `request` method. Lets per-arche API files
   * (e.g. `client/src/arche/arches/<arche>/api/index.ts`) use the shared
   * fetch/CSRF/auth/retry/telemetry pipeline without growing the central
   * `ApiClient` class with arche-specific surface area. See
   * docs/audits/social-media-modularity-audit.md §10 PW-B.
   */
  public async httpRequest<T>(
    method: string,
    path: string,
    body?: unknown,
    options?: { signal?: AbortSignal },
  ): Promise<T> {
    return this.request<T>(method, path, body, options);
  }

  /**
   * PUT a binary blob (e.g. an image) and parse a JSON response. Mirrors
   * the auth + telemetry flow of `request()` but skips JSON serialization
   * of the body and lets the caller pin a specific Content-Type.
   */
  private async requestBinary<T>(
    method: 'PUT' | 'POST',
    path: string,
    body: Blob,
    contentType: string,
    options?: { signal?: AbortSignal; isRetry?: boolean },
  ): Promise<T> {
    const url = `${API_BASE}${path}`;
    const traceId = traceApiRequest(method, url, { contentType, byteLength: body.size });

    const headers: Record<string, string> = { 'Content-Type': contentType };
    attachCsrfHeader(method, headers);

    let response: Response;
    try {
      response = await fetch(url, {
        method,
        headers,
        credentials: 'include',
        body,
        signal: options?.signal,
      });
    } catch (err) {
      traceApiError(traceId, err instanceof Error ? err : new Error(String(err)));
      throw err;
    }

    if (response.ok && maintenanceState.getState().isActive) {
      maintenanceState.clearActive();
    }

    if (response.status === 204) {
      traceApiResponse(traceId, response.status, null);
      return undefined as T;
    }

    const { data, responseText } = await readResponseBody(response);
    traceApiResponse(traceId, response.status, data ?? responseText);

    // Same 401 → refresh-and-retry as `request()`. See that method for
    // the rationale and the guards.
    if (
      response.status === 401 &&
      !options?.isRetry &&
      !path.startsWith('/auth/') &&
      !(options?.signal?.aborted ?? false)
    ) {
      const refreshed = await refreshAuthSession();
      if (refreshed) {
        return this.requestBinary<T>(method, path, body, contentType, {
          ...options,
          isRetry: true,
        });
      }
      notifySessionExpired();
    }

    if (!response.ok) {
      this.throwApiErrorFromBody(data, responseText, response);
    }

    return data as T;
  }

  /**
   * Generic GET request with optional query parameters
   */
  async get<T>(path: string, options?: { params?: Record<string, string> }): Promise<{ data: T }> {
    let url = path;
    if (options?.params) {
      const params = new URLSearchParams(options.params);
      url = `${path}?${params.toString()}`;
    }
    const data = await this.request<T>('GET', url);
    return { data };
  }

  /**
   * Generic POST request. Cookie auth + CSRF handled by `request()`.
   */
  async post<T>(path: string, body?: Record<string, unknown>): Promise<{ data: T }> {
    const data = await this.request<T>('POST', path, body);
    return { data };
  }

  // Arche API
  async listArches(options?: { categoryId?: string; limit?: number; offset?: number }) {
    const params = new URLSearchParams();
    if (options?.categoryId) params.set('categoryId', options.categoryId);
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.offset) params.set('offset', options.offset.toString());
    const query = params.toString();
    return this.request<{ arches: unknown[]; total: number }>(
      'GET',
      `/arches${query ? `?${query}` : ''}`,
    );
  }

  async getArche(id: string) {
    return this.request<{ arche: unknown }>('GET', `/arches/${id}`);
  }

  /**
   * Gets lightweight arche metadata for route protection
   * Used to determine if an arche is public/fullscreen before loading full content
   */
  async getArcheMetadata(id: string) {
    return this.request<ArcheMetadata>('GET', `/arches/${id}/metadata`);
  }

  // Arche File Operations API

  /**
   * Write or manipulate a file in an arche's sandboxed folder
   */
  async archeFileWrite(
    archeId: string,
    payload: {
      filename: string;
      content: string;
      format: string;
      mode: string;
      separator?: string;
      section?: string;
      find?: string;
      requestId?: string;
    },
  ) {
    return this.request<{
      success: boolean;
      fileId: string;
      filename: string;
    }>('POST', `/arches/${archeId}/files/write`, payload);
  }

  /**
   * Read a file from an arche's sandboxed folder
   */
  async archeFileRead(archeId: string, options: { filename?: string; fileId?: string }) {
    const params = new URLSearchParams();
    if (options.filename) params.set('filename', options.filename);
    if (options.fileId) params.set('fileId', options.fileId);
    const query = params.toString();
    return this.request<{
      content: string;
      filename: string;
      fileId: string;
    }>('GET', `/arches/${archeId}/files/read${query ? `?${query}` : ''}`);
  }

  /**
   * Get a presigned download URL for a file in an arche's sandboxed folder
   */
  async archeFileDownloadUrl(archeId: string, options: { filename?: string; fileId?: string }) {
    const params = new URLSearchParams();
    if (options.filename) params.set('filename', options.filename);
    if (options.fileId) params.set('fileId', options.fileId);
    const query = params.toString();
    return this.request<{
      downloadUrl: string;
      filename: string;
    }>('GET', `/arches/${archeId}/files/download-url${query ? `?${query}` : ''}`);
  }

  /**
   * List files in an arche's sandboxed folder
   */
  async archeFileList(archeId: string) {
    return this.request<{
      files: Array<{ fileId: string; filename: string; format: string; createdAt: string }>;
    }>('GET', `/arches/${archeId}/files`);
  }

  // Entity API
  async listEntities(options?: { categoryId?: string; limit?: number; offset?: number }) {
    const params = new URLSearchParams();
    if (options?.categoryId) params.set('categoryId', options.categoryId);
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.offset) params.set('offset', options.offset.toString());
    const query = params.toString();
    return this.request<{ entities: unknown[]; total: number }>(
      'GET',
      `/entities${query ? `?${query}` : ''}`,
    );
  }

  async getEntity(id: string) {
    return this.request<{ entity: unknown }>('GET', `/entities/${id}`);
  }

  async invokeEntity(entityId: string, input: Record<string, unknown>) {
    return this.request<{
      output: unknown;
      usage?: {
        tokens?: { input: number; output: number };
        cost?: number;
      };
    }>('POST', `/entities/${entityId}/invoke`, { input });
  }

  // Category API
  async listCategories() {
    return this.request<{ categories: unknown[] }>('GET', '/categories');
  }

  async getCategory(id: string) {
    return this.request<{ category: unknown }>('GET', `/categories/${id}`);
  }

  // Workflow API
  async startArche(archeId: string, input?: Record<string, unknown>) {
    return this.request<{
      requestId: string;
      archId: string;
      status: 'pending' | 'running';
    }>('POST', `/arches/${archeId}/start`, { input });
  }

  async getRequestStatus(requestId: string) {
    return this.request<{
      requestId: string;
      archId: string;
      userId: string;
      status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled' | 'timed_out';
      input: Record<string, unknown> | null;
      output: Record<string, unknown> | null;
      startedAt: string;
      completedAt: string | null;
      totalCostUsd: number | null;
      errorMessage: string | null;
      nodes: Array<{
        nodeId: string;
        entityId: string | null;
        entityName: string | null;
        status: 'pending' | 'running' | 'completed' | 'failed' | 'skipped';
        input: Record<string, unknown> | null;
        output: Record<string, unknown> | null;
        startedAt: string | null;
        completedAt: string | null;
        costUsd: number | null;
        latencyMs: number | null;
        errorMessage: string | null;
      }>;
    }>('GET', `/requests/${requestId}`);
  }

  async listRequests(options?: {
    archId?: string;
    status?: string;
    limit?: number;
    offset?: number;
  }) {
    const params = new URLSearchParams();
    if (options?.archId) params.set('archId', options.archId);
    if (options?.status) params.set('status', options.status);
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.offset) params.set('offset', options.offset.toString());
    const query = params.toString();
    return this.request<{
      requests: Array<{
        requestId: string;
        archId: string;
        status: string;
        startedAt: string;
        completedAt: string | null;
      }>;
      total: number;
    }>('GET', `/requests${query ? `?${query}` : ''}`);
  }

  // ==========================================================================
  // Favorites API
  // ==========================================================================

  async getFavorites() {
    return this.request<{ favorites: UserFavoriteWithArche[] }>('GET', '/arches/favorites');
  }

  async addFavorite(archId: string) {
    return this.request<{ favorite: unknown }>('POST', '/arches/favorites', { archId });
  }

  async removeFavorite(archId: string) {
    return this.request<void>('DELETE', `/arches/favorites/${archId}`);
  }

  async checkFavorite(archId: string) {
    return this.request<{ isFavorite: boolean }>('GET', `/arches/favorites/check/${archId}`);
  }

  async reorderFavorites(placements: { archId: string; position: number }[]) {
    return this.request<{ success: boolean }>('PUT', '/arches/favorites/reorder', { placements });
  }

  // ==========================================================================
  // Marketplace API
  // ==========================================================================

  async searchMarketplace(options?: {
    q?: string;
    categoryId?: string;
    archeType?: ArcheType;
    tags?: string[];
    sortBy?: 'relevance' | 'fitness' | 'popular' | 'newest';
    limit?: number;
    offset?: number;
    enableSemanticSearch?: boolean;
  }) {
    const params = new URLSearchParams();
    if (options?.q) params.set('q', options.q);
    if (options?.categoryId) params.set('categoryId', options.categoryId);
    if (options?.archeType) params.set('archeType', options.archeType);
    if (options?.tags?.length) params.set('tags', options.tags.join(','));
    if (options?.sortBy) params.set('sortBy', options.sortBy);
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.offset) params.set('offset', options.offset.toString());
    if (options?.enableSemanticSearch === false) params.set('enableSemanticSearch', 'false');
    const query = params.toString();
    return this.request<MarketplaceSearchResult>(
      'GET',
      `/marketplace/search${query ? `?${query}` : ''}`,
    );
  }

  async getFeaturedArches(limit?: number) {
    const query = limit ? `?limit=${limit}` : '';
    return this.request<{ arches: MarketplaceArche[] }>('GET', `/marketplace/featured${query}`);
  }

  async getMarketplaceCategories() {
    return this.request<{ categories: MarketplaceCategory[] }>('GET', '/marketplace/categories');
  }

  async getMarketplaceCategoryArches(
    slug: string,
    options?: {
      sortBy?: 'fitness' | 'popular' | 'newest';
      limit?: number;
      offset?: number;
    },
  ) {
    const params = new URLSearchParams();
    if (options?.sortBy) params.set('sortBy', options.sortBy);
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.offset) params.set('offset', options.offset.toString());
    const query = params.toString();
    return this.request<{
      category: MarketplaceCategory;
      arches: MarketplaceArche[];
      total: number;
      limit: number;
      offset: number;
      hasMore: boolean;
    }>('GET', `/marketplace/categories/${slug}${query ? `?${query}` : ''}`);
  }

  async getMarketplaceArche(id: string) {
    return this.request<MarketplaceArcheDetail>('GET', `/marketplace/arches/${id}`);
  }

  // ==========================================================================
  // Marketplace Entity API
  // ==========================================================================

  async searchMarketplaceEntities(options?: {
    q?: string;
    categoryId?: string;
    type?: EntityType;
    sortBy?: 'relevance' | 'fitness' | 'popular' | 'newest';
    limit?: number;
    offset?: number;
  }) {
    const params = new URLSearchParams();
    if (options?.q) params.set('q', options.q);
    if (options?.categoryId) params.set('categoryId', options.categoryId);
    if (options?.type) params.set('type', options.type);
    if (options?.sortBy) params.set('sortBy', options.sortBy);
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.offset) params.set('offset', options.offset.toString());
    const query = params.toString();
    return this.request<MarketplaceEntitySearchResult>(
      'GET',
      `/marketplace/entities/search${query ? `?${query}` : ''}`,
    );
  }

  async getFeaturedEntities(limit?: number) {
    const query = limit ? `?limit=${limit}` : '';
    return this.request<{ entities: MarketplaceEntity[] }>(
      'GET',
      `/marketplace/entities/featured${query}`,
    );
  }

  async getMarketplaceEntity(id: string) {
    return this.request<MarketplaceEntityDetail>('GET', `/marketplace/entities/${id}`);
  }

  async getMarketplaceCategoriesWithEntityCounts() {
    return this.request<{ categories: MarketplaceCategoryWithEntityCount[] }>(
      'GET',
      '/marketplace/entity-categories',
    );
  }

  // ==========================================================================
  // Contributor API
  // ==========================================================================

  async getContributorMetrics() {
    return this.request<ContributorMetricsResult>('GET', '/contributor/entities/metrics');
  }

  async getContributorArcheDashboard() {
    return this.request<ContributorArcheDashboardResult>('GET', '/contributor/arches/dashboard');
  }

  // ==========================================================================
  // Credits API
  // ==========================================================================

  async getCreditBalance() {
    return this.request<CreditBalance>('GET', '/credits');
  }

  async getCreditSpendingSummary() {
    return this.request<{ categories: SpendingCategorySummary[] }>('GET', '/credits/summary');
  }

  async getCreditTransactions(options?: {
    type?: 'grant' | 'usage' | 'refund' | 'adjustment';
    limit?: number;
    offset?: number;
  }) {
    const params = new URLSearchParams();
    if (options?.type) params.set('type', options.type);
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.offset) params.set('offset', options.offset.toString());
    const query = params.toString();
    return this.request<CreditTransactionsResult>(
      'GET',
      `/credits/transactions${query ? `?${query}` : ''}`,
    );
  }

  async grantCredits(targetUserId: string, amount: number, description?: string) {
    return this.request<CreditGrantResult>('POST', '/admin/credits/grant', {
      userId: targetUserId,
      amount,
      description,
    });
  }

  // ==========================================================================
  // Payments API
  // ==========================================================================

  async getPaymentConfig() {
    return this.request<PaymentConfig>('GET', '/payments/config');
  }

  async createCheckoutSession(amountCents: number) {
    return this.request<CheckoutSessionResult>('POST', '/payments/checkout-session', {
      amountCents,
    });
  }

  async createCryptoCheckout(amountCents: number) {
    return this.request<CryptoCheckoutResult>('POST', '/payments/crypto/checkout', {
      amountCents,
    });
  }

  // ==========================================================================
  // Subscriptions API
  // ==========================================================================

  async getSubscriptionTiers() {
    return this.request<SubscriptionTiersResult>('GET', '/payments/subscriptions/tiers');
  }

  async getCurrentSubscription() {
    return this.request<SubscriptionInfo>('GET', '/payments/subscriptions/current');
  }

  async subscribeToTier(tier: 'standard' | 'pro') {
    return this.request<{ sessionUrl: string }>('POST', '/payments/subscriptions/subscribe', {
      tier,
    });
  }

  async cancelSubscription() {
    return this.request<{
      status: string;
      cancelAtPeriodEnd: boolean;
      currentPeriodEnd: string | null;
    }>('POST', '/payments/subscriptions/cancel');
  }

  async reactivateSubscription() {
    return this.request<{
      status: string;
      cancelAtPeriodEnd: boolean;
      currentPeriodEnd: string | null;
    }>('POST', '/payments/subscriptions/reactivate');
  }

  async getSubscriptionPortalUrl() {
    return this.request<{ url: string }>('GET', '/payments/subscriptions/portal');
  }

  async changeSubscriptionTier(tier: 'standard' | 'pro') {
    return this.request<SubscriptionInfo>('POST', '/payments/subscriptions/change-tier', { tier });
  }

  async syncSubscription() {
    return this.request<SubscriptionInfo>('POST', '/payments/subscriptions/sync');
  }

  // ==========================================================================
  // Admin Payments API
  // ==========================================================================

  // Maintenance Mode
  async adminGetMaintenanceStatus() {
    return this.request<MaintenanceStatus>('GET', '/admin/maintenance');
  }

  async adminSetMaintenanceMode(params: { enabled: boolean; message?: string }) {
    return this.request<MaintenanceStatus>('POST', '/admin/maintenance', params);
  }

  async adminGetPaymentsDashboard(period?: string) {
    return this.request<AdminPaymentsDashboard>(
      'GET',
      `/admin/payments/dashboard${period ? `?period=${period}` : ''}`,
    );
  }

  async adminGetPaymentsCosts(period?: string) {
    return this.request<AdminPaymentsCosts>(
      'GET',
      `/admin/payments/costs${period ? `?period=${period}` : ''}`,
    );
  }

  async adminProcessRefund(paymentId: string, reason?: string) {
    return this.request<{ success: boolean; paymentId: string }>('POST', '/admin/payments/refund', {
      paymentId,
      reason,
    });
  }

  async adminGrantCredits(userId: string, amount: number, description?: string) {
    return this.request<{ success: boolean; newBalance: number; transactionId: string }>(
      'POST',
      '/admin/payments/grant',
      { userId, amount, description },
    );
  }

  async adminGetUserPaymentDetail(userId: string) {
    return this.request<AdminUserPaymentDetail>('GET', `/admin/payments/users/${userId}`);
  }

  // ── Platform Usage Analytics (Layer 2 admin dashboard) ──────────────
  async adminGetAnalyticsOverview(period: AnalyticsPeriod, nocache = false) {
    const qs = new URLSearchParams({ period });
    if (nocache) qs.set('nocache', '1');
    return this.request<AdminAnalyticsOverview>('GET', `/admin/analytics/overview?${qs}`);
  }

  async adminGetAnalyticsEngagement(period: AnalyticsPeriod, nocache = false) {
    const qs = new URLSearchParams({ period });
    if (nocache) qs.set('nocache', '1');
    return this.request<AdminAnalyticsEngagement>('GET', `/admin/analytics/engagement?${qs}`);
  }

  async adminGetAnalyticsHealth(period: AnalyticsPeriod, nocache = false) {
    const qs = new URLSearchParams({ period });
    if (nocache) qs.set('nocache', '1');
    return this.request<AdminAnalyticsHealth>('GET', `/admin/analytics/health?${qs}`);
  }

  async adminGetAnalyticsUser(userId: string, period: AnalyticsPeriod, nocache = false) {
    const qs = new URLSearchParams({ period });
    if (nocache) qs.set('nocache', '1');
    return this.request<AdminAnalyticsUserDetail>(
      'GET',
      `/admin/analytics/users/${encodeURIComponent(userId)}?${qs}`,
    );
  }

  async adminSearchPayments(params: AdminPaymentSearchParams) {
    const qs = new URLSearchParams();
    if (params.status) qs.set('status', params.status);
    if (params.userId) qs.set('userId', params.userId);
    if (params.startDate) qs.set('startDate', params.startDate);
    if (params.endDate) qs.set('endDate', params.endDate);
    if (params.minAmountCents) qs.set('minAmountCents', String(params.minAmountCents));
    if (params.maxAmountCents) qs.set('maxAmountCents', String(params.maxAmountCents));
    if (params.limit) qs.set('limit', String(params.limit));
    if (params.offset) qs.set('offset', String(params.offset));
    const query = qs.toString();
    return this.request<AdminPaymentSearchResult>(
      'GET',
      `/admin/payments/search${query ? `?${query}` : ''}`,
    );
  }

  async adminListSubscriptions(params: AdminSubscriptionListParams) {
    const qs = new URLSearchParams();
    if (params.tier) qs.set('tier', params.tier);
    if (params.status) qs.set('status', params.status);
    if (params.limit) qs.set('limit', String(params.limit));
    if (params.offset) qs.set('offset', String(params.offset));
    const query = qs.toString();
    return this.request<AdminSubscriptionListResult>(
      'GET',
      `/admin/payments/subscriptions${query ? `?${query}` : ''}`,
    );
  }

  async adminChangeSubscriptionTier(userId: string, newTier: string) {
    return this.request<{ success: boolean; subscription: AdminSubscriptionRecord }>(
      'POST',
      `/admin/payments/subscriptions/${userId}/change-tier`,
      { newTier },
    );
  }

  async adminCancelSubscription(userId: string) {
    return this.request<{ success: boolean; subscription: AdminSubscriptionRecord }>(
      'POST',
      `/admin/payments/subscriptions/${userId}/cancel`,
    );
  }

  async adminReactivateSubscription(userId: string) {
    return this.request<{ success: boolean; subscription: AdminSubscriptionRecord }>(
      'POST',
      `/admin/payments/subscriptions/${userId}/reactivate`,
    );
  }

  async adminReconcileSubscription(userId: string) {
    return this.request<{ success: boolean; subscription: AdminSubscriptionRecord }>(
      'POST',
      `/admin/payments/subscriptions/${userId}/reconcile`,
    );
  }

  async adminListStripeEvents(params: AdminStripeEventListParams) {
    const qs = new URLSearchParams();
    if (params.eventType) qs.set('eventType', params.eventType);
    if (params.startDate) qs.set('startDate', params.startDate);
    if (params.endDate) qs.set('endDate', params.endDate);
    if (params.processedOk !== undefined) qs.set('processedOk', String(params.processedOk));
    if (params.limit) qs.set('limit', String(params.limit));
    if (params.offset) qs.set('offset', String(params.offset));
    const query = qs.toString();
    return this.request<AdminStripeEventListResult>(
      'GET',
      `/admin/payments/stripe-events${query ? `?${query}` : ''}`,
    );
  }

  async adminGetStripeEvent(eventId: string) {
    return this.request<AdminStripeEventDetail>('GET', `/admin/payments/stripe-events/${eventId}`);
  }

  async adminDeductCredits(userId: string, amount: number, bucket: string, reason: string) {
    return this.request<{ success: boolean; newBalance: number; transactionId: string }>(
      'POST',
      '/admin/payments/credits/deduct',
      { userId, amount, bucket, reason },
    );
  }

  async getPaymentHistory(options?: { limit?: number; offset?: number; status?: string }) {
    const params = new URLSearchParams();
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.offset) params.set('offset', options.offset.toString());
    if (options?.status) params.set('status', options.status);
    const query = params.toString();
    return this.request<PaymentHistoryResult>(
      'GET',
      `/payments/history${query ? `?${query}` : ''}`,
    );
  }

  // ==========================================================================
  // Models API
  // ==========================================================================

  async listModels(options?: {
    provider?: ModelProvider;
    tier?: 'fast' | 'balanced' | 'powerful';
  }) {
    const params = new URLSearchParams();
    if (options?.provider) params.set('provider', options.provider);
    if (options?.tier) params.set('tier', options.tier);
    const query = params.toString();
    return this.request<ModelListResponse>('GET', `/models${query ? `?${query}` : ''}`);
  }

  async getModel(id: string) {
    return this.request<ModelEntity>('GET', `/models/${id}`);
  }

  // ==========================================================================
  // Model Definitions API (Entity-driven parameter schema)
  // ==========================================================================

  /**
   * Lists model definitions with their parameter schemas.
   * Used by the frontend to render data-driven parameter controls.
   *
   * @param options.provider - Filter by provider (openai, anthropic, google)
   * @param options.tier - Filter by tier (fast, balanced, powerful)
   * @param options.modelType - Filter by model type (text, image)
   */
  async listModelDefinitions(options?: {
    provider?: ModelProvider;
    tier?: 'fast' | 'balanced' | 'powerful';
    modelType?: 'text' | 'image';
  }) {
    const params = new URLSearchParams();
    if (options?.provider) params.set('provider', options.provider);
    if (options?.tier) params.set('tier', options.tier);
    if (options?.modelType) params.set('modelType', options.modelType);
    const query = params.toString();
    return this.request<ModelDefinitionListResponse>(
      'GET',
      `/models/definitions${query ? `?${query}` : ''}`,
    );
  }

  /**
   * Gets a model definition by ID, including full parameter schema.
   */
  async getModelDefinition(id: string) {
    return this.request<ModelDefinition>('GET', `/models/definitions/${id}`);
  }

  /**
   * Gets a model definition by provider and model name.
   * Useful when you have the model name but not the entity ID.
   */
  async getModelDefinitionByLookup(provider: ModelProvider, modelName: string) {
    const params = new URLSearchParams();
    params.set('provider', provider);
    params.set('modelName', modelName);
    return this.request<ModelDefinition>('GET', `/models/definitions/lookup?${params.toString()}`);
  }

  // ==========================================================================
  // Conversations API
  // ==========================================================================

  async listConversations(options?: {
    archeId?: string;
    status?: 'active' | 'archived';
    limit?: number;
    offset?: number;
  }) {
    const params = new URLSearchParams();
    if (options?.archeId) params.set('archeId', options.archeId);
    if (options?.status) params.set('status', options.status);
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.offset) params.set('offset', options.offset.toString());
    const query = params.toString();
    return this.request<ConversationListResult>('GET', `/conversations${query ? `?${query}` : ''}`);
  }

  async getConversation(conversationId: string) {
    return this.request<ConversationWithMessages>('GET', `/conversations/${conversationId}`);
  }

  async createConversation(archeId: string, title?: string) {
    return this.request<ConversationResponse>('POST', '/conversations', { archeId, title });
  }

  async addMessages(conversationId: string, messages: ConversationMessageInput[]) {
    return this.request<{ messages: ConversationMessageResponse[] }>(
      'POST',
      `/conversations/${conversationId}/messages`,
      { messages },
    );
  }

  async addMessage(conversationId: string, message: ConversationMessageInput) {
    return this.request<{ messages: ConversationMessageResponse[] }>(
      'POST',
      `/conversations/${conversationId}/messages`,
      { message },
    );
  }

  async updateConversation(
    conversationId: string,
    updates: { title?: string; status?: 'active' | 'archived' },
  ) {
    return this.request<ConversationResponse>('PATCH', `/conversations/${conversationId}`, updates);
  }

  async deleteConversation(conversationId: string) {
    return this.request<void>('DELETE', `/conversations/${conversationId}`);
  }

  async deleteAllConversations() {
    return this.request<DeleteAllConversationsResult>('DELETE', '/conversations/all');
  }

  async exportConversation(conversationId: string, format: 'json' | 'markdown' = 'markdown') {
    // Export returns plain text (markdown or JSON string), not a JSON object
    // So we need to handle it separately from the standard request method
    const url = `${API_BASE}/conversations/${conversationId}/export?format=${format}`;

    const response = await fetch(url, { method: 'GET', credentials: 'include' });

    if (!response.ok) {
      throw new Error(`Export failed with status ${response.status}`);
    }

    return response.text();
  }

  async shareConversation(conversationId: string) {
    return this.request<ShareConversationResponse>(
      'POST',
      `/conversations/${conversationId}/share`,
      {},
    );
  }

  async getSharedConversation(shareId: string) {
    return this.request<SharedConversationResponse>('GET', `/shared/${shareId}`);
  }

  // ==========================================================================
  // Arche Session API
  // ==========================================================================

  async getArcheSession(archeId: string) {
    return this.request<ArcheSessionResponse>('GET', `/arches/${archeId}/session`);
  }

  async updateArcheSession(archeId: string, updates: ArcheSessionUpdateInput) {
    return this.request<ArcheSessionResponse>('PUT', `/arches/${archeId}/session`, updates);
  }

  async deleteArcheSession(archeId: string) {
    return this.request<void>('DELETE', `/arches/${archeId}/session`);
  }

  // ==========================================================================
  // Fias AI API
  // ==========================================================================

  async fiasAIChat(payload: {
    message: string;
    conversationId?: string;
    mode?: 'hybrid' | 'ai-only' | 'search-only';
    routingPreference?: 'auto' | 'speed' | 'balanced' | 'performance' | 'cost';
    conversationHistory?: Array<{ role: 'user' | 'assistant'; content: string }>;
    archeContext?: {
      archeId: string;
      state: Record<string, unknown>;
      actions: Array<{
        id: string;
        description: string;
        parameterSchema: Record<string, unknown>;
        requiresConfirmation?: boolean;
        category?: string;
      }>;
    };
  }) {
    return this.request<{
      requestId: string;
      conversationId: string;
      selectedModel: {
        provider: string;
        modelName: string;
        displayName: string;
        tier: string;
      };
      searchIncluded: boolean;
    }>('POST', '/arches/fias-ai/chat', payload);
  }

  async listFiasAIConversations(options?: { limit?: number; offset?: number }) {
    const params = new URLSearchParams();
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.offset) params.set('offset', options.offset.toString());
    const query = params.toString();
    return this.request<{
      conversations: Array<{
        conversationId: string;
        title: string | null;
        messageCount: number;
        createdAt: string;
        updatedAt: string;
      }>;
      total: number;
      limit: number;
      offset: number;
      hasMore: boolean;
    }>('GET', `/arches/fias-ai/conversations${query ? `?${query}` : ''}`);
  }

  async getFiasAIConversation(id: string) {
    return this.request<{
      conversationId: string;
      title: string | null;
      messages: Array<{
        messageId: string;
        role: 'user' | 'assistant';
        content: string;
        provider: string | null;
        modelName: string | null;
        createdAt: string;
      }>;
    }>('GET', `/arches/fias-ai/conversations/${id}`);
  }

  async deleteFiasAIConversation(id: string) {
    return this.request<void>('DELETE', `/arches/fias-ai/conversations/${id}`);
  }

  // ==========================================================================
  // Contact Book API
  // ==========================================================================

  async listContacts(options?: {
    categoryId?: string;
    priorityId?: string;
    search?: string;
    dateFrom?: string;
    dateTo?: string;
    status?: 'active' | 'archived' | 'deleted';
    sortBy?: 'name' | 'gatheredAt' | 'company' | 'createdAt';
    sortDir?: 'asc' | 'desc';
    limit?: number;
    offset?: number;
  }) {
    const params = new URLSearchParams();
    if (options?.categoryId) params.set('categoryId', options.categoryId);
    if (options?.priorityId) params.set('priorityId', options.priorityId);
    if (options?.search) params.set('search', options.search);
    if (options?.dateFrom) params.set('dateFrom', options.dateFrom);
    if (options?.dateTo) params.set('dateTo', options.dateTo);
    if (options?.status) params.set('status', options.status);
    if (options?.sortBy) params.set('sortBy', options.sortBy);
    if (options?.sortDir) params.set('sortDir', options.sortDir);
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.offset) params.set('offset', options.offset.toString());
    const query = params.toString();
    return this.request<{ contacts: ContactDetail[]; total: number }>(
      'GET',
      `/arches/contact-book/contacts${query ? `?${query}` : ''}`,
    );
  }

  async getContact(id: string) {
    return this.request<ContactDetail>('GET', `/arches/contact-book/contacts/${id}`);
  }

  async createContact(input: ContactInput) {
    return this.request<ContactDetail>('POST', '/arches/contact-book/contacts', input);
  }

  async updateContact(id: string, input: Partial<ContactInput>) {
    return this.request<ContactDetail>('PUT', `/arches/contact-book/contacts/${id}`, input);
  }

  async deleteContact(id: string) {
    return this.request<void>('DELETE', `/arches/contact-book/contacts/${id}`);
  }

  async archiveContact(id: string, archived: boolean) {
    return this.request<ContactDetail>('PATCH', `/arches/contact-book/contacts/${id}/archive`, {
      archived,
    });
  }

  async restoreContact(id: string) {
    return this.request<ContactDetail>('PATCH', `/arches/contact-book/contacts/${id}/restore`);
  }

  async permanentlyDeleteContact(id: string) {
    return this.request<void>('DELETE', `/arches/contact-book/contacts/${id}/permanent`);
  }

  async emptyTrash() {
    return this.request<{ deletedCount: number }>('DELETE', '/arches/contact-book/trash');
  }

  async getTrashCount() {
    return this.request<{ count: number }>('GET', '/arches/contact-book/trash/count');
  }

  async listContactCategories() {
    return this.request<{ categories: ContactCategory[] }>(
      'GET',
      '/arches/contact-book/categories',
    );
  }

  async createContactCategory(input: ContactCategoryInput) {
    return this.request<ContactCategory>('POST', '/arches/contact-book/categories', input);
  }

  async updateContactCategory(id: string, input: Partial<ContactCategoryInput>) {
    return this.request<ContactCategory>('PUT', `/arches/contact-book/categories/${id}`, input);
  }

  async deleteContactCategory(id: string) {
    return this.request<{ uncategorizedCount: number }>(
      'DELETE',
      `/arches/contact-book/categories/${id}`,
    );
  }

  async reorderContactCategories(categoryIds: string[]) {
    return this.request<{ categories: ContactCategory[] }>(
      'PUT',
      '/arches/contact-book/categories/reorder',
      { categoryIds },
    );
  }

  async listContactPriorities() {
    return this.request<{ priorities: ContactPriority[] }>(
      'GET',
      '/arches/contact-book/priorities',
    );
  }

  async createContactPriority(input: ContactPriorityInput) {
    return this.request<ContactPriority>('POST', '/arches/contact-book/priorities', input);
  }

  async updateContactPriority(id: string, input: Partial<ContactPriorityInput>) {
    return this.request<ContactPriority>('PUT', `/arches/contact-book/priorities/${id}`, input);
  }

  async deleteContactPriority(id: string) {
    return this.request<void>('DELETE', `/arches/contact-book/priorities/${id}`);
  }

  async listContactAttachments(contactId: string) {
    return this.request<{ attachments: ContactAttachment[] }>(
      'GET',
      `/arches/contact-book/contacts/${contactId}/attachments`,
    );
  }

  async addContactAttachment(contactId: string, fileId: string, label?: string) {
    return this.request<ContactAttachment>(
      'POST',
      `/arches/contact-book/contacts/${contactId}/attachments`,
      { fileId, label },
    );
  }

  async removeContactAttachment(contactId: string, attachmentId: string) {
    return this.request<void>(
      'DELETE',
      `/arches/contact-book/contacts/${contactId}/attachments/${attachmentId}`,
    );
  }

  // ==========================================================================
  // Calendar API
  // ==========================================================================

  async listCalendarCollections() {
    return this.request<{ calendars: CalendarCollectionDetail[] }>(
      'GET',
      '/arches/calendar/calendars',
    );
  }

  async createCalendarCollection(input: CalendarCollectionInput) {
    return this.request<CalendarCollectionDetail>('POST', '/arches/calendar/calendars', input);
  }

  async updateCalendarCollection(id: string, input: Partial<CalendarCollectionInput>) {
    return this.request<CalendarCollectionDetail>('PUT', `/arches/calendar/calendars/${id}`, input);
  }

  async deleteCalendarCollection(id: string) {
    return this.request<void>('DELETE', `/arches/calendar/calendars/${id}`);
  }

  async listCalendarEvents(options: {
    rangeStart: string;
    rangeEnd: string;
    calendarId?: string;
    search?: string;
    status?: string;
  }) {
    const params = new URLSearchParams();
    params.set('rangeStart', options.rangeStart);
    params.set('rangeEnd', options.rangeEnd);
    if (options.calendarId) params.set('calendarId', options.calendarId);
    if (options.search) params.set('search', options.search);
    if (options.status) params.set('status', options.status);
    return this.request<{ events: CalendarEventInstance[] }>(
      'GET',
      `/arches/calendar/events?${params.toString()}`,
    );
  }

  async getCalendarEvent(id: string) {
    return this.request<CalendarEventDetail>('GET', `/arches/calendar/events/${id}`);
  }

  async createCalendarEvent(input: CalendarEventCreateInput) {
    return this.request<CalendarEventDetail>('POST', '/arches/calendar/events', input);
  }

  async updateCalendarEvent(
    id: string,
    input: Partial<CalendarEventCreateInput> & {
      scope?: 'this' | 'this_and_future' | 'all';
      instanceDate?: string;
    },
  ) {
    return this.request<CalendarEventDetail>('PUT', `/arches/calendar/events/${id}`, input);
  }

  async deleteCalendarEvent(
    id: string,
    scope?: 'this' | 'this_and_future' | 'all',
    instanceDate?: string,
  ) {
    const params = new URLSearchParams();
    if (scope) params.set('scope', scope);
    if (instanceDate) params.set('instanceDate', instanceDate);
    const query = params.toString();
    return this.request<void>('DELETE', `/arches/calendar/events/${id}${query ? `?${query}` : ''}`);
  }

  async countDeletedCalendarEvents() {
    return this.request<{ count: number }>('GET', '/arches/calendar/events/deleted/count');
  }

  async listDeletedCalendarEvents() {
    return this.request<{ events: CalendarEventDetail[] }>(
      'GET',
      '/arches/calendar/events/deleted',
    );
  }

  async restoreCalendarEvent(eventId: string) {
    return this.request<CalendarEventDetail>('POST', `/arches/calendar/events/${eventId}/restore`);
  }

  async clearCalendarEvents(
    calendarId: string,
    options: { dryRun?: boolean } = {},
  ): Promise<{ deleted: number } | { wouldDelete: number }> {
    const query = options.dryRun ? '?dryRun=true' : '';
    return this.request<{ deleted: number } | { wouldDelete: number }>(
      'DELETE',
      `/arches/calendar/calendars/${calendarId}/events${query}`,
    );
  }

  async parseCalendarImport(file: File): Promise<CalendarParsedImportResult> {
    const content = await file.text();
    return this.request<CalendarParsedImportResult>('POST', '/arches/calendar/import/parse', {
      fileName: file.name,
      content,
    });
  }

  async confirmCalendarImport(
    request: CalendarImportConfirmRequest,
  ): Promise<CalendarImportConfirmResult> {
    return this.request<CalendarImportConfirmResult>(
      'POST',
      '/arches/calendar/import/confirm',
      request,
    );
  }

  async parseNaturalEvent(input: {
    text: string;
    timezone?: string;
    referenceDate?: string;
  }): Promise<ParsedEventFields> {
    return this.request<ParsedEventFields>('POST', '/arches/calendar/events/parse-natural', input);
  }

  async exportCalendar(calendarId: string): Promise<string> {
    const url = `${API_BASE}/arches/calendar/export/${calendarId}`;
    const traceId = traceApiRequest('GET', url);
    let response: Response;
    try {
      response = await fetch(url, { credentials: 'include' });
    } catch (err) {
      traceApiError(traceId, err instanceof Error ? err : new Error(String(err)));
      throw err;
    }
    if (!response.ok) {
      const text = await response.text();
      const error = new Error(text || `Export failed with status ${response.status}`);
      traceApiError(traceId, error);
      throw error;
    }
    const text = await response.text();
    traceApiResponse(traceId, response.status, { length: text.length });
    return text;
  }

  // ==========================================================================
  // Calendar Event Templates API
  // ==========================================================================

  async listCalendarEventTemplates() {
    return this.request<{ templates: CalendarEventTemplateDetail[] }>(
      'GET',
      '/arches/calendar/templates',
    );
  }

  async createCalendarEventTemplate(input: CalendarEventTemplateInput) {
    return this.request<CalendarEventTemplateDetail>('POST', '/arches/calendar/templates', input);
  }

  async updateCalendarEventTemplate(id: string, input: Partial<CalendarEventTemplateInput>) {
    return this.request<CalendarEventTemplateDetail>(
      'PUT',
      `/arches/calendar/templates/${id}`,
      input,
    );
  }

  async deleteCalendarEventTemplate(id: string) {
    return this.request<void>('DELETE', `/arches/calendar/templates/${id}`);
  }

  // ==========================================================================
  // Calendar Sharing API
  // ==========================================================================

  async searchCalendarUsers(username: string) {
    return this.request<{ users: UserSearchResult[] }>(
      'GET',
      `/arches/calendar/users/search?username=${encodeURIComponent(username)}`,
    );
  }

  async listCalendarShares(calendarId: string) {
    return this.request<{ shares: CalendarShareDetail[] }>(
      'GET',
      `/arches/calendar/calendars/${calendarId}/shares`,
    );
  }

  async createCalendarShare(calendarId: string, input: CalendarShareInput) {
    return this.request<CalendarShareDetail>(
      'POST',
      `/arches/calendar/calendars/${calendarId}/shares`,
      input,
    );
  }

  async updateCalendarShare(calendarId: string, shareId: string, input: CalendarShareUpdate) {
    return this.request<CalendarShareDetail>(
      'PUT',
      `/arches/calendar/calendars/${calendarId}/shares/${shareId}`,
      input,
    );
  }

  async deleteCalendarShare(calendarId: string, shareId: string) {
    return this.request<void>(
      'DELETE',
      `/arches/calendar/calendars/${calendarId}/shares/${shareId}`,
    );
  }

  async listSharedCalendars() {
    return this.request<{ sharedCalendars: SharedCalendarSummary[] }>(
      'GET',
      '/arches/calendar/shared-calendars',
    );
  }

  async listSharedCalendarEvents(calendarId: string, rangeStart: string, rangeEnd: string) {
    const params = new URLSearchParams({ rangeStart, rangeEnd });
    return this.request<{ events: CalendarEventInstance[] }>(
      'GET',
      `/arches/calendar/shared-calendars/${calendarId}/events?${params.toString()}`,
    );
  }

  async findTime(input: FindTimeInput) {
    return this.request<FindTimeResult>('POST', '/arches/calendar/scheduling/find-time', input);
  }

  // ==========================================================================
  // Calendar Scheduling Links API
  // ==========================================================================

  async listSchedulingLinks() {
    return this.request<{ schedulingLinks: SchedulingLinkDetail[] }>(
      'GET',
      '/arches/calendar/scheduling-links',
    );
  }

  async createSchedulingLink(input: SchedulingLinkInput) {
    return this.request<SchedulingLinkDetail>('POST', '/arches/calendar/scheduling-links', input);
  }

  async getSchedulingLink(id: string) {
    return this.request<SchedulingLinkDetail>('GET', `/arches/calendar/scheduling-links/${id}`);
  }

  async updateSchedulingLink(id: string, input: Partial<SchedulingLinkInput>) {
    return this.request<SchedulingLinkDetail>(
      'PUT',
      `/arches/calendar/scheduling-links/${id}`,
      input,
    );
  }

  async deleteSchedulingLink(id: string) {
    return this.request<void>('DELETE', `/arches/calendar/scheduling-links/${id}`);
  }

  async listSchedulingLinkBookings(linkId: string, status?: string) {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    const query = params.toString();
    return this.request<{ bookings: BookingDetail[] }>(
      'GET',
      `/arches/calendar/scheduling-links/${linkId}/bookings${query ? `?${query}` : ''}`,
    );
  }

  async cancelBookingByHost(linkId: string, bookingId: string) {
    return this.request<void>(
      'DELETE',
      `/arches/calendar/scheduling-links/${linkId}/bookings/${bookingId}`,
    );
  }

  // Public scheduling (no auth required)

  async getPublicSchedulingInfo(slug: string, date?: string, timezone?: string) {
    const params = new URLSearchParams();
    if (date) params.set('date', date);
    if (timezone) params.set('timezone', timezone);
    const query = params.toString();
    return this.request<PublicSchedulingInfo>(
      'GET',
      `/public/scheduling/${slug}${query ? `?${query}` : ''}`,
    );
  }

  async createPublicBooking(
    slug: string,
    input: {
      bookerName: string;
      bookerEmail: string;
      startAt: string;
      notes?: string;
    },
  ) {
    return this.request<BookingConfirmation>('POST', `/public/scheduling/${slug}/book`, input);
  }

  async cancelPublicBooking(slug: string, bookingId: string, cancellationToken: string) {
    return this.request<{ success: boolean }>('POST', `/public/scheduling/${slug}/cancel`, {
      bookingId,
      cancellationToken,
    });
  }

  // ==========================================================================
  // Calendar Schedulable Tasks API
  // ==========================================================================

  async listSchedulableTasks(status?: string) {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    const query = params.toString();
    return this.request<{ tasks: SchedulableTaskDetail[] }>(
      'GET',
      `/arches/calendar/schedulable-tasks${query ? `?${query}` : ''}`,
    );
  }

  async createSchedulableTask(input: SchedulableTaskInput) {
    return this.request<SchedulableTaskDetail>('POST', '/arches/calendar/schedulable-tasks', input);
  }

  async updateSchedulableTask(id: string, input: Partial<SchedulableTaskInput>) {
    return this.request<SchedulableTaskDetail>(
      'PUT',
      `/arches/calendar/schedulable-tasks/${id}`,
      input,
    );
  }

  async deleteSchedulableTask(id: string) {
    return this.request<void>('DELETE', `/arches/calendar/schedulable-tasks/${id}`);
  }

  async suggestSchedule(
    taskId: string,
    opts?: { workingHoursStart?: number; workingHoursEnd?: number; timezone?: string },
  ) {
    return this.request<{ suggestions: AiScheduleSuggestion[] }>(
      'POST',
      `/arches/calendar/schedulable-tasks/${taskId}/suggest`,
      opts || {},
    );
  }

  async acceptScheduleSuggestion(taskId: string, suggestionIndex: number) {
    return this.request<SchedulableTaskDetail>(
      'POST',
      `/arches/calendar/schedulable-tasks/${taskId}/accept`,
      { suggestionIndex },
    );
  }

  async completeSchedulableTask(taskId: string) {
    return this.request<SchedulableTaskDetail>(
      'POST',
      `/arches/calendar/schedulable-tasks/${taskId}/complete`,
    );
  }

  // ==========================================================================
  // Calendar Focus Time API
  // ==========================================================================

  async getFocusTimeSettings() {
    return this.request<{ settings: FocusTimeSettingsDetail | null }>(
      'GET',
      '/arches/calendar/focus-time/settings',
    );
  }

  async updateFocusTimeSettings(input: FocusTimeSettingsInput) {
    return this.request<FocusTimeSettingsDetail>(
      'PUT',
      '/arches/calendar/focus-time/settings',
      input,
    );
  }

  async generateFocusTimeBlocks() {
    return this.request<{ blocksCreated: number }>('POST', '/arches/calendar/focus-time/generate');
  }

  async regenerateFocusTimeBlocks() {
    return this.request<{ blocksCreated: number }>(
      'POST',
      '/arches/calendar/focus-time/regenerate',
    );
  }

  async clearFocusTimeBlocks() {
    return this.request<{ deleted: number }>('DELETE', '/arches/calendar/focus-time/blocks');
  }

  // ==========================================================================
  // Calendar Subscriptions API
  // ==========================================================================

  async subscribeToCalendarFeed(input: { feedUrl: string; name?: string }) {
    return this.request<CalendarSubscriptionDetail>(
      'POST',
      '/arches/calendar/subscriptions',
      input,
    );
  }

  async listCalendarSubscriptions() {
    return this.request<{ subscriptions: CalendarSubscriptionDetail[] }>(
      'GET',
      '/arches/calendar/subscriptions',
    );
  }

  async refreshCalendarSubscription(id: string) {
    return this.request<CalendarSubscriptionDetail>(
      'POST',
      `/arches/calendar/subscriptions/${id}/refresh`,
    );
  }

  async unsubscribeCalendar(id: string) {
    return this.request<void>('DELETE', `/arches/calendar/subscriptions/${id}`);
  }

  // Calendar Analytics
  async getCalendarAnalytics(start: string, end: string) {
    const params = new URLSearchParams({ start, end });
    return this.request<CalendarAnalyticsResult>(
      'GET',
      `/arches/calendar/analytics?${params.toString()}`,
    );
  }

  // ==========================================================================
  // Lit Legion API
  // ==========================================================================

  async listLitLegionTasks(options?: {
    category?: string;
    status?: string;
    limit?: number;
    offset?: number;
  }) {
    const params = new URLSearchParams();
    if (options?.category) params.set('category', options.category);
    if (options?.status) params.set('status', options.status);
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset) params.set('offset', String(options.offset));
    const qs = params.toString();
    return this.request<LitLegionTaskStatusListResult>(
      'GET',
      `/arches/lit-legion/tasks${qs ? `?${qs}` : ''}`,
    );
  }

  async getLitLegionTask(taskId: string) {
    return this.request<{ task: LitLegionTaskWithStatus }>(
      'GET',
      `/arches/lit-legion/tasks/${taskId}`,
    );
  }

  async updateLitLegionTaskStatus(
    taskId: string,
    status: string,
    body?: { submission?: LitLegionTaskSubmission; userInput?: string },
  ) {
    return this.request<{ taskStatus: LitLegionTaskStatusResponse }>(
      'POST',
      `/arches/lit-legion/tasks/${taskId}/status`,
      {
        status,
        ...(body?.submission !== undefined ? { submission: body.submission } : {}),
        ...(body?.userInput !== undefined ? { userInput: body.userInput } : {}),
      },
    );
  }

  async getLitLegionStats() {
    return this.request<{ stats: LitLegionPlayerStats }>('GET', '/arches/lit-legion/stats');
  }

  async listLitLegionNpcs() {
    return this.request<{ npcs: LitLegionNpc[] }>('GET', '/arches/lit-legion/npcs');
  }

  async assignLitLegionNpc(npcId: string) {
    return this.request<{ taskStatuses: LitLegionTaskStatusResponse[]; count: number }>(
      'POST',
      '/arches/lit-legion/assign-npc',
      { npcId },
    );
  }

  // Lit Legion Admin Methods

  async adminListLitLegionTasks(options?: {
    category?: string;
    taskType?: string;
    limit?: number;
    offset?: number;
  }) {
    const params = new URLSearchParams();
    if (options?.category) params.set('category', options.category);
    if (options?.taskType) params.set('taskType', options.taskType);
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset) params.set('offset', String(options.offset));
    const qs = params.toString();
    return this.request<LitLegionAdminTaskListResult>(
      'GET',
      `/arches/lit-legion/admin/tasks${qs ? `?${qs}` : ''}`,
    );
  }

  async createLitLegionTask(input: CreateLitLegionTaskPayload) {
    return this.request<{ task: LitLegionAdminTask }>(
      'POST',
      '/arches/lit-legion/admin/tasks',
      input,
    );
  }

  async adminGetLitLegionTask(taskId: string) {
    return this.request<{
      task: LitLegionAdminTask;
      coinRewards: LitLegionCoinReward[];
      badgeRewards: LitLegionBadgeReward[];
    }>('GET', `/arches/lit-legion/admin/tasks/${taskId}`);
  }

  async adminListAllLitLegionTasks() {
    return this.request<{ tasks: LitLegionAdminTask[]; total: number }>(
      'GET',
      '/arches/lit-legion/admin/tasks/all',
    );
  }

  async adminUpdateLitLegionTask(taskId: string, input: UpdateLitLegionTaskPayload) {
    return this.request<{ task: LitLegionAdminTask }>(
      'PATCH',
      `/arches/lit-legion/admin/tasks/${taskId}`,
      input,
    );
  }

  async adminDeleteLitLegionTask(taskId: string) {
    return this.request<void>('DELETE', `/arches/lit-legion/admin/tasks/${taskId}`);
  }

  async adminListLitLegionNpcs() {
    return this.request<{ npcs: LitLegionNpc[] }>('GET', '/arches/lit-legion/admin/npcs');
  }

  async adminListLitLegionLevels() {
    return this.request<{ levels: LitLegionUserLevel[] }>('GET', '/arches/lit-legion/admin/levels');
  }

  async adminListLitLegionBadges() {
    return this.request<{ badges: LitLegionBadge[] }>('GET', '/arches/lit-legion/admin/badges');
  }

  async adminListLitLegionUserTaskStatuses(
    username: string,
    options?: { limit?: number; offset?: number },
  ) {
    const params = new URLSearchParams();
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset) params.set('offset', String(options.offset));
    const qs = params.toString();
    return this.request<{
      username: string;
      userId: string;
      statuses: LitLegionAdminTaskStatusWithTask[];
      total: number;
    }>(
      'GET',
      `/arches/lit-legion/admin/users/by-username/${encodeURIComponent(username)}/statuses${
        qs ? `?${qs}` : ''
      }`,
    );
  }

  async adminUpdateLitLegionTaskStatus(taskStatusId: string, status: string) {
    return this.request<{ taskStatus: LitLegionTaskStatusResponse }>(
      'PATCH',
      `/arches/lit-legion/admin/statuses/${taskStatusId}`,
      { status },
    );
  }

  /**
   * Hard-delete a task-status row. To refresh availability after a delete,
   * use `adminRecomputeLitLegionAvailability`.
   */
  async adminDeleteLitLegionTaskStatus(taskStatusId: string) {
    return this.request<void>('DELETE', `/arches/lit-legion/admin/statuses/${taskStatusId}`);
  }

  async adminAssignLitLegionTaskToUser(userId: string, taskId: string, status?: string) {
    return this.request<{ taskStatus: LitLegionTaskStatusResponse }>(
      'POST',
      `/arches/lit-legion/admin/users/${encodeURIComponent(userId)}/assign-task`,
      { taskId, ...(status ? { status } : {}) },
    );
  }

  /**
   * Sweep every lit-legion task and insert an 'available' row for any task
   * that the user is missing AND is currently unblocked (root task or parent
   * is awarded). Existing rows are left alone.
   */
  async adminRecomputeLitLegionAvailability(userId: string) {
    return this.request<{ taskStatuses: LitLegionTaskStatusResponse[]; count: number }>(
      'POST',
      `/arches/lit-legion/admin/users/${encodeURIComponent(userId)}/recompute-availability`,
    );
  }

  // ==========================================================================
  // Files API
  // ==========================================================================

  /**
   * Get presigned URL for single file upload
   */
  async getUploadUrl(params: {
    fileName: string;
    mimeType: string;
    sizeBytes: number;
    category?: FileCategory;
    checksumSha256?: string;
  }) {
    return this.request<PresignedUploadResponse>('POST', '/files/upload-url', params);
  }

  /**
   * Initiate multipart upload for large files
   */
  async initiateMultipartUpload(params: {
    fileName: string;
    mimeType: string;
    sizeBytes: number;
    category?: FileCategory;
  }) {
    return this.request<MultipartUploadResponse>('POST', '/files/upload-url/multipart', params);
  }

  /**
   * Get presigned URL for a specific part of multipart upload
   */
  async getMultipartPartUrl(params: {
    uploadId: string;
    fileId: string;
    s3Key: string;
    partNumber: number;
  }) {
    return this.request<PresignedPartResponse>('POST', '/files/upload-url/multipart/part', params);
  }

  /**
   * Complete multipart upload
   */
  async completeMultipartUpload(params: {
    uploadId: string;
    fileId: string;
    s3Key: string;
    parts: Array<{ partNumber: number; etag: string }>;
  }) {
    return this.request<{ location: string }>(
      'POST',
      '/files/upload-url/multipart/complete',
      params,
    );
  }

  /**
   * Confirm file upload and create metadata record
   */
  async confirmUpload(
    fileId: string,
    params: {
      fileName: string;
      mimeType: string;
      sizeBytes: number;
      s3Key: string;
      category?: FileCategory;
      checksumSha256?: string;
      folderId?: string;
      tags?: string[];
      metadata?: Record<string, unknown>;
    },
  ) {
    return this.request<{ file: UserFileResponse }>('POST', `/files/${fileId}/confirm`, params);
  }

  /**
   * List user files
   */
  async listFiles(options?: {
    folderId?: string | null;
    category?: FileCategory;
    status?: FileStatus;
    tags?: string[];
    search?: string;
    sortBy?: 'name' | 'created' | 'updated' | 'accessed' | 'size';
    sortOrder?: 'asc' | 'desc';
    limit?: number;
    offset?: number;
  }) {
    const params = new URLSearchParams();
    if (options?.folderId !== undefined) params.set('folderId', String(options.folderId));
    if (options?.category) params.set('category', options.category);
    if (options?.status) params.set('status', options.status);
    if (options?.tags?.length) params.set('tags', options.tags.join(','));
    if (options?.search) params.set('search', options.search);
    if (options?.sortBy) params.set('sortBy', options.sortBy);
    if (options?.sortOrder) params.set('sortOrder', options.sortOrder);
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.offset) params.set('offset', options.offset.toString());
    const query = params.toString();
    return this.request<FileListResult>('GET', `/files${query ? `?${query}` : ''}`);
  }

  /**
   * Get file metadata
   */
  async getFile(fileId: string) {
    return this.request<{ file: UserFileResponse }>('GET', `/files/${fileId}`);
  }

  /**
   * Get presigned download URL for a file
   */
  async getDownloadUrl(fileId: string) {
    return this.request<{ downloadUrl: string; expiresAt: string }>(
      'GET',
      `/files/${fileId}/download-url`,
    );
  }

  /**
   * Update file metadata
   */
  async updateFile(
    fileId: string,
    updates: {
      fileName?: string;
      folderId?: string | null;
      category?: FileCategory;
      tags?: string[];
      metadata?: Record<string, unknown>;
    },
  ) {
    return this.request<{ file: UserFileResponse }>('PATCH', `/files/${fileId}`, updates);
  }

  /**
   * Delete a file (soft delete)
   */
  async deleteFile(fileId: string) {
    return this.request<void>('DELETE', `/files/${fileId}`);
  }

  /**
   * Permanently delete a file (removes from DB and S3, cannot be undone)
   */
  async permanentDeleteFile(fileId: string) {
    return this.request<void>('DELETE', `/files/${fileId}/permanent`);
  }

  /**
   * Restore a file from Trash to its original folder
   */
  async restoreFromTrash(fileId: string) {
    return this.request<{
      file: { fileId: string; fileName: string; folderId: string | null };
      message: string;
    }>('POST', `/files/${fileId}/restore-from-trash`);
  }

  /**
   * Archive a file to Glacier storage
   */
  async archiveFile(fileId: string) {
    return this.request<{ file: UserFileResponse }>('POST', `/files/${fileId}/archive`);
  }

  /**
   * Restore a file from archive
   */
  async restoreFile(fileId: string) {
    return this.request<{ file: UserFileResponse; message: string }>(
      'POST',
      `/files/${fileId}/restore`,
    );
  }

  /**
   * Bulk file operations
   */
  async bulkFileOperation(params: {
    operation: 'delete' | 'move' | 'tag' | 'restore-from-trash' | 'permanent-delete';
    fileIds: string[];
    folderId?: string | null;
    tags?: string[];
  }) {
    return this.request<{
      operation: string;
      requestedCount: number;
      affectedCount: number;
    }>('POST', '/files/bulk', params);
  }

  // ==========================================================================
  // Folders API
  // ==========================================================================

  /**
   * Create a new folder
   */
  async createFolder(params: { name: string; parentFolderId?: string }) {
    return this.request<{ folder: FolderResponse }>('POST', '/folders', params);
  }

  /**
   * List folders
   */
  async listFolders(parentFolderId?: string | null) {
    const params = new URLSearchParams();
    if (parentFolderId !== undefined) params.set('parentFolderId', String(parentFolderId));
    const query = params.toString();
    return this.request<{ folders: FolderResponse[] }>(
      'GET',
      `/folders${query ? `?${query}` : ''}`,
    );
  }

  /**
   * Get folder details
   */
  async getFolder(folderId: string) {
    return this.request<{ folder: FolderResponse }>('GET', `/folders/${folderId}`);
  }

  /**
   * Get folder breadcrumb path
   */
  async getFolderBreadcrumb(folderId: string) {
    return this.request<{ breadcrumb: FolderResponse[] }>('GET', `/folders/${folderId}/breadcrumb`);
  }

  /**
   * Update folder (rename or move)
   */
  async updateFolder(
    folderId: string,
    updates: {
      name?: string;
      parentFolderId?: string | null;
    },
  ) {
    return this.request<{ folder: FolderResponse }>('PATCH', `/folders/${folderId}`, updates);
  }

  /**
   * Delete a folder
   */
  async deleteFolder(folderId: string, deleteContents: boolean = false) {
    const params = new URLSearchParams();
    if (deleteContents) params.set('deleteContents', 'true');
    const query = params.toString();
    return this.request<void>('DELETE', `/folders/${folderId}${query ? `?${query}` : ''}`);
  }

  /**
   * Ensure My-Fias/Context folder exists
   * Creates the protected folder structure if it doesn't exist.
   * Returns the Context folder info.
   */
  async ensureContextFolder() {
    return this.request<{ folder: ContextFolderResponse }>('POST', '/folders/context');
  }

  /**
   * Ensure the My-Fias/AI Images folder exists (creates if missing)
   */
  async ensureAIImagesFolder() {
    return this.request<{ folder: FolderResponse }>('POST', '/folders/ai-images');
  }

  /**
   * Ensure the My-Fias/Reference Images folder exists (creates if missing)
   */
  async ensureReferenceImagesFolder() {
    return this.request<{ folder: FolderResponse }>('POST', '/folders/reference-images');
  }

  /**
   * Ensure the My-Fias/Trash folder exists (creates if missing)
   */
  async ensureTrashFolder() {
    return this.request<{ folder: FolderResponse }>('POST', '/folders/trash');
  }

  // ==========================================================================
  // Storage API
  // ==========================================================================

  /**
   * Get storage usage and quota
   */
  async getStorageUsage() {
    return this.request<StorageUsageResponse>('GET', '/storage');
  }

  /**
   * List file operations for audit
   */
  async listFileOperations(options?: {
    fileId?: string;
    operationType?: string;
    limit?: number;
    offset?: number;
  }) {
    const params = new URLSearchParams();
    if (options?.fileId) params.set('fileId', options.fileId);
    if (options?.operationType) params.set('operationType', options.operationType);
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.offset) params.set('offset', options.offset.toString());
    const query = params.toString();
    return this.request<FileOperationsListResult>(
      'GET',
      `/storage/operations${query ? `?${query}` : ''}`,
    );
  }

  // ==========================================================================
  // Image Generation API
  // ==========================================================================

  async generateImage(archeId: string, input: ImageGenerationInput) {
    return this.request<ImageGenerationResponse>('POST', `/arches/${archeId}/generate-image`, {
      input,
    });
  }

  async cancelImageGeneration(archeId: string, requestId: string) {
    return this.request<{ status: string; requestId: string }>(
      'POST',
      `/arches/${archeId}/cancel-generation/${requestId}`,
    );
  }

  async listImageModels(archeId: string) {
    return this.request<ImageModelsResponse>('GET', `/arches/${archeId}/image-models`);
  }

  async getImageUrl(archeId: string, imageRef: string): Promise<string> {
    const result = await this.request<{ url: string }>(
      'GET',
      `/arches/${archeId}/image-url?imageRef=${encodeURIComponent(imageRef)}`,
    );
    return result.url;
  }

  async checkImageStatus(
    archeId: string,
    requestId: string,
  ): Promise<{
    status: 'pending' | 'complete' | 'error';
    imageRef?: string;
    fileId?: string;
    url?: string;
    error?: string;
  }> {
    return this.request<{
      status: 'pending' | 'complete' | 'error';
      imageRef?: string;
      fileId?: string;
      url?: string;
      error?: string;
    }>('GET', `/arches/${archeId}/image-status/${requestId}`);
  }

  // ==========================================================================
  // Icon Generator API
  // ==========================================================================

  async generateIcon(input: {
    name: string;
    description: string;
    useDefaultStyle?: boolean;
    style?: string;
  }) {
    return this.request<{ requestId: string }>('POST', '/arches/icon-generator/generate', input);
  }

  async getIconGeneratorStatus(requestId: string) {
    return this.request<{
      status: 'pending' | 'complete' | 'error';
      previewUrl?: string;
      requestId?: string;
      error?: string;
    }>('GET', `/arches/icon-generator/status/${requestId}`);
  }

  async saveIcon(input: { requestId: string; fileName?: string; attachToArcheId?: string }) {
    return this.request<{
      fileId: string;
      imageRef: string;
      attachedIconKey: string | null;
    }>('POST', '/arches/icon-generator/save', input);
  }

  // ==========================================================================
  // Voice Studio API
  // ==========================================================================

  async transcribeAudio(
    fileId: string,
    opts?: { language?: string },
  ): Promise<TranscriptionResult> {
    return this.request<TranscriptionResult>('POST', '/arches/voice-studio/transcribe', {
      fileId,
      ...(opts?.language ? { language: opts.language } : {}),
    });
  }

  async synthesizeSpeech(body: {
    text: string;
    voiceId: string;
    stability?: number;
    similarityBoost?: number;
  }): Promise<SynthesisResult> {
    return this.request<SynthesisResult>('POST', '/arches/voice-studio/synthesize', body);
  }

  async listVoiceModels(): Promise<{ voiceModels: VoiceModel[] }> {
    return this.request<{ voiceModels: VoiceModel[] }>('GET', '/arches/voice-studio/voice-models');
  }

  async createVoiceModel(body: {
    name: string;
    description?: string;
    sampleFileIds: string[];
  }): Promise<{ voiceModel: VoiceModel }> {
    return this.request<{ voiceModel: VoiceModel }>(
      'POST',
      '/arches/voice-studio/voice-models',
      body,
    );
  }

  async deleteVoiceModel(modelId: string): Promise<void> {
    return this.request<void>('DELETE', `/arches/voice-studio/voice-models/${modelId}`);
  }

  async listPlatformVoices(): Promise<{ voices: PlatformVoice[] }> {
    return this.request<{ voices: PlatformVoice[] }>('GET', '/arches/voice-studio/platform-voices');
  }

  // ==========================================================================
  // Image Editor API
  // ==========================================================================

  async processImage(request: {
    sourceFileId?: string;
    sourceDataUrl?: string;
    operations: Array<Record<string, unknown>>;
    output: {
      format: 'png' | 'jpeg' | 'webp';
      quality?: number;
      width?: number;
      height?: number;
      maintainAspectRatio?: boolean;
    };
    saveAs?: { fileName: string };
  }) {
    return this.request<{
      fileId?: string;
      downloadUrl?: string;
      width: number;
      height: number;
      sizeBytes: number;
      format: string;
    }>('POST', '/arches/image-editor/process', request);
  }

  async smartCreateImage(request: {
    prompt: string;
    /** Aspect ratio for the generated image. Aspect-ratio strings
     *  rather than pixel dimensions because every wired image provider
     *  accepts them, and this 5-element set is the intersection of
     *  natively-supported sizes across BFL / OpenAI / Google / Recraft
     *  / Stability. */
    size?: '1:1' | '4:3' | '3:4' | '16:9' | '9:16';
    /** Source PNG data URL for mask-aware inpainting. Must be paired
     *  with `maskDataUrl`. When omitted, falls back to text-to-image. */
    imageDataUrl?: string;
    /** Mask PNG data URL — same dimensions as `imageDataUrl`, with
     *  alpha=0 in the edit region and alpha=255 elsewhere. */
    maskDataUrl?: string;
    /** Model picker key for Insert mode (text-to-image). Ignored when
     *  imageDataUrl + maskDataUrl are present — inpaint is always
     *  routed through OpenAI gpt-image-1 since FLUX / Recraft /
     *  Stability text-to-image models don't accept masks. */
    model?:
      | 'flux-2-pro'
      | 'flux-ultra'
      | 'gpt-image-1'
      | 'nano-banana'
      | 'imagen-4'
      | 'recraft-v3'
      | 'stable-ultra';
  }) {
    return this.request<{ imageDataUrl: string }>(
      'POST',
      '/arches/image-editor/smart-create',
      request,
    );
  }

  /**
   * Image Editor — Smart Erase. The escalation path from the free
   * MI-GAN-based Smart Delete: when the on-device model leaves
   * artifacts, this calls Stability AI's dedicated `/v2beta/stable-image/edit/erase`
   * endpoint. No prompt — the model fills the masked region from the
   * surrounding pixels. Costs roughly 4 credits per call (Stability's
   * 0.03 USD raw cost × 1.20 platform markup).
   */
  /**
   * Image Editor — Smart Inpaint. Mask-aware AI replacement: fills the
   * masked region with content matching the prompt. Three engines —
   * picked by the caller based on cost / quality tradeoffs:
   *   - `'stability'` (default, ~4 credits) — Stability's dedicated
   *     `/edit/inpaint`. Supports `negativePrompt` and `growMask`.
   *   - `'recraft'` (~5 credits) — Recraft's replacement-oriented inpaint.
   *     Strong with explicit prompts; supports prompt only.
   *   - `'openai'` (~5 credits) — gpt-image-1's `/edits` endpoint.
   *     Good text rendering; mask alpha inverted server-side.
   */
  async smartInpaintImage(request: {
    imageDataUrl: string;
    maskDataUrl: string;
    prompt: string;
    engine?: 'stability' | 'recraft' | 'openai';
    /** Optional negative prompt — what to avoid. Forwarded to engines
     *  that support it (Stability). Ignored by engines that don't. */
    negativePrompt?: string;
    /** Pixels to grow the mask outward server-side before sending to
     *  the engine. Range 0–64 px; defaults to 0. */
    growMask?: number;
    /** Optional engine-specific style preset:
     *    - Stability: one of 11 presets (`cinematic`, `anime`, etc.)
     *    - Recraft: one of 4 modes (`realistic_image`, `digital_illustration`,
     *      `vector_illustration`, `icon`)
     *    - OpenAI: ignored. */
    stylePreset?: string;
  }) {
    return this.request<{ imageDataUrl: string }>(
      'POST',
      '/arches/image-editor/smart-inpaint',
      request,
    );
  }

  /**
   * Image Editor — Search & Replace. Stability auto-segments the
   * search target (no marquee required) and replaces it with content
   * matching `prompt`. Returns the full-source-sized result with the
   * replacement composited.
   */
  /**
   * Image Editor — Search & Recolor. Stability auto-segments via
   * `selectPrompt` and recolors the matching region with the color
   * or effect described in `prompt`. Shape stays fixed; only color
   * changes. Stability-only.
   */
  async smartImageToImageImage(
    request: {
      imageDataUrl: string;
      prompt: string;
      /** Engine slug: 'core' | 'ultra' | 'sd3-large'. */
      engine: 'core' | 'ultra' | 'sd3-large';
      /** Stability convention — 0 = identical to source, 1 = ignore source. */
      strength: number;
      negativePrompt?: string;
      /** Optional Stability style preset (only respected by Ultra). */
      stylePreset?: string;
      /** Optional seed for reproducibility (0..2^32-1). */
      seed?: number;
    },
    options?: { signal?: AbortSignal },
  ) {
    return this.request<{ imageDataUrl: string }>(
      'POST',
      '/arches/image-editor/smart-image-to-image',
      request,
      options,
    );
  }

  async smartOutpaintImage(
    request: {
      imageDataUrl: string;
      /** Pixels to extend on each edge. ≥1 must be > 0. Each 0..2000. */
      left?: number;
      right?: number;
      up?: number;
      down?: number;
      /** Optional prompt to guide what fills the new area. */
      prompt?: string;
      /** 0..1; how much to invent (1.0) vs. extend literally (0.0). */
      creativity?: number;
      /** Optional negative prompt — features to avoid. */
      negativePrompt?: string;
      /** Optional Stability style preset (one of 11 named presets). */
      stylePreset?: string;
      /** Optional seed for reproducibility (0..2^32-1). */
      seed?: number;
    },
    options?: { signal?: AbortSignal },
  ) {
    return this.request<{ imageDataUrl: string }>(
      'POST',
      '/arches/image-editor/smart-outpaint',
      request,
      options,
    );
  }

  async smartSearchRecolorImage(request: {
    imageDataUrl: string;
    /** Text describing the object to recolor. */
    selectPrompt: string;
    /** Text describing the new color/effect. */
    prompt: string;
    /** Optional negative prompt — features to avoid. */
    negativePrompt?: string;
    /** Pixels to grow the auto-segmented mask outward (0–64). */
    growMask?: number;
    /** Optional Stability style preset. */
    stylePreset?: string;
    /** Optional seed for reproducibility (0..2^32-1). */
    seed?: number;
  }) {
    return this.request<{ imageDataUrl: string }>(
      'POST',
      '/arches/image-editor/smart-search-recolor',
      request,
    );
  }

  async smartSearchReplaceImage(request: {
    imageDataUrl: string;
    /** Text describing the existing object to replace. */
    searchPrompt: string;
    /** Text describing what should appear instead. */
    prompt: string;
    /** Optional negative prompt — features to avoid. */
    negativePrompt?: string;
    /** Pixels to grow the auto-segmented mask outward (0–64). */
    growMask?: number;
    /** Optional Stability style preset (one of 11 named presets:
     *  cinematic, anime, photographic, etc.). Empty/omitted = default. */
    stylePreset?: string;
    /** Optional seed for reproducibility. Same prompts + same seed →
     *  same output. Range 0..2^32-1. */
    seed?: number;
  }) {
    return this.request<{ imageDataUrl: string }>(
      'POST',
      '/arches/image-editor/smart-search-replace',
      request,
    );
  }

  async smartEraseImage(request: {
    /** Source PNG data URL — the layer being edited. */
    imageDataUrl: string;
    /** Mask PNG data URL — alpha=255 in the region to erase, alpha=0
     *  elsewhere. Same dimensions as `imageDataUrl`. */
    maskDataUrl: string;
    /** Pixels to grow the mask outward server-side before sending.
     *  Helps when the marquee just barely catches the object and
     *  Stability otherwise leaves an edge halo. Range 0–64 px;
     *  defaults to 0 (no growth). */
    growMask?: number;
  }) {
    return this.request<{ imageDataUrl: string }>(
      'POST',
      '/arches/image-editor/smart-erase',
      request,
    );
  }

  /** Server-side background removal. The free `'imgly'` engine never
   *  reaches this client — it runs entirely in the browser via
   *  `@imgly/background-removal`. Only `'stability'` and `'recraft'` hit
   *  the route. */
  async smartRemoveBackgroundImage(request: {
    /** Source PNG data URL — the layer being edited. */
    imageDataUrl: string;
    engine: 'stability' | 'recraft';
  }) {
    return this.request<{ imageDataUrl: string }>(
      'POST',
      '/arches/image-editor/smart-remove-background',
      request,
    );
  }

  /** Control / Guided image generation. Four modes (from-sketch,
   *  match-structure, match-style, style-transfer) map to four
   *  Stability v2beta `/control/*` endpoints. The (mode, fields)
   *  tuple is validated server-side — see `smart-control.ts` for
   *  the dispatch table and required-field rules per mode. */
  async smartControlImage(
    request: {
      imageDataUrl: string;
      mode: 'from-sketch' | 'match-structure' | 'match-style' | 'style-transfer';
      /** Required for from-sketch / match-structure / match-style. */
      prompt?: string;
      negativePrompt?: string;
      /** 0..1, used by from-sketch + match-structure. */
      controlStrength?: number;
      /** 0..1, used by match-style. */
      fidelity?: number;
      /** Required for style-transfer. */
      styleImageDataUrl?: string;
      /** 0..1, style-transfer only. */
      styleStrength?: number;
      /** 0..1, style-transfer only. */
      compositionFidelity?: number;
      /** 0..1, style-transfer only. */
      changeStrength?: number;
      seed?: number;
    },
    options?: { signal?: AbortSignal },
  ) {
    return this.request<{ imageDataUrl: string }>(
      'POST',
      '/arches/image-editor/smart-control',
      request,
      options,
    );
  }

  /** Convert a raster image to SVG via Recraft's vectorize endpoint.
   *  Returns the SVG text, NOT a raster — caller should treat as a
   *  file to save, not pixels to draw. The editor's vectorize flow
   *  writes the result to the user's filesystem via showSaveFilePicker
   *  rather than rasterizing back into a layer (which would defeat
   *  the entire point of vectorizing). */
  async smartVectorizeImage(request: { imageDataUrl: string }, options?: { signal?: AbortSignal }) {
    return this.request<{ svg: string }>(
      'POST',
      '/arches/image-editor/smart-vectorize',
      request,
      options,
    );
  }

  /** Upscale an image. Three modes (fast/high-quality/reimagine) map to
   *  five entities behind the scenes. Engine + extra fields depend on
   *  the mode — see the route at smart-upscale.ts for the dispatch
   *  table. Server validates the (mode, engine) tuple. */
  async smartUpscaleImage(
    request: {
      /** Source PNG data URL — the active layer being upscaled. */
      imageDataUrl: string;
      mode: 'fast' | 'high-quality' | 'reimagine';
      engine: 'stability' | 'recraft';
      /** Required for stability HQ + reimagine; ignored otherwise. */
      prompt?: string;
      /** Stability HQ + reimagine only — 0..0.35. */
      creativity?: number;
      /** Recraft fast + reimagine only — 2 or 4. Stability fast is
       *  always 4×. Default behavior when omitted is provider-side
       *  default (4 for both Recraft endpoints). */
      factor?: 2 | 4;
      /** Stability HQ + reimagine only. */
      seed?: number;
    },
    options?: { signal?: AbortSignal },
  ) {
    return this.request<{ imageDataUrl: string }>(
      'POST',
      '/arches/image-editor/smart-upscale',
      request,
      options,
    );
  }

  /** Replace the background of an image with a prompt-described scene
   *  (Recraft) or a prompt + optional reference image with subject
   *  relighting (Stability). Engines have very different feature
   *  surfaces — fields not honored by the chosen engine are dropped
   *  silently server-side. */
  async smartReplaceBackgroundImage(
    request: {
      /** Source PNG data URL — the full image, NOT a pre-cut subject;
       *  both engines segment internally. */
      imageDataUrl: string;
      engine: 'recraft' | 'stability';
      /** Required for Recraft. Required for Stability if no reference. */
      backgroundPrompt?: string;
      /** Stability only — reference image whose look the new BG should
       *  match. Either this or `backgroundPrompt` is required for
       *  Stability; if both are provided, both are sent and Stability
       *  blends. */
      backgroundReferenceDataUrl?: string;
      /** Stability only — bias the relighting from a specific direction.
       *  Omit (or use `'none'` client-side and don't forward) for no
       *  bias. */
      lightDirection?: 'above' | 'below' | 'left' | 'right';
      lightStrength?: number;
      lightReferenceDataUrl?: string;
      /** Stability only — 0..1, how much of the original subject's
       *  appearance to preserve through the relight. */
      preserveSubject?: number;
      /** Stability only — 0..1, blend factor with the original BG. */
      preserveOriginalBg?: number;
      /** Recraft only — one of 4 style presets. */
      style?: 'realistic_image' | 'digital_illustration' | 'vector_illustration' | 'icon';
      /** 0..2^32-1, both engines. */
      seed?: number;
    },
    options?: { signal?: AbortSignal },
  ) {
    return this.request<{ imageDataUrl: string }>(
      'POST',
      '/arches/image-editor/smart-replace-background',
      request,
      options,
    );
  }

  // ==========================================================================
  // Builder Sessions API
  // ==========================================================================

  async createBuilderSession(params?: { templateId?: string; existingArchId?: string }) {
    return this.request<{
      sessionId: string;
      status: 'active';
      template?: BuilderTemplate;
      existingArche?: unknown;
    }>('POST', '/arches/builder/sessions', params || {});
  }

  async listBuilderSessions() {
    return this.request<{ sessions: BuilderSessionSummary[] }>('GET', '/arches/builder/sessions');
  }

  async getBuilderSession(sessionId: string) {
    return this.request<{ session: BuilderSession; currentPreview: unknown }>(
      'GET',
      `/arches/builder/sessions/${sessionId}`,
    );
  }

  async updateBuilderSession(
    sessionId: string,
    updates: {
      currentName?: string;
      currentDescription?: string;
      currentSettings?: unknown;
      status?: 'active' | 'paused' | 'completed' | 'abandoned';
    },
  ) {
    return this.request<{ session: BuilderSession }>(
      'PATCH',
      `/arches/builder/sessions/${sessionId}`,
      updates,
    );
  }

  async deleteBuilderSession(sessionId: string) {
    return this.request<void>('DELETE', `/arches/builder/sessions/${sessionId}`);
  }

  async resetBuilderContext(sessionId: string) {
    return this.request<{ session: BuilderSession; currentPreview: unknown }>(
      'POST',
      `/arches/builder/sessions/${sessionId}/reset-context`,
    );
  }

  async checkArcheName(name: string, excludeArchId?: string) {
    const params = new URLSearchParams({ name });
    if (excludeArchId) {
      params.append('excludeArchId', excludeArchId);
    }
    return this.request<{ available: boolean }>(
      'GET',
      `/arches/builder/check-name?${params.toString()}`,
    );
  }

  async sendBuilderMessage(sessionId: string, message: string) {
    return this.request<{
      response: ClaudeMessageResponse;
      updatedPreview: unknown;
    }>('POST', `/arches/builder/sessions/${sessionId}/message`, { message });
  }

  async startBuilderMessageStream(
    sessionId: string,
    message: string,
    verbose?: boolean,
    useOpus?: boolean,
    guidedPhase?: string,
  ) {
    return this.request<{
      requestId: string;
      sessionId: string;
      status: 'pending';
    }>('POST', `/arches/builder/sessions/${sessionId}/message/stream`, {
      message,
      verbose,
      useOpus,
      ...(guidedPhase ? { guidedPhase } : {}),
    });
  }

  async startGuidedFlow(sessionId: string, idea: string) {
    return this.request<{
      guidedFlow: {
        phase: string;
        ideaSummary: string;
        clarifyingQA: unknown[];
        assumptions: unknown[];
        requireStepApproval: boolean;
        lastPhaseTransition: string;
      };
    }>('POST', `/arches/builder/sessions/${sessionId}/guided/start`, { idea });
  }

  async saveBuilderSession(sessionId: string, params: { name: string; description: string }) {
    return this.request<{ archId: string; status: 'draft'; version: number }>(
      'POST',
      `/arches/builder/sessions/${sessionId}/save`,
      params,
    );
  }

  async uploadPreviewScreenshot(sessionId: string, dataUrl: string): Promise<void> {
    await this.request<{ success: boolean }>(
      'POST',
      `/arches/builder/sessions/${sessionId}/preview-screenshot`,
      { screenshot: dataUrl },
    );
  }

  async submitProblemReport(
    sessionId: string,
    report: {
      description: string;
      problemScope: number;
      conversationHistory?: unknown;
      currentUiDefinition?: unknown;
      currentSettings?: unknown;
      logEntries?: unknown;
      telemetryState?: unknown;
    },
  ) {
    return this.request<{ reportId: string; status: string; createdAt: string }>(
      'POST',
      `/arches/builder/sessions/${sessionId}/report-problem`,
      report,
    );
  }

  // ==========================================================================
  // Plugin Builder API
  // ==========================================================================

  async createPluginBuilderSession(params?: { name?: string; mode?: 'chat' | 'guided' }) {
    return this.request<{
      sessionId: string;
      status: 'active';
      currentFiles: Record<string, unknown>;
    }>('POST', '/arches/plugin-builder/sessions', params || {});
  }

  async listPluginBuilderSessions() {
    return this.request<{ sessions: unknown[] }>('GET', '/arches/plugin-builder/sessions');
  }

  async getPluginBuilderSession(sessionId: string) {
    return this.request<{ session: unknown }>(
      'GET',
      `/arches/plugin-builder/sessions/${sessionId}`,
    );
  }

  async updatePluginBuilderSession(
    sessionId: string,
    updates: {
      currentName?: string;
      currentDescription?: string;
      currentExpandedDescription?: string;
      currentSettings?: unknown;
      status?: 'active' | 'paused' | 'completed' | 'abandoned';
    },
  ) {
    return this.request<unknown>('PATCH', `/arches/plugin-builder/sessions/${sessionId}`, updates);
  }

  async updatePluginBuilderSessionFiles(sessionId: string, currentFiles: Record<string, unknown>) {
    return this.request<unknown>('PATCH', `/arches/plugin-builder/sessions/${sessionId}`, {
      currentSettings: { currentFiles },
    });
  }

  async deletePluginBuilderSession(sessionId: string) {
    return this.request<void>('DELETE', `/arches/plugin-builder/sessions/${sessionId}`);
  }

  async startPluginBuilderMessageStream(
    sessionId: string,
    message: string,
    verbose?: boolean,
    useOpus?: boolean,
    guidedPhase?: string,
    autoApprove?: boolean,
  ) {
    return this.request<{
      requestId: string;
      sessionId: string;
      status: 'pending';
    }>('POST', `/arches/plugin-builder/sessions/${sessionId}/message/stream`, {
      message,
      verbose,
      useOpus,
      ...(guidedPhase ? { guidedPhase } : {}),
      ...(autoApprove !== undefined ? { autoApprove } : {}),
    });
  }

  async advancePluginBuilderStep(sessionId: string) {
    return this.request<{
      sessionId: string;
      buildPlan: unknown;
      allVerified: boolean;
      nextStep: { id: string; title: string } | null;
    }>('POST', `/arches/plugin-builder/sessions/${sessionId}/guided/advance-step`);
  }

  async startPluginGuidedFlow(
    sessionId: string,
    idea: string,
    verbose?: boolean,
    signal?: AbortSignal,
    useOpus?: boolean,
  ) {
    return this.request<{
      requestId: string;
      sessionId: string;
    }>(
      'POST',
      `/arches/plugin-builder/sessions/${sessionId}/guided/start`,
      { idea, verbose, useOpus },
      { signal },
    );
  }

  async submitGuidedAnswers(
    sessionId: string,
    answers: Record<number, string>,
    verbose?: boolean,
    signal?: AbortSignal,
    comment?: string,
    useOpus?: boolean,
  ) {
    return this.request<{
      requestId: string;
      sessionId: string;
    }>(
      'POST',
      `/arches/plugin-builder/sessions/${sessionId}/guided/answers`,
      { answers, verbose, useOpus, ...(comment ? { comment } : {}) },
      { signal },
    );
  }

  async submitGuidedPlanDecision(
    sessionId: string,
    action: 'approve' | 'request-changes',
    verbose?: boolean,
    feedback?: string,
    signal?: AbortSignal,
    useOpus?: boolean,
  ) {
    return this.request<{
      requestId: string;
      sessionId: string;
    }>(
      'POST',
      `/arches/plugin-builder/sessions/${sessionId}/guided/plan-decision`,
      {
        action,
        verbose,
        useOpus,
        ...(feedback ? { feedback } : {}),
      },
      { signal },
    );
  }

  async submitGuidedAssumptionDecision(
    sessionId: string,
    action: 'approve' | 'suggest-changes',
    verbose?: boolean,
    comments?: Record<string, string>,
    signal?: AbortSignal,
    useOpus?: boolean,
  ) {
    // approve returns full result synchronously; suggest-changes returns { requestId } for streaming
    return this.request<{
      sessionId: string;
      requestId?: string;
      guidedFlow?: {
        phase: string;
        ideaSummary: string;
        clarifyingQA: Array<{ question: string; answer: string | null }>;
        assumptions: unknown[];
        requireStepApproval: boolean;
        lastPhaseTransition: string;
      };
      buildPlan?: unknown;
    }>(
      'POST',
      `/arches/plugin-builder/sessions/${sessionId}/guided/assumption-decision`,
      {
        action,
        verbose,
        useOpus,
        ...(comments ? { comments } : {}),
      },
      { signal },
    );
  }

  async rewindGuidedPhase(sessionId: string, targetPhase: string) {
    return this.request<{
      sessionId: string;
      guidedFlow: {
        phase: string;
        ideaSummary: string;
        clarifyingQA: Array<{ question: string; answer: string | null }>;
        assumptions: unknown[];
        requireStepApproval: boolean;
        lastPhaseTransition: string;
      };
      buildPlan: unknown;
    }>('POST', `/arches/plugin-builder/sessions/${sessionId}/guided/rewind`, {
      targetPhase,
    });
  }

  /**
   * Streams a setup-coach reply for the Plugin "Guide Me" flow. Ephemeral —
   * the server does not persist anything; the caller passes the rolling
   * conversation each turn.
   */
  async streamPluginSetupHelp(
    platform: 'mac' | 'windows' | 'linux' | 'codespaces',
    messages: Array<{ role: 'user' | 'assistant'; content: string }>,
    options?: { signal?: AbortSignal },
  ): Promise<Response> {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    attachCsrfHeader('POST', headers);
    return fetch(`${API_BASE}/arches/plugin-builder/setup-help/stream`, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify({ platform, messages }),
      signal: options?.signal,
    });
  }

  async buildPluginPreview(sessionId: string, files?: Record<string, unknown>) {
    return this.request<{
      success: boolean;
      html: string;
      fileCount: number;
    }>('POST', `/arches/plugin-builder/sessions/${sessionId}/build`, files ? { files } : {});
  }

  async savePluginBuilderSession(
    sessionId: string,
    params: { name: string; description?: string },
  ) {
    return this.request<{ success: boolean; archId: string }>(
      'POST',
      `/arches/plugin-builder/sessions/${sessionId}/save`,
      params,
    );
  }

  async getPluginBuilderIconUploadUrl(sessionId: string, mimeType: string, sizeBytes: number) {
    return this.request<{
      archId: string;
      uploadUrl: string;
      stagingKey: string;
      mimeType: string;
      expiresAt: string;
    }>('POST', `/arches/plugin-builder/sessions/${sessionId}/icon/upload-url`, {
      mimeType,
      sizeBytes,
    });
  }

  async confirmPluginBuilderIcon(sessionId: string, stagingKey: string) {
    return this.request<{
      archId: string;
      iconKey: string;
      imageRef: string;
      variantSizes: number[];
    }>('PUT', `/arches/plugin-builder/sessions/${sessionId}/icon/confirm`, { stagingKey });
  }

  async deletePluginBuilderIcon(sessionId: string) {
    return this.request<void>('DELETE', `/arches/plugin-builder/sessions/${sessionId}/icon`);
  }

  async uploadPluginPreviewScreenshot(sessionId: string, dataUrl: string): Promise<void> {
    await this.request<{ success: boolean }>(
      'POST',
      `/arches/plugin-builder/sessions/${sessionId}/preview-screenshot`,
      { dataUrl },
    );
  }

  async uploadPluginPreviewLogs(
    sessionId: string,
    entries: Array<{ level: string; category: string; message: string; timestamp: number }>,
  ): Promise<void> {
    await this.request<{ success: boolean }>(
      'POST',
      `/arches/plugin-builder/sessions/${sessionId}/preview-logs`,
      { entries },
    );
  }

  async resetPluginBuilderContext(sessionId: string) {
    return this.request<{ success: boolean }>(
      'POST',
      `/arches/plugin-builder/sessions/${sessionId}/reset-context`,
    );
  }

  async upgradePluginBuilderSdk(sessionId: string) {
    return this.request<{
      session: unknown;
      migrationNotes: string | null;
      triggerAiMigration: boolean;
    }>('POST', `/arches/plugin-builder/sessions/${sessionId}/upgrade-sdk`);
  }

  async getPluginPublishCost(sessionId: string) {
    return this.request<{
      isFirstPublish: boolean;
      totalCents: number;
      reviewFeeCents: number;
      listingFeeCents: number;
      /**
       * Estimated AI security review cost (cents). Pass-through priced:
       * raw Claude cost × 1.20 markup, computed when the Lambda runs.
       * This value is the upfront reservation upper bound; the gateway
       * refunds the difference between this and the actual finalize.
       */
      estimatedReviewCents: number;
    }>('GET', `/arches/plugin-builder/sessions/${sessionId}/publish-cost`);
  }

  async publishPluginBuilder(sessionId: string) {
    return this.request<{
      submissionId: string;
      archId: string;
      reviewId: string;
      status: string;
    }>('POST', `/arches/plugin-builder/sessions/${sessionId}/publish`);
  }

  async unpublishPluginSession(sessionId: string) {
    return this.request<{
      archId: string;
      status: string;
      isListed: boolean;
    }>('POST', `/arches/plugin-builder/sessions/${sessionId}/unpublish`);
  }

  async submitPluginProblemReport(
    sessionId: string,
    report: {
      description: string;
      problemScope: number;
      conversationHistory?: unknown;
      currentFiles?: unknown;
      currentSettings?: unknown;
      guidedFlow?: unknown;
      buildPlan?: unknown;
      logEntries?: unknown;
      telemetryState?: unknown;
      guidedBuilderState?: unknown;
    },
  ) {
    return this.request<{ reportId: string; status: string; createdAt: string }>(
      'POST',
      `/arches/plugin-builder/sessions/${sessionId}/report-problem`,
      report,
    );
  }

  async invokePluginAI(
    sessionId: string,
    input: string,
    entityId: string,
  ): Promise<{ output: string }> {
    return this.request<{ output: string }>(
      'POST',
      `/arches/plugin-builder/sessions/${sessionId}/ai-invoke`,
      { input, entityId },
    );
  }

  async generatePluginImage(
    sessionId: string,
    payload: {
      entityId: string;
      prompt: string;
      size?: string;
      quality?: string;
      style?: string;
    },
  ): Promise<{
    imageUrl: string;
    imageRef: string;
    revisedPrompt?: string;
    provider: string;
    model: string;
  }> {
    return this.request(
      'POST',
      `/arches/plugin-builder/sessions/${sessionId}/image-generate`,
      payload,
    );
  }

  /**
   * Preview storage operations — proxied to S3 so preview behaves like published.
   */
  async previewStorage(
    sessionId: string,
    action: 'read' | 'write' | 'list' | 'delete',
    params: { path?: string; content?: string; prefix?: string },
  ): Promise<unknown> {
    return this.request('POST', `/arches/plugin-builder/sessions/${sessionId}/storage`, {
      action,
      ...params,
    });
  }

  // ==========================================================================
  // Builder Templates API
  // ==========================================================================

  async listBuilderTemplates(options?: { category?: string; featured?: boolean }) {
    const params = new URLSearchParams();
    if (options?.category) params.set('category', options.category);
    if (options?.featured !== undefined) params.set('featured', String(options.featured));
    const query = params.toString();
    return this.request<{ templates: BuilderTemplate[] }>(
      'GET',
      `/arches/builder/templates${query ? `?${query}` : ''}`,
    );
  }

  async getBuilderTemplate(templateId: string) {
    return this.request<{ template: BuilderTemplate }>(
      'GET',
      `/arches/builder/templates/${templateId}`,
    );
  }

  // ==========================================================================
  // Builder Reviews API
  // ==========================================================================

  async requestArcheReview(archId: string) {
    return this.request<{
      reviewId: string;
      status: 'pending';
      estimatedCostCents: number;
    }>('POST', `/contributor/arches/${archId}/request-review`);
  }

  async listArcheReviews(archId: string) {
    return this.request<{ reviews: ArcheReviewSummary[] }>(
      'GET',
      `/contributor/arches/${archId}/reviews`,
    );
  }

  async getArcheReview(archId: string, reviewId: string) {
    return this.request<{ review: ArcheReviewDetail }>(
      'GET',
      `/contributor/arches/${archId}/reviews/${reviewId}`,
    );
  }

  // ==========================================================================
  // Chat API
  // ==========================================================================

  // Channel operations

  async listChatChannels(options?: { limit?: number; offset?: number }) {
    const params = new URLSearchParams();
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.offset) params.set('offset', options.offset.toString());
    const query = params.toString();
    return this.request<ChatChannelListResult>(
      'GET',
      `/arches/chat/channels${query ? `?${query}` : ''}`,
    );
  }

  async createChatChannel(input: CreateChatChannelInput) {
    return this.request<{ channel: ChatChannelResponse }>('POST', '/arches/chat/channels', input);
  }

  async getChatChannel(channelId: string) {
    return this.request<{ channel: ChatChannelResponse }>(
      'GET',
      `/arches/chat/channels/${channelId}`,
    );
  }

  async updateChatChannel(
    channelId: string,
    input: {
      name?: string;
      description?: string;
      imageRef?: string | null;
      avatarFileId?: string | null;
      isPrivate?: boolean;
      defaultPermissions?: ChatMemberPermissions;
    },
  ) {
    return this.request<{ channel: ChatChannelResponse }>(
      'PATCH',
      `/arches/chat/channels/${channelId}`,
      input,
    );
  }

  async deleteChatChannel(channelId: string) {
    return this.request<void>('DELETE', `/arches/chat/channels/${channelId}`);
  }

  async getGlobalChatChannel() {
    return this.request<{ channel: ChatChannelResponse }>('GET', '/arches/chat/channels/global');
  }

  async getOrCreateDirectChannel(target: string) {
    const isEmail = target.includes('@');
    return this.request<{ channel: ChatChannelResponse }>('POST', '/arches/chat/channels/direct', {
      ...(isEmail ? { targetEmail: target } : { targetUsername: target }),
    });
  }

  async joinChatChannel(channelId: string) {
    return this.request<{ member: ChatChannelMemberResponse }>(
      'POST',
      `/arches/chat/channels/${channelId}/join`,
    );
  }

  async leaveChatChannel(channelId: string) {
    return this.request<void>('POST', `/arches/chat/channels/${channelId}/leave`);
  }

  async searchChatUsers(channelId: string, username: string) {
    return this.request<{
      users: Array<{ userId: string; username: string; displayName: string; isMember: boolean }>;
    }>(
      'GET',
      `/arches/chat/channels/${channelId}/users/search?username=${encodeURIComponent(username)}`,
    );
  }

  async addChatMember(channelId: string, userId: string) {
    return this.request<{ channelId: string; userId: string; role: string; joinedAt: string }>(
      'POST',
      `/arches/chat/channels/${channelId}/members`,
      { userId },
    );
  }

  async getChatChannelMembers(channelId: string, options?: { limit?: number; offset?: number }) {
    const params = new URLSearchParams();
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.offset) params.set('offset', options.offset.toString());
    const query = params.toString();
    return this.request<{ members: ChatChannelMemberResponse[]; total: number }>(
      'GET',
      `/arches/chat/channels/${channelId}/members${query ? `?${query}` : ''}`,
    );
  }

  async updateChatMemberRole(channelId: string, userId: string, role: string) {
    return this.request<{ member: ChatChannelMemberResponse }>(
      'PATCH',
      `/arches/chat/channels/${channelId}/members/${userId}`,
      { role },
    );
  }

  async updateChatMemberPermissions(
    channelId: string,
    userId: string,
    permissions: ChatMemberPermissions,
  ) {
    return this.request<{
      member: { channelId: string; userId: string; permissions: ChatMemberPermissions };
    }>('PATCH', `/arches/chat/channels/${channelId}/members/${userId}/permissions`, {
      permissions,
    });
  }

  async removeChatMember(channelId: string, userId: string) {
    return this.request<void>('DELETE', `/arches/chat/channels/${channelId}/members/${userId}`);
  }

  async getChatChannelBans(channelId: string) {
    return this.request<{ bans: ChatChannelBanResponse[] }>(
      'GET',
      `/arches/chat/channels/${channelId}/bans`,
    );
  }

  async banChatMember(channelId: string, userId: string, reason?: string) {
    return this.request<{ ban: ChatChannelBanResponse }>(
      'POST',
      `/arches/chat/channels/${channelId}/bans`,
      { userId, reason },
    );
  }

  async unbanChatMember(channelId: string, userId: string) {
    return this.request<void>('DELETE', `/arches/chat/channels/${channelId}/bans/${userId}`);
  }

  async generateChatChannelInvite(channelId: string, expiresInHours?: number) {
    return this.request<{ inviteCode: string; expiresAt: string | null }>(
      'POST',
      `/arches/chat/channels/${channelId}/invite`,
      { expiresInHours },
    );
  }

  async getChatChannelByInviteCode(code: string) {
    return this.request<{
      channel: {
        channelId: string;
        name: string | null;
        description: string | null;
        avatarFileId: string | null;
        imageRef: string | null;
      };
    }>('GET', `/arches/chat/channels/invite/${code}`);
  }

  async joinChatChannelByInvite(code: string) {
    return this.request<{ channelId: string; userId: string; role: string; joinedAt: string }>(
      'POST',
      `/arches/chat/channels/invite/${code}/join`,
    );
  }

  async getChatDmPartnerProfile(channelId: string) {
    return this.request<{ profile: ChatDmPartnerProfile }>(
      'GET',
      `/arches/chat/channels/${channelId}/partner-profile`,
    );
  }

  async getChatUserProfile(userId: string) {
    return this.request<{ profile: ChatDmPartnerProfile }>(
      'GET',
      `/arches/chat/users/${userId}/profile`,
    );
  }

  async muteChatChannel(channelId: string) {
    return this.request<void>('POST', `/arches/chat/channels/${channelId}/mute`);
  }

  async unmuteChatChannel(channelId: string) {
    return this.request<void>('DELETE', `/arches/chat/channels/${channelId}/mute`);
  }

  async pinChatChannel(channelId: string) {
    return this.request<void>('POST', `/arches/chat/channels/${channelId}/pin`);
  }

  async unpinChatChannel(channelId: string) {
    return this.request<void>('DELETE', `/arches/chat/channels/${channelId}/pin`);
  }

  async markChatChannelRead(channelId: string, messageId: string) {
    return this.request<void>('POST', `/arches/chat/channels/${channelId}/read`, { messageId });
  }

  async getChatUnreadCounts() {
    return this.request<{ channels: ChatUnreadCount[] }>('GET', '/arches/chat/unread');
  }

  // Message operations

  async listChatMessages(
    channelId: string,
    options?: { before?: string; after?: string; limit?: number },
  ) {
    const params = new URLSearchParams();
    if (options?.before) params.set('before', options.before);
    if (options?.after) params.set('after', options.after);
    if (options?.limit) params.set('limit', options.limit.toString());
    const query = params.toString();
    return this.request<ChatMessageListResult>(
      'GET',
      `/arches/chat/channels/${channelId}/messages${query ? `?${query}` : ''}`,
    );
  }

  async sendChatMessage(
    channelId: string,
    input: {
      content: string;
      replyToId?: string;
      mentions?: Array<{ userId: string; displayName: string }>;
      attachmentRefs?: ChatAttachmentRef[];
    },
  ) {
    return this.request<{ message: ChatMessageResponse }>(
      'POST',
      `/arches/chat/channels/${channelId}/messages`,
      input,
    );
  }

  async editChatMessage(messageId: string, content: string) {
    return this.request<{ message: ChatMessageResponse }>(
      'PATCH',
      `/arches/chat/messages/${messageId}`,
      {
        content,
      },
    );
  }

  async deleteChatMessage(messageId: string) {
    return this.request<void>('DELETE', `/arches/chat/messages/${messageId}`);
  }

  async searchChatMessages(
    channelId: string,
    query: string,
    options?: { limit?: number; offset?: number },
  ) {
    const params = new URLSearchParams();
    params.set('q', query);
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.offset) params.set('offset', options.offset.toString());
    return this.request<{ messages: ChatMessageResponse[]; total: number }>(
      'GET',
      `/arches/chat/channels/${channelId}/messages/search?${params.toString()}`,
    );
  }

  async getChatPinnedMessages(channelId: string) {
    return this.request<{ pins: ChatPinnedMessageResponse[] }>(
      'GET',
      `/arches/chat/channels/${channelId}/pins`,
    );
  }

  async pinChatMessage(messageId: string) {
    return this.request<void>('POST', `/arches/chat/messages/${messageId}/pin`);
  }

  async unpinChatMessage(messageId: string) {
    return this.request<void>('DELETE', `/arches/chat/messages/${messageId}/pin`);
  }

  async addChatReaction(messageId: string, emoji: string) {
    return this.request<void>('POST', `/arches/chat/messages/${messageId}/reactions`, { emoji });
  }

  async removeChatReaction(messageId: string, emoji: string) {
    return this.request<void>(
      'DELETE',
      `/arches/chat/messages/${messageId}/reactions/${encodeURIComponent(emoji)}`,
    );
  }

  // Moderation operations

  async reportChatMessage(input: {
    messageId?: string;
    channelId?: string;
    reason: string;
    description?: string;
  }) {
    return this.request<{ report: { reportId: string } }>('POST', '/arches/chat/reports', input);
  }

  async blockChatUser(blockedId: string) {
    return this.request<void>('POST', '/arches/chat/blocks', { blockedId });
  }

  async unblockChatUser(blockedId: string) {
    return this.request<void>('DELETE', `/arches/chat/blocks/${blockedId}`);
  }

  async listChatBlockedUsers() {
    return this.request<{ blocks: ChatBlockResponse[] }>('GET', '/arches/chat/blocks');
  }

  async getChatSettings() {
    return this.request<{ settings: ChatUserSettingsResponse }>('GET', '/arches/chat/settings');
  }

  async updateChatSettings(input: UpdateChatUserSettingsInput) {
    return this.request<{ settings: ChatUserSettingsResponse }>(
      'PUT',
      '/arches/chat/settings',
      input,
    );
  }

  // ==========================================================================
  // Guild Methods
  // ==========================================================================

  async listGuilds(options?: {
    search?: string;
    guildType?: string;
    sortBy?: string;
    limit?: number;
    offset?: number;
  }) {
    const params = new URLSearchParams();
    if (options?.search) params.set('search', options.search);
    if (options?.guildType) params.set('guildType', options.guildType);
    if (options?.sortBy) params.set('sortBy', options.sortBy);
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset) params.set('offset', String(options.offset));
    const qs = params.toString();
    return this.request<GuildListResult>('GET', `/arches/guilds${qs ? `?${qs}` : ''}`);
  }

  async getGuildDetail(guildId: string) {
    return this.request<GuildDetailResponse>('GET', `/arches/guilds/${guildId}/detail`);
  }

  async getGuildInviteInfo(inviteCode: string) {
    return this.request<{ guild: GuildSummary; invite: GuildInviteInfo }>(
      'GET',
      `/arches/guilds/invite/${inviteCode}`,
    );
  }

  async createGuild(input: CreateGuildPayload) {
    return this.request<GuildDetailResponse>('POST', '/arches/guilds', input);
  }

  async updateGuild(guildId: string, input: UpdateGuildPayload) {
    return this.request<GuildDetailResponse>('PATCH', `/arches/guilds/${guildId}`, input);
  }

  async deleteGuild(guildId: string) {
    return this.request<void>('DELETE', `/arches/guilds/${guildId}`);
  }

  async getMyGuilds() {
    return this.request<{ guilds: GuildSummary[] }>('GET', '/arches/guilds/me');
  }

  async joinGuild(guildId: string) {
    return this.request<GuildMemberResponse>('POST', `/arches/guilds/${guildId}/join`);
  }

  async leaveGuild(guildId: string) {
    return this.request<void>('POST', `/arches/guilds/${guildId}/leave`);
  }

  async getGuildMembers(
    guildId: string,
    options?: { role?: string; limit?: number; offset?: number },
  ) {
    const params = new URLSearchParams();
    if (options?.role) params.set('role', options.role);
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset) params.set('offset', String(options.offset));
    const qs = params.toString();
    return this.request<GuildMemberListResult>(
      'GET',
      `/arches/guilds/${guildId}/members${qs ? `?${qs}` : ''}`,
    );
  }

  async updateGuildMemberRole(guildId: string, userId: string, role: string) {
    return this.request<GuildMemberResponse>(
      'PATCH',
      `/arches/guilds/${guildId}/members/${userId}`,
      {
        role,
      },
    );
  }

  async removeGuildMember(guildId: string, userId: string) {
    return this.request<void>('DELETE', `/arches/guilds/${guildId}/members/${userId}`);
  }

  async transferGuildOwnership(guildId: string, newOwnerId: string) {
    return this.request<{ success: boolean }>('POST', `/arches/guilds/${guildId}/transfer`, {
      newOwnerId,
    });
  }

  async createGuildInvite(
    guildId: string,
    options?: { maxUses?: number; expiresInHours?: number },
  ) {
    return this.request<GuildInviteResponse>(
      'POST',
      `/arches/guilds/${guildId}/invites`,
      options || {},
    );
  }

  async listGuildInvites(guildId: string) {
    return this.request<{ invites: GuildInviteResponse[] }>(
      'GET',
      `/arches/guilds/${guildId}/invites`,
    );
  }

  async revokeGuildInvite(guildId: string, inviteId: string) {
    return this.request<void>('DELETE', `/arches/guilds/${guildId}/invites/${inviteId}`);
  }

  async acceptGuildInvite(inviteCode: string) {
    return this.request<GuildMemberResponse>('POST', `/arches/guilds/invite/${inviteCode}/accept`);
  }

  async banGuildMember(guildId: string, userId: string, reason?: string) {
    return this.request<GuildBanResponse>('POST', `/arches/guilds/${guildId}/bans`, {
      userId,
      reason,
    });
  }

  async unbanGuildMember(guildId: string, banId: string) {
    return this.request<void>('DELETE', `/arches/guilds/${guildId}/bans/${banId}`);
  }

  async listGuildBans(guildId: string) {
    return this.request<{ bans: GuildBanResponse[] }>('GET', `/arches/guilds/${guildId}/bans`);
  }

  async listGuildActivity(
    guildId: string,
    options?: { activityType?: string; limit?: number; offset?: number },
  ) {
    const params = new URLSearchParams();
    if (options?.activityType) params.set('activityType', options.activityType);
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset) params.set('offset', String(options.offset));
    const qs = params.toString();
    return this.request<GuildActivityResult>(
      'GET',
      `/arches/guilds/${guildId}/activity${qs ? `?${qs}` : ''}`,
    );
  }

  async getGuildTreasury(guildId: string) {
    return this.request<GuildTreasuryResponse>('GET', `/arches/guilds/${guildId}/treasury`);
  }

  async listGuildTreasuryTransactions(
    guildId: string,
    options?: { limit?: number; offset?: number },
  ) {
    const params = new URLSearchParams();
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset) params.set('offset', String(options.offset));
    const qs = params.toString();
    return this.request<{ transactions: GuildTreasuryTransactionResponse[] }>(
      'GET',
      `/arches/guilds/${guildId}/treasury/transactions${qs ? `?${qs}` : ''}`,
    );
  }

  // ==========================================================================
  // Learn Anything - Course API
  // ==========================================================================

  async listCourses(options?: {
    status?: string;
    difficultyLevel?: string;
    limit?: number;
    offset?: number;
  }) {
    const params = new URLSearchParams();
    if (options?.status) params.set('status', options.status);
    if (options?.difficultyLevel) params.set('difficultyLevel', options.difficultyLevel);
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset) params.set('offset', String(options.offset));
    const qs = params.toString();
    return this.request<{
      courses: Array<{
        courseId: string;
        slug: string;
        title: string;
        description: string | null;
        difficultyLevel: string;
        estimatedDurationHours: number | null;
        coverImageRef: string | null;
        categoryId: string | null;
        tags: string[];
        status: string;
        createdAt: string;
        updatedAt: string;
        publishedAt: string | null;
      }>;
      total: number;
      limit: number;
      offset: number;
    }>('GET', `/arches/learn-anything/courses${qs ? `?${qs}` : ''}`);
  }

  async getCourseCurriculum(slug: string) {
    return this.request<{
      courseId: string;
      slug: string;
      title: string;
      description: string | null;
      difficultyLevel: string;
      estimatedDurationHours: number | null;
      tags: string[];
      status: string;
      modules: Array<{
        moduleId: string;
        title: string;
        description: string | null;
        sequenceOrder: number;
        estimatedDurationMinutes: number | null;
        lessons: Array<{
          lessonId: string;
          title: string;
          lessonType: string;
          sequenceOrder: number;
          estimatedDurationMinutes: number | null;
        }>;
      }>;
    }>('GET', `/arches/learn-anything/courses/${slug}/curriculum`);
  }

  async enrollCourse(slug: string) {
    return this.request<{ enrollmentId: string; status: string }>(
      'POST',
      `/arches/learn-anything/courses/${slug}/enroll`,
    );
  }

  async getCourseProgress(slug: string) {
    return this.request<{
      enrollmentId: string | null;
      lessons: Record<string, { correctCount: number; targetQuizCount: number; status: string }>;
    }>('GET', `/arches/learn-anything/courses/${slug}/progress`);
  }

  async updateLessonProgress(
    lessonId: string,
    data: { correctCount: number; targetQuizCount: number; status: string },
  ) {
    return this.request<{ success: boolean }>(
      'PATCH',
      `/arches/learn-anything/lessons/${lessonId}/progress`,
      data,
    );
  }

  // ==========================================================================
  // DFE Inventory API
  // ==========================================================================

  async listDfeDefinitions(options?: {
    search?: string;
    fungibility?: string;
    limit?: number;
    offset?: number;
  }) {
    const params = new URLSearchParams();
    if (options?.search) params.set('search', options.search);
    if (options?.fungibility) params.set('fungibility', options.fungibility);
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset) params.set('offset', String(options.offset));
    const qs = params.toString();
    return this.request<DfeDefinitionListResult>(
      'GET',
      `/arches/dfe/definitions${qs ? `?${qs}` : ''}`,
    );
  }

  async getDfeDefinition(itemDefId: string) {
    return this.request<DfeDefinitionDetail>('GET', `/arches/dfe/definitions/${itemDefId}`);
  }

  async getDfeInventory(options?: {
    search?: string;
    fungibility?: string;
    category?: string;
    sortBy?: string;
    sortOrder?: string;
    limit?: number;
    offset?: number;
  }) {
    const params = new URLSearchParams();
    if (options?.search) params.set('search', options.search);
    if (options?.fungibility) params.set('fungibility', options.fungibility);
    if (options?.category !== undefined) params.set('category', options.category);
    if (options?.sortBy) params.set('sortBy', options.sortBy);
    if (options?.sortOrder) params.set('sortOrder', options.sortOrder);
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset) params.set('offset', String(options.offset));
    const qs = params.toString();
    return this.request<DfeInventoryListResult>(
      'GET',
      `/arches/dfe/inventory${qs ? `?${qs}` : ''}`,
    );
  }

  async getDfeInventoryItem(itemId: string) {
    return this.request<DfeInventoryItemDetail>('GET', `/arches/dfe/inventory/${itemId}`);
  }

  async transferDfeItem(input: TransferDfeItemInput) {
    return this.request<{ success: boolean }>('POST', '/arches/dfe/transfer', input);
  }

  async burnDfeItem(itemId: string) {
    return this.request<{ success: boolean }>('DELETE', `/arches/dfe/inventory/${itemId}`);
  }

  async burnDfeFungibleItems(input: {
    itemDefId: string;
    quantity: number;
    category?: string;
    position?: string;
  }) {
    return this.request<{ success: boolean }>('POST', '/arches/dfe/inventory/burn-fungible', input);
  }

  async createDfeDefinition(input: CreateDfeDefinitionInput) {
    return this.request<DfeDefinitionDetail>('POST', '/arches/dfe/admin/definitions', input);
  }

  async updateDfeDefinition(
    itemDefId: string,
    input: UpdateDfeDefinitionInput | Record<string, unknown>,
  ) {
    return this.request<DfeDefinitionDetail>(
      'PATCH',
      `/arches/dfe/admin/definitions/${itemDefId}`,
      input,
    );
  }

  async archiveDfeDefinition(itemDefId: string) {
    return this.request<{ itemDefId: string; name: string; status: string }>(
      'DELETE',
      `/arches/dfe/admin/definitions/${itemDefId}`,
    );
  }

  async mintDfeItems(input: {
    itemDefId: string;
    toUsername: string;
    quantity: number;
    metadata?: Record<string, unknown>;
    notes?: string;
    category?: string;
    position?: string;
  }) {
    return this.request<DfeMintResult>('POST', '/arches/dfe/admin/mint', input);
  }

  async listDfeTransfers(options?: {
    itemDefId?: string;
    userId?: string;
    transferType?: string;
    limit?: number;
    offset?: number;
  }) {
    const params = new URLSearchParams();
    if (options?.itemDefId) params.set('itemDefId', options.itemDefId);
    if (options?.userId) params.set('userId', options.userId);
    if (options?.transferType) params.set('transferType', options.transferType);
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset) params.set('offset', String(options.offset));
    const qs = params.toString();
    return this.request<DfeEnrichedTransferListResult>(
      'GET',
      `/arches/dfe/admin/transfers${qs ? `?${qs}` : ''}`,
    );
  }

  async getDfeTransferHistory(options?: { limit?: number; offset?: number }) {
    const params = new URLSearchParams();
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset) params.set('offset', String(options.offset));
    const qs = params.toString();
    return this.request<DfeEnrichedTransferListResult>(
      'GET',
      `/arches/dfe/transfer-history${qs ? `?${qs}` : ''}`,
    );
  }

  async updateDfeItemPlacement(itemId: string, input: { category?: string; position?: string }) {
    return this.request<{ itemId: string; category: string; position: string }>(
      'PATCH',
      `/arches/dfe/inventory/${itemId}/placement`,
      input,
    );
  }

  async moveDfeFungibleStack(input: {
    itemDefId: string;
    quantity: number;
    fromCategory: string;
    fromPosition: string;
    toCategory: string;
    toPosition: string;
  }) {
    return this.request<{ success: boolean }>('POST', '/arches/dfe/inventory/move-fungible', input);
  }

  async getDfePositionConflicts() {
    return this.request<{ conflicts: DfePositionConflict[] }>(
      'GET',
      '/arches/dfe/inventory/position-conflicts',
    );
  }

  // =========================================================================
  // DFE Inventory Transition
  // =========================================================================

  async importDfeLegacyInventory(input: { username: string; password: string }) {
    return this.request<DfeLegacyImportResult>(
      'POST',
      '/arches/dfe-inventory-transition/import',
      input,
    );
  }

  async getDfeLegacyImportStatus() {
    return this.request<{ imports: DfeLegacyImportEntry[] }>(
      'GET',
      '/arches/dfe-inventory-transition/import/status',
    );
  }

  async retryDfeLegacyImport(importId: string, password: string) {
    return this.request<DfeLegacyImportResult>(
      'POST',
      `/arches/dfe-inventory-transition/import/${importId}/retry`,
      { password },
    );
  }

  async checkDfeLegacyUsername(username: string) {
    const params = new URLSearchParams({ username });
    return this.request<{ imported: boolean }>(
      'GET',
      `/arches/dfe-inventory-transition/import/check?${params.toString()}`,
    );
  }

  async listAllDfeLegacyImports() {
    return this.request<AdminDfeLegacyImportListResult>(
      'GET',
      '/arches/dfe-inventory-transition/admin/imports',
    );
  }

  async deleteDfeLegacyImport(importId: string) {
    return this.request<{ success: boolean }>(
      'DELETE',
      `/arches/dfe-inventory-transition/admin/imports/${importId}`,
    );
  }

  // ==========================================================================
  // LitSquad (arche_litsquad) — NFT inventory
  // ==========================================================================

  async listLitSquadDefinitions(options?: {
    search?: string;
    limit?: number;
    offset?: number;
  }): Promise<LitSquadDefinitionListResult> {
    const params = new URLSearchParams();
    if (options?.search) params.set('search', options.search);
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset) params.set('offset', String(options.offset));
    const qs = params.toString();
    return this.request<LitSquadDefinitionListResult>(
      'GET',
      `/arches/litsquad/definitions${qs ? `?${qs}` : ''}`,
    );
  }

  async getLitSquadDefinition(itemDefId: string): Promise<LitSquadDefinitionDetail> {
    return this.request<LitSquadDefinitionDetail>(
      'GET',
      `/arches/litsquad/definitions/${itemDefId}`,
    );
  }

  async getLitSquadInventory(options?: {
    search?: string;
    category?: string;
    sortBy?: string;
    sortOrder?: string;
    limit?: number;
    offset?: number;
  }): Promise<LitSquadInventoryListResult> {
    const params = new URLSearchParams();
    if (options?.search) params.set('search', options.search);
    if (options?.category !== undefined) params.set('category', options.category);
    if (options?.sortBy) params.set('sortBy', options.sortBy);
    if (options?.sortOrder) params.set('sortOrder', options.sortOrder);
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset !== undefined) params.set('offset', String(options.offset));
    const qs = params.toString();
    return this.request<LitSquadInventoryListResult>(
      'GET',
      `/arches/litsquad/inventory${qs ? `?${qs}` : ''}`,
    );
  }

  async getLitSquadInventoryItem(itemId: string): Promise<LitSquadInventoryItemDetail> {
    return this.request<LitSquadInventoryItemDetail>('GET', `/arches/litsquad/inventory/${itemId}`);
  }

  async burnLitSquadItem(itemId: string): Promise<{ success: boolean }> {
    return this.request<{ success: boolean }>('DELETE', `/arches/litsquad/inventory/${itemId}`);
  }

  async updateLitSquadItemPlacement(
    itemId: string,
    input: { category?: string; position?: string },
  ): Promise<{ itemId: string; category: string; position: string }> {
    return this.request<{ itemId: string; category: string; position: string }>(
      'PATCH',
      `/arches/litsquad/inventory/${itemId}/placement`,
      input,
    );
  }

  async transferLitSquadItem(input: TransferLitSquadItemInput): Promise<{ success: boolean }> {
    return this.request<{ success: boolean }>('POST', '/arches/litsquad/transfer', input);
  }

  async getLitSquadTransferHistory(options?: {
    limit?: number;
    offset?: number;
  }): Promise<LitSquadEnrichedTransferListResult> {
    const params = new URLSearchParams();
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset !== undefined) params.set('offset', String(options.offset));
    const qs = params.toString();
    return this.request<LitSquadEnrichedTransferListResult>(
      'GET',
      `/arches/litsquad/transfer-history${qs ? `?${qs}` : ''}`,
    );
  }

  async createLitSquadDefinition(
    input: CreateLitSquadDefinitionInput,
  ): Promise<LitSquadDefinitionDetail> {
    return this.request<LitSquadDefinitionDetail>(
      'POST',
      '/arches/litsquad/admin/definitions',
      input,
    );
  }

  async updateLitSquadDefinition(
    itemDefId: string,
    input: UpdateLitSquadDefinitionInput,
  ): Promise<LitSquadDefinitionDetail> {
    return this.request<LitSquadDefinitionDetail>(
      'PATCH',
      `/arches/litsquad/admin/definitions/${itemDefId}`,
      input,
    );
  }

  async archiveLitSquadDefinition(
    itemDefId: string,
  ): Promise<{ itemDefId: string; name: string; status: string; updatedAt: string }> {
    return this.request<{ itemDefId: string; name: string; status: string; updatedAt: string }>(
      'DELETE',
      `/arches/litsquad/admin/definitions/${itemDefId}`,
    );
  }

  async mintLitSquadItems(input: {
    itemDefId: string;
    toUserId?: string;
    toUsername?: string;
    quantity: number;
    displayName?: string;
    thumbnailUri?: string;
    metadata?: Record<string, unknown>;
    notes?: string;
    category?: string;
    position?: string;
  }): Promise<LitSquadMintResult> {
    return this.request<LitSquadMintResult>('POST', '/arches/litsquad/admin/mint', input);
  }

  // ==========================================================================
  // LitSquad Accessory Crafting
  // ==========================================================================

  async getLitSquadCraftIngredients(): Promise<LitSquadCraftIngredientsResult> {
    return this.request<LitSquadCraftIngredientsResult>(
      'GET',
      '/arches/litsquad/craft/ingredients',
    );
  }

  async previewLitSquadCraft(input: LitSquadCraftInput): Promise<LitSquadCraftPreviewResult> {
    return this.request<LitSquadCraftPreviewResult>(
      'POST',
      '/arches/litsquad/craft/preview',
      input,
    );
  }

  async executeLitSquadCraft(input: LitSquadCraftInput): Promise<LitSquadCraftResult> {
    return this.request<LitSquadCraftResult>('POST', '/arches/litsquad/craft', input);
  }

  async listLitSquadAdminTransfers(options?: {
    itemDefId?: string;
    userId?: string;
    transferType?: string;
    limit?: number;
    offset?: number;
  }): Promise<LitSquadEnrichedTransferListResult> {
    const params = new URLSearchParams();
    if (options?.itemDefId) params.set('itemDefId', options.itemDefId);
    if (options?.userId) params.set('userId', options.userId);
    if (options?.transferType) params.set('transferType', options.transferType);
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset !== undefined) params.set('offset', String(options.offset));
    const qs = params.toString();
    return this.request<LitSquadEnrichedTransferListResult>(
      'GET',
      `/arches/litsquad/admin/transfers${qs ? `?${qs}` : ''}`,
    );
  }

  async getGuildChatChannel(guildId: string) {
    return this.request<{ channelId: string }>('GET', `/arches/guilds/${guildId}/channel`);
  }

  // ==========================================================================
  // Game Rewards Methods
  // ==========================================================================

  // Public
  async getGameRewardsTiers() {
    return this.request<GrTierListResult>('GET', '/arches/game-rewards/tiers');
  }

  // User - Subscription
  async getGameRewardsSubscription() {
    return this.request<{ subscription: GrSubscriptionResponse | null }>(
      'GET',
      '/arches/game-rewards/subscription',
    );
  }

  async purchaseGameRewardsSubscription(tier: string) {
    return this.request<{ subscription: GrSubscriptionResponse }>(
      'POST',
      '/arches/game-rewards/subscription/purchase',
      { tier },
    );
  }

  async cancelGameRewardsSubscription() {
    return this.request<{ success: boolean }>('POST', '/arches/game-rewards/subscription/cancel');
  }

  async getGameRewardsSubscriptionHistory(options?: { limit?: number; offset?: number }) {
    const params = new URLSearchParams();
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset) params.set('offset', String(options.offset));
    const qs = params.toString();
    return this.request<GrSubscriptionHistoryResult>(
      'GET',
      `/arches/game-rewards/subscription/history${qs ? `?${qs}` : ''}`,
    );
  }

  // User - LitCraft Store
  async getLitCraftStoreProducts() {
    return this.request<LitCraftStoreResponse>('GET', '/arches/game-rewards/store/products');
  }

  async purchaseLitCraftStoreProduct(productIdentifier: string) {
    return this.request<LitCraftStorePurchaseResponse>(
      'POST',
      '/arches/game-rewards/store/purchase',
      { productIdentifier },
    );
  }

  // User - Rewards
  async getGameRewardsHistory(options?: { limit?: number; offset?: number }) {
    const params = new URLSearchParams();
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset) params.set('offset', String(options.offset));
    const qs = params.toString();
    return this.request<GrRewardLogListResult>(
      'GET',
      `/arches/game-rewards/rewards${qs ? `?${qs}` : ''}`,
    );
  }

  // Admin - Games
  async listGameRewardsGames(options?: {
    isActive?: boolean;
    reviewStatus?: GrGameConfigReviewStatus;
    /** Include soft-deleted rows alongside live ones. */
    includeDeleted?: boolean;
    /** Return ONLY soft-deleted rows. The admin "Deleted" filter chip. */
    onlyDeleted?: boolean;
    limit?: number;
    offset?: number;
  }) {
    const params = new URLSearchParams();
    if (options?.isActive !== undefined) params.set('isActive', String(options.isActive));
    if (options?.reviewStatus) params.set('reviewStatus', options.reviewStatus);
    if (options?.includeDeleted) params.set('includeDeleted', 'true');
    if (options?.onlyDeleted) params.set('onlyDeleted', 'true');
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset) params.set('offset', String(options.offset));
    const qs = params.toString();
    return this.request<GrGameConfigListResult>(
      'GET',
      `/arches/game-rewards/admin/games${qs ? `?${qs}` : ''}`,
    );
  }

  async approveGameRewardsGame(gameConfigId: string) {
    return this.request<{
      gameConfigId: string;
      gameName: string;
      reviewStatus: GrGameConfigReviewStatus;
      updatedAt: string;
    }>('POST', `/arches/game-rewards/admin/games/${gameConfigId}/approve`);
  }

  async rejectGameRewardsGame(gameConfigId: string, reason?: string) {
    return this.request<{
      gameConfigId: string;
      gameName: string;
      reviewStatus: GrGameConfigReviewStatus;
      updatedAt: string;
    }>('POST', `/arches/game-rewards/admin/games/${gameConfigId}/reject`, reason ? { reason } : {});
  }

  /**
   * Soft-delete a game config. The row is hidden from admin lists and
   * the claim path; historical reward-log entries still surface via
   * LEFT JOIN. Reversible via {@link restoreGameRewardsGame}. For
   * irreversible removal use {@link purgeGameRewardsGame}.
   */
  async deleteGameRewardsGame(gameConfigId: string) {
    return this.request<{
      gameConfigId: string;
      gameName: string;
      deletedAt: string | null;
    }>('DELETE', `/arches/game-rewards/admin/games/${gameConfigId}`);
  }

  /**
   * Reverse a soft-delete. The config reappears in admin lists and
   * accepts new claims again. Idempotent on already-live configs.
   */
  async restoreGameRewardsGame(gameConfigId: string) {
    return this.request<{
      gameConfigId: string;
      gameName: string;
      deletedAt: string | null;
    }>('POST', `/arches/game-rewards/admin/games/${gameConfigId}/restore`);
  }

  /**
   * Hard-delete a game config and all of its children (reward log,
   * rules, recipes, leaderboards, time-played). Irreversible. The
   * server requires `confirmGameName` to match the row's current
   * game_name — pass the name the admin typed into the UI's
   * confirmation field.
   *
   * If gr_reward_log has more rows than the server-side threshold, the
   * call returns a 409 CONFLICT with `{ rewardLogCount, threshold,
   * forceRequired: true }`. Re-call with `force: true` to override.
   */
  async purgeGameRewardsGame(
    gameConfigId: string,
    confirmGameName: string,
    options: { force?: boolean } = {},
  ) {
    return this.request<{
      gameConfigId: string;
      gameName: string;
      rewardLogRowsDeleted: number;
      purged: true;
    }>('DELETE', `/arches/game-rewards/admin/games/${gameConfigId}/purge`, {
      confirmGameName,
      ...(options.force ? { force: true } : {}),
    });
  }

  async getGameRewardsGame(gameConfigId: string) {
    return this.request<GrGameConfigDetail>(
      'GET',
      `/arches/game-rewards/admin/games/${gameConfigId}`,
    );
  }

  async createGameRewardsGame(input: {
    gameName: string;
    displayName: string;
    description?: string;
    minScore?: number;
    maxScore?: number;
  }) {
    return this.request<GrGameConfigCreateResult>(
      'POST',
      '/arches/game-rewards/admin/games',
      input,
    );
  }

  async updateGameRewardsGame(
    gameConfigId: string,
    input: {
      displayName?: string;
      description?: string;
      minScore?: number;
      maxScore?: number;
      leaderboardType?: GrLeaderboardType;
      rewardMode?: GrRewardMode;
      itemListFormat?: GrItemListFormat;
      allowedItems?: string[] | null;
      allowedItemPrefixes?: string[] | null;
      claimCosts?: GrClaimCostRule[] | null;
      claimStateKeyword?: string | null;
      rewardLookupEvent?: string | null;
      isActive?: boolean;
      linkedArcheId?: string | null;
      leaderboardTargetGameId?: string | null;
    },
  ) {
    return this.request<GrGameConfigDetail>(
      'PATCH',
      `/arches/game-rewards/admin/games/${gameConfigId}`,
      input,
    );
  }

  /**
   * Clone an existing game config + its reward rules into a new game config.
   * The clone resets gameName/displayName/api-key/linked-arche/review-status;
   * everything else copies (including leaderboardTargetGameId).
   */
  async cloneGameRewardsGame(input: {
    sourceGameConfigId: string;
    gameName: string;
    displayName: string;
  }) {
    return this.request<{
      gameConfigId: string;
      gameName: string;
      displayName: string;
      reviewStatus: GrGameConfigReviewStatus;
      rulesCopied: number;
      apiKey: string;
      apiKeyPrefix: string;
    }>('POST', '/arches/game-rewards/admin/games/clone', input);
  }

  async rotateGameRewardsApiKey(gameConfigId: string) {
    return this.request<{ gameConfigId: string; apiKey: string; apiKeyPrefix: string }>(
      'POST',
      `/arches/game-rewards/admin/games/${gameConfigId}/rotate-key`,
    );
  }

  // Admin - Reward Rules
  async listGameRewardsRules(options?: {
    gameConfigId?: string;
    gameEvent?: string;
    isActive?: boolean;
    limit?: number;
    offset?: number;
  }) {
    const params = new URLSearchParams();
    if (options?.gameConfigId) params.set('gameConfigId', options.gameConfigId);
    if (options?.gameEvent) params.set('gameEvent', options.gameEvent);
    if (options?.isActive !== undefined) params.set('isActive', String(options.isActive));
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset) params.set('offset', String(options.offset));
    const qs = params.toString();
    return this.request<GrRewardRuleListResult>(
      'GET',
      `/arches/game-rewards/admin/rules${qs ? `?${qs}` : ''}`,
    );
  }

  async createGameRewardsRule(input: {
    gameConfigId: string;
    gameEvent: string;
    rewardType: string;
    /** Item name (e.g. "Heptal"). Server resolves to item_def_id. */
    rewardItemName: string;
    m: number;
    b: number;
    maxRewardCount?: number;
    bronzeMultiplier?: number;
    silverMultiplier?: number;
    goldMultiplier?: number;
    steps?: GrStepDefinition[] | null;
    baseMultiplier?: number;
  }) {
    return this.request<GrRewardRuleResponse>('POST', '/arches/game-rewards/admin/rules', input);
  }

  async updateGameRewardsRule(
    ruleId: string,
    input: {
      m?: number;
      b?: number;
      maxRewardCount?: number;
      bronzeMultiplier?: number;
      silverMultiplier?: number;
      goldMultiplier?: number;
      steps?: GrStepDefinition[] | null;
      baseMultiplier?: number;
      isActive?: boolean;
      /** Item name to swap the rule's reward to. Server resolves to item_def_id. */
      rewardItemName?: string;
    },
  ) {
    return this.request<GrRewardRuleResponse>(
      'PATCH',
      `/arches/game-rewards/admin/rules/${ruleId}`,
      input,
    );
  }

  /**
   * List recent claim rejections (admin Recent Rejections panel).
   */
  async listGameRewardsRejections(options?: {
    archeId?: string;
    attemptedGameName?: string;
    /** Player-targeting filter — UIs send `username`; server resolves to userId. */
    username?: string;
    userId?: string;
    sinceHours?: number;
    limit?: number;
    offset?: number;
  }) {
    const params = new URLSearchParams();
    if (options?.archeId) params.set('archeId', options.archeId);
    if (options?.attemptedGameName) params.set('attemptedGameName', options.attemptedGameName);
    if (options?.username) params.set('username', options.username);
    if (options?.userId) params.set('userId', options.userId);
    if (options?.sinceHours) params.set('sinceHours', String(options.sinceHours));
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset) params.set('offset', String(options.offset));
    const qs = params.toString();
    return this.request<GrClaimRejectionListResult>(
      'GET',
      `/arches/game-rewards/admin/rejections${qs ? `?${qs}` : ''}`,
    );
  }

  async deleteGameRewardsRule(ruleId: string) {
    return this.request<{ success: boolean }>(
      'DELETE',
      `/arches/game-rewards/admin/rules/${ruleId}`,
    );
  }

  // Admin - Rate Limits
  async listGameRewardsLimits(options?: { limit?: number; offset?: number }) {
    const params = new URLSearchParams();
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset) params.set('offset', String(options.offset));
    const qs = params.toString();
    return this.request<GrRewardTypeLimitListResult>(
      'GET',
      `/arches/game-rewards/admin/limits${qs ? `?${qs}` : ''}`,
    );
  }

  async createGameRewardsLimit(input: {
    rewardType: string;
    minSecondsBetweenRewards?: number;
    maxRewardsPerDay?: number;
  }) {
    return this.request<GrRewardTypeLimitResponse>(
      'POST',
      '/arches/game-rewards/admin/limits',
      input,
    );
  }

  async updateGameRewardsLimit(
    limitId: string,
    input: { minSecondsBetweenRewards?: number; maxRewardsPerDay?: number },
  ) {
    return this.request<GrRewardTypeLimitResponse>(
      'PATCH',
      `/arches/game-rewards/admin/limits/${limitId}`,
      input,
    );
  }

  async deleteGameRewardsLimit(limitId: string) {
    return this.request<{ success: boolean }>(
      'DELETE',
      `/arches/game-rewards/admin/limits/${limitId}`,
    );
  }

  // Admin - Tier Config
  async listGameRewardsAdminTiers() {
    return this.request<GrAdminTierListResult>('GET', '/arches/game-rewards/admin/tiers');
  }

  async createGameRewardsTier(input: {
    tier: string;
    displayName: string;
    currencyItemDefId: string;
    priceAmount: number;
    durationDays: number;
    description?: string;
  }) {
    return this.request<GrTierConfigResponse>('POST', '/arches/game-rewards/admin/tiers', input);
  }

  async updateGameRewardsTier(
    tierId: string,
    input: {
      displayName?: string;
      currencyItemDefId?: string;
      priceAmount?: number;
      durationDays?: number;
      description?: string;
      isActive?: boolean;
    },
  ) {
    return this.request<GrTierConfigResponse>(
      'PATCH',
      `/arches/game-rewards/admin/tiers/${tierId}`,
      input,
    );
  }

  // Admin - Subscriptions
  async listGameRewardsSubscriptions(options?: {
    userId?: string;
    isActive?: boolean;
    tier?: string;
    limit?: number;
    offset?: number;
  }) {
    const params = new URLSearchParams();
    if (options?.userId) params.set('userId', options.userId);
    if (options?.isActive !== undefined) params.set('isActive', String(options.isActive));
    if (options?.tier) params.set('tier', options.tier);
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset) params.set('offset', String(options.offset));
    const qs = params.toString();
    return this.request<GrAdminSubscriptionListResult>(
      'GET',
      `/arches/game-rewards/admin/subscriptions${qs ? `?${qs}` : ''}`,
    );
  }

  async adminSetGameRewardsSubscription(input: {
    username: string;
    tier: string;
    durationDays: number;
  }) {
    return this.request<GrSubscriptionResponse>(
      'POST',
      '/arches/game-rewards/admin/subscriptions',
      input,
    );
  }

  async adminRemoveGameRewardsSubscription(subscriptionId: string) {
    return this.request<{ success: boolean }>(
      'DELETE',
      `/arches/game-rewards/admin/subscriptions/${subscriptionId}`,
    );
  }

  // Admin - Reward Log
  async listGameRewardsLog(options?: {
    /** Player-targeting filter — UIs send `username`; server resolves to userId. */
    username?: string;
    userId?: string;
    gameConfigId?: string;
    sinceHours?: number;
    limit?: number;
    offset?: number;
  }) {
    const params = new URLSearchParams();
    if (options?.username) params.set('username', options.username);
    if (options?.userId) params.set('userId', options.userId);
    if (options?.gameConfigId) params.set('gameConfigId', options.gameConfigId);
    if (options?.sinceHours) params.set('sinceHours', String(options.sinceHours));
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset) params.set('offset', String(options.offset));
    const qs = params.toString();
    return this.request<GrRewardLogListResult>(
      'GET',
      `/arches/game-rewards/admin/reward-log${qs ? `?${qs}` : ''}`,
    );
  }

  // User - Time Played
  async getGameRewardsTimePlayed() {
    return this.request<GrTimePlayedAggregates>('GET', '/arches/game-rewards/time-played');
  }

  // User - Leaderboard
  async getGameRewardsLeaderboard(
    gameConfigId: string,
    gameEvent: string,
    options?: { limit?: number; offset?: number },
  ) {
    const params = new URLSearchParams();
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset) params.set('offset', String(options.offset));
    const qs = params.toString();
    return this.request<GrLeaderboardResult>(
      'GET',
      `/arches/game-rewards/leaderboard/${gameConfigId}/${gameEvent}${qs ? `?${qs}` : ''}`,
    );
  }

  // User - My Scores
  async getGameRewardsMyScores(options?: {
    gameConfigId?: string;
    limit?: number;
    offset?: number;
  }) {
    const params = new URLSearchParams();
    if (options?.gameConfigId) params.set('gameConfigId', options.gameConfigId);
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset) params.set('offset', String(options.offset));
    const qs = params.toString();
    return this.request<GrUserBestScoresResult>(
      'GET',
      `/arches/game-rewards/my-scores${qs ? `?${qs}` : ''}`,
    );
  }

  // Public - Leaderboard Games
  async getGameRewardsLeaderboardGames() {
    return this.request<{ games: GrLeaderboardGameInfo[] }>(
      'GET',
      '/arches/game-rewards/leaderboard/games',
    );
  }

  // ==========================================================================
  // DFE Game State API
  // ==========================================================================

  async getGameState(keyword: string) {
    return this.request<{ gameState: GameStateResponse }>(
      'GET',
      `/arches/dfe-game-state/state/${encodeURIComponent(keyword)}`,
    );
  }

  async setGameState(
    keyword: string,
    body: {
      dataType: string;
      data?: string;
      dataBinary?: string;
      version?: number | null;
      checksum?: string;
      metadata?: Record<string, unknown>;
    },
  ) {
    return this.request<{ gameState: GameStateResponse }>(
      'PUT',
      `/arches/dfe-game-state/state/${encodeURIComponent(keyword)}`,
      body,
    );
  }

  async deleteGameState(keyword: string, version: number) {
    return this.request<void>(
      'DELETE',
      `/arches/dfe-game-state/state/${encodeURIComponent(keyword)}?version=${version}`,
    );
  }

  async listGameStates(options?: { limit?: number; offset?: number; keyword?: string }) {
    const params = new URLSearchParams();
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset != null) params.set('offset', String(options.offset));
    if (options?.keyword) params.set('keyword', options.keyword);
    const qs = params.toString();
    return this.request<GameStateListResult>(
      'GET',
      `/arches/dfe-game-state/states${qs ? `?${qs}` : ''}`,
    );
  }

  // ==========================================================================
  // Admin User API
  // ==========================================================================

  async adminLookupUser(username: string) {
    return this.request<AdminUserLookupResult>(
      'GET',
      `/admin/users/lookup?username=${encodeURIComponent(username)}`,
    );
  }

  async adminLookupUserByEmail(email: string) {
    return this.request<AdminUserLookupResult>(
      'GET',
      `/admin/users/lookup?email=${encodeURIComponent(email)}`,
    );
  }

  // ==========================================================================
  // Admin Arche Icons API
  // ==========================================================================

  async adminGetArcheIconEnvironments() {
    return this.request<{
      environments: string[];
      active: { environment: string; bucket: string; cdnUrl: string } | null;
    }>('GET', '/admin/arches/icon/environments');
  }

  async adminGetArcheIconUploadUrls(archId: string, mimeType: string, sizeBytes: number) {
    return this.request<{
      uploadUrl: string;
      stagingKey: string;
      mimeType: string;
      environment: string;
      expiresAt: string;
    }>('POST', `/admin/arches/${encodeURIComponent(archId)}/icon/upload-urls`, {
      mimeType,
      sizeBytes,
    });
  }

  async adminConfirmArcheIcon(archId: string, stagingKey: string) {
    return this.request<{
      arche: { archId: string; iconKey: string; imageRef: string };
      environment: string;
      iconKey: string;
      imageRef: string;
      variantSizes: number[];
    }>('PUT', `/admin/arches/${encodeURIComponent(archId)}/icon/confirm`, { stagingKey });
  }

  async adminDeleteArcheIcon(archId: string) {
    return this.request<void>('DELETE', `/admin/arches/${encodeURIComponent(archId)}/icon`);
  }

  // ==========================================================================
  // Admin Arche Management API
  // ==========================================================================

  async adminGetFeaturedArches() {
    return this.request<{ featured: { archId: string; displayOrder: number }[] }>(
      'GET',
      '/admin/arche-management/featured',
    );
  }

  async adminSetFeaturedArches(archIds: string[]) {
    return this.request<{ featured: { archId: string; displayOrder: number }[] }>(
      'PUT',
      '/admin/arche-management/featured',
      { archIds },
    );
  }

  async adminGetSuggestedArches(archId: string) {
    return this.request<{ archId: string; suggestedArches: string[] }>(
      'GET',
      `/admin/arche-management/${encodeURIComponent(archId)}/suggested`,
    );
  }

  async adminSetSuggestedArches(archId: string, suggestedArches: string[]) {
    return this.request<{ archId: string; suggestedArches: string[] }>(
      'PUT',
      `/admin/arche-management/${encodeURIComponent(archId)}/suggested`,
      { suggestedArches },
    );
  }

  async adminGetArchesSummary(opts?: {
    limit?: number;
    lifecycle?: 'all' | ArcheLifecycleState;
    search?: string;
  }) {
    const params = new URLSearchParams();
    if (opts?.limit) params.set('limit', String(opts.limit));
    if (opts?.lifecycle) params.set('lifecycle', opts.lifecycle);
    if (opts?.search) params.set('search', opts.search);
    const qs = params.toString();
    return this.request<{ arches: ArcheManagementSummary[] }>(
      'GET',
      `/admin/arche-management/arches-summary${qs ? `?${qs}` : ''}`,
    );
  }

  async adminDisableArche(archId: string, reason?: string) {
    return this.request<{ archId: string; lifecycleState: ArcheLifecycleState; isListed: boolean }>(
      'POST',
      `/admin/arche-management/${encodeURIComponent(archId)}/disable`,
      reason ? { reason } : {},
    );
  }

  async adminEnableArche(archId: string) {
    return this.request<{ archId: string; lifecycleState: ArcheLifecycleState; isListed: boolean }>(
      'POST',
      `/admin/arche-management/${encodeURIComponent(archId)}/enable`,
    );
  }

  async adminDeleteArche(archId: string, reason?: string) {
    return this.request<{
      archId: string;
      hardDeleted: boolean;
      lifecycleState?: ArcheLifecycleState;
      isListed?: boolean;
    }>(
      'POST',
      `/admin/arche-management/${encodeURIComponent(archId)}/delete`,
      reason ? { reason } : {},
    );
  }

  async adminRestoreArche(archId: string) {
    return this.request<{ archId: string; lifecycleState: ArcheLifecycleState; isListed: boolean }>(
      'POST',
      `/admin/arche-management/${encodeURIComponent(archId)}/restore`,
    );
  }

  async adminUnlistArche(archId: string, reason?: string) {
    return this.request<{ archId: string; lifecycleState: ArcheLifecycleState; isListed: boolean }>(
      'POST',
      `/admin/arche-management/${encodeURIComponent(archId)}/unlist`,
      reason ? { reason } : {},
    );
  }

  async adminListArche(archId: string) {
    return this.request<{ archId: string; lifecycleState: ArcheLifecycleState; isListed: boolean }>(
      'POST',
      `/admin/arche-management/${encodeURIComponent(archId)}/list`,
    );
  }

  // ==========================================================================
  // Admin Invite Codes API
  // ==========================================================================

  async adminGetInviteCodeEnvironments() {
    return this.request<{ environments: string[] }>('GET', '/admin/invite-codes/environments');
  }

  async adminCreateInviteCodes(options: {
    count?: number;
    expiresAt?: string;
    grantRoles?: string[];
    metadata?: { description?: string; notes?: string };
    environment?: string;
  }) {
    return this.request<{
      inviteCode?: InviteCodeResponse;
      inviteCodes?: InviteCodeResponse[];
      count?: number;
    }>('POST', '/admin/invite-codes', options);
  }

  async adminListInviteCodes(options?: { status?: string; limit?: number; environment?: string }) {
    const params = new URLSearchParams();
    if (options?.status) params.set('status', options.status);
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.environment) params.set('environment', options.environment);
    const qs = params.toString();
    return this.request<{ inviteCodes: InviteCodeResponse[]; count: number }>(
      'GET',
      `/admin/invite-codes${qs ? `?${qs}` : ''}`,
    );
  }

  async adminRevokeInviteCode(code: string, environment?: string) {
    const params = new URLSearchParams();
    if (environment) params.set('environment', environment);
    const qs = params.toString();
    return this.request<{ message: string; code: string }>(
      'DELETE',
      `/admin/invite-codes/${encodeURIComponent(code)}${qs ? `?${qs}` : ''}`,
    );
  }

  // ==========================================================================
  // DFE Game State Admin API
  //
  // Player-targeting paths use the canonical /by-username/:username shape per
  // the platform "username in UI, resolve userId server-side" convention.
  // ==========================================================================

  async adminListGameStates(
    username: string,
    options?: { limit?: number; offset?: number; keyword?: string },
  ) {
    const params = new URLSearchParams();
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset != null) params.set('offset', String(options.offset));
    if (options?.keyword) params.set('keyword', options.keyword);
    const qs = params.toString();
    return this.request<GameStateListResult & { username: string }>(
      'GET',
      `/arches/dfe-game-state/admin/users/by-username/${encodeURIComponent(username)}/states${qs ? `?${qs}` : ''}`,
    );
  }

  async adminGetGameState(username: string, keyword: string) {
    return this.request<{ gameState: GameStateResponse }>(
      'GET',
      `/arches/dfe-game-state/admin/users/by-username/${encodeURIComponent(username)}/state/${encodeURIComponent(keyword)}`,
    );
  }

  async adminSetGameState(
    username: string,
    keyword: string,
    body: {
      dataType: string;
      data?: string;
      dataBinary?: string;
      checksum?: string;
      metadata?: Record<string, unknown>;
    },
  ) {
    return this.request<{ gameState: GameStateResponse }>(
      'PUT',
      `/arches/dfe-game-state/admin/users/by-username/${encodeURIComponent(username)}/state/${encodeURIComponent(keyword)}`,
      body,
    );
  }

  async adminDeleteGameState(username: string, keyword: string) {
    return this.request<void>(
      'DELETE',
      `/arches/dfe-game-state/admin/users/by-username/${encodeURIComponent(username)}/state/${encodeURIComponent(keyword)}`,
    );
  }

  async adminListGameStateAuditLog(options?: {
    username?: string;
    keyword?: string;
    limit?: number;
    offset?: number;
  }) {
    const params = new URLSearchParams();
    if (options?.username) params.set('username', options.username);
    if (options?.keyword) params.set('keyword', options.keyword);
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset) params.set('offset', String(options.offset));
    const qs = params.toString();
    return this.request<GameStateAuditLogResult>(
      'GET',
      `/arches/dfe-game-state/admin/audit${qs ? `?${qs}` : ''}`,
    );
  }

  // --------------------------------------------------------------------------
  // DFE Game State — Plugin Resource Grants (admin)
  // --------------------------------------------------------------------------

  async adminListGameStateGrants(options?: {
    archeId?: string;
    resourceType?: GsResourceType;
    reviewStatus?: GsGrantReviewStatus;
    limit?: number;
    offset?: number;
  }) {
    const params = new URLSearchParams();
    if (options?.archeId) params.set('archeId', options.archeId);
    if (options?.resourceType) params.set('resourceType', options.resourceType);
    if (options?.reviewStatus) params.set('reviewStatus', options.reviewStatus);
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset != null) params.set('offset', String(options.offset));
    const qs = params.toString();
    return this.request<GameStateGrantListResult>(
      'GET',
      `/arches/dfe-game-state/admin/grants${qs ? `?${qs}` : ''}`,
    );
  }

  async adminGetGameStateGrant(
    archeId: string,
    resourceType: GsResourceType,
    resourceName: string,
  ) {
    return this.request<{ grant: GameStateGrant }>(
      'GET',
      `/arches/dfe-game-state/admin/grants/${encodeURIComponent(archeId)}/${encodeURIComponent(resourceType)}/${encodeURIComponent(resourceName)}`,
    );
  }

  async adminUpsertGameStateGrant(
    archeId: string,
    resourceType: GsResourceType,
    resourceName: string,
    access: GsAccessLevel,
  ) {
    return this.request<SetGameStateGrantResult>(
      'PUT',
      `/arches/dfe-game-state/admin/grants/${encodeURIComponent(archeId)}/${encodeURIComponent(resourceType)}/${encodeURIComponent(resourceName)}`,
      { access },
    );
  }

  async adminDeleteGameStateGrant(
    archeId: string,
    resourceType: GsResourceType,
    resourceName: string,
  ) {
    return this.request<void>(
      'DELETE',
      `/arches/dfe-game-state/admin/grants/${encodeURIComponent(archeId)}/${encodeURIComponent(resourceType)}/${encodeURIComponent(resourceName)}`,
    );
  }

  async adminApproveGameStateGrant(
    archeId: string,
    resourceType: GsResourceType,
    resourceName: string,
  ) {
    return this.request<ApproveRejectGameStateGrantResult>(
      'POST',
      `/arches/dfe-game-state/admin/grants/${encodeURIComponent(archeId)}/${encodeURIComponent(resourceType)}/${encodeURIComponent(resourceName)}/approve`,
    );
  }

  async adminRejectGameStateGrant(
    archeId: string,
    resourceType: GsResourceType,
    resourceName: string,
    reason?: string,
  ) {
    return this.request<ApproveRejectGameStateGrantResult>(
      'POST',
      `/arches/dfe-game-state/admin/grants/${encodeURIComponent(archeId)}/${encodeURIComponent(resourceType)}/${encodeURIComponent(resourceName)}/reject`,
      reason ? { reason } : {},
    );
  }

  // ==========================================================================
  // Polls API
  // ==========================================================================

  async createPoll(input: CreatePollPayload) {
    return this.request<PollDetailResponse>('POST', '/arches/polls', input);
  }

  async listMyPolls(options?: { status?: string; limit?: number; offset?: number }) {
    const params = new URLSearchParams();
    if (options?.status) params.set('status', options.status);
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.offset) params.set('offset', options.offset.toString());
    const qs = params.toString();
    return this.request<PollListResult>('GET', `/arches/polls${qs ? `?${qs}` : ''}`);
  }

  async getPoll(pollId: string) {
    return this.request<PollFullDetailResponse>('GET', `/arches/polls/${pollId}`);
  }

  async updatePoll(pollId: string, input: UpdatePollPayload) {
    return this.request<PollDetailResponse>('PUT', `/arches/polls/${pollId}`, input);
  }

  async deletePoll(pollId: string) {
    return this.request<void>('DELETE', `/arches/polls/${pollId}`);
  }

  async publishPoll(pollId: string) {
    return this.request<{ pollId: string; status: string; updatedAt: string }>(
      'POST',
      `/arches/polls/${pollId}/publish`,
    );
  }

  async closePoll(pollId: string) {
    return this.request<{ pollId: string; status: string; updatedAt: string }>(
      'POST',
      `/arches/polls/${pollId}/close`,
    );
  }

  async submitPollResponse(pollId: string, input: { answers: PollAnswerPayload[] }) {
    return this.request<{ responseId: string; pollId: string; submittedAt: string }>(
      'POST',
      `/arches/polls/${pollId}/respond`,
      input,
    );
  }

  async getMyPollResponse(pollId: string) {
    return this.request<PollResponseResult>('GET', `/arches/polls/${pollId}/my-response`);
  }

  async getPollResults(pollId: string) {
    return this.request<PollResultsResponse>('GET', `/arches/polls/${pollId}/results`);
  }

  async viewPoll(pollId: string) {
    return this.request<PollPublicViewResponse>('GET', `/arches/polls/${pollId}/view`);
  }

  // ==========================================================================
  // Plugin Submission API
  // ==========================================================================

  async getPluginUploadUrl(body: { fileName: string; sizeBytes: number; checksumSha256?: string }) {
    return this.request<{
      uploadUrl: string;
      submissionId: string;
      s3Key: string;
      expiresAt: string;
    }>('POST', '/plugins/submissions/upload-url', body);
  }

  async createPluginSubmission(body: {
    submissionId: string;
    sourceRef: string;
    manifest: Record<string, unknown>;
    archId?: string;
  }) {
    return this.request<{
      submissionId: string;
      archId: string;
      reviewId: string;
      status: string;
      createdAt: string;
    }>('POST', '/plugins/submissions', body);
  }

  async listPluginSubmissions(options?: { status?: string; limit?: number; offset?: number }) {
    const params = new URLSearchParams();
    if (options?.status) params.set('status', options.status);
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.offset) params.set('offset', options.offset.toString());
    const query = params.toString();
    return this.request<PluginSubmissionListResult>(
      'GET',
      `/plugins/submissions${query ? `?${query}` : ''}`,
    );
  }

  async getPluginSubmission(submissionId: string) {
    return this.request<PluginSubmissionDetailResult>(
      'GET',
      `/plugins/submissions/${submissionId}`,
    );
  }

  async deletePluginSubmission(submissionId: string) {
    return this.request<{ success: boolean; submissionId: string }>(
      'DELETE',
      `/plugins/submissions/${submissionId}`,
    );
  }

  // ========================================================================
  // Plugin Admin API
  // ========================================================================

  async listEscalatedPluginReviews(options?: { limit?: number; offset?: number }) {
    const params = new URLSearchParams();
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset) params.set('offset', String(options.offset));
    const qs = params.toString();
    return this.request<{
      reviews: Array<{
        reviewId: string;
        submissionId: string;
        archId: string;
        manifestName: string;
        riskScore: number | null;
        criticalCount: number;
        highCount: number;
        filesAnalyzed: number;
        createdAt: string;
      }>;
      total: number;
      limit: number;
      offset: number;
    }>('GET', `/admin/plugins/reviews/escalated${qs ? `?${qs}` : ''}`);
  }

  async getPluginReviewDetail(reviewId: string) {
    return this.request<{
      review: {
        reviewId: string;
        submissionId: string;
        reviewerType: string;
        status: string;
        riskScore: number | null;
        codeFindings: unknown[] | null;
        dependencyFindings: unknown[] | null;
        policyFindings: unknown[] | null;
        qualityFindings: unknown[] | null;
        criticalCount: number;
        highCount: number;
        filesAnalyzed: number;
        dependenciesAnalyzed: number;
        reviewedBy: string | null;
        humanNotes: string | null;
        createdAt: string;
        completedAt: string | null;
      };
      submission: {
        submissionId: string;
        archId: string;
        manifestName: string;
        manifestVersion: string;
        status: string;
        sourceRef: string;
        createdAt: string;
      } | null;
    }>('GET', `/admin/plugins/reviews/${reviewId}`);
  }

  async approvePluginReview(reviewId: string, notes?: string) {
    return this.request<{ success: boolean; reviewId: string; status: string }>(
      'POST',
      `/admin/plugins/reviews/${reviewId}/approve`,
      { notes },
    );
  }

  async rejectPluginReview(reviewId: string, notes: string) {
    return this.request<{ success: boolean; reviewId: string; status: string }>(
      'POST',
      `/admin/plugins/reviews/${reviewId}/reject`,
      { notes },
    );
  }

  // ==========================================================================
  // Contributor Asset Library API
  // ==========================================================================

  async getArcheAssetLibraryConfig(archeId: string) {
    return this.request<{
      config: {
        enabled: boolean;
        suspendedAt: string | null;
        suspendReason: string | null;
        effectiveCaps: {
          maxAssets: number;
          maxStorageBytes: number;
          maxUploadBytes: number;
          maxDecodedPixels: number;
        };
        usage: { activeStorageBytes: number; activeAssetCount: number };
      };
    }>('GET', `/contributor/arches/${archeId}/asset-library/config`);
  }

  async enableArcheAssetLibrary(archeId: string) {
    return this.request<{ config: ContributorAssetLibraryConfig }>(
      'POST',
      `/contributor/arches/${archeId}/asset-library/enable`,
    );
  }

  async disableArcheAssetLibrary(archeId: string) {
    return this.request<{ config: ContributorAssetLibraryConfig }>(
      'POST',
      `/contributor/arches/${archeId}/asset-library/disable`,
    );
  }

  async getArcheAssetBillingSummary(archeId: string) {
    return this.request<{
      summary: {
        yesterday: {
          storageChargeCredits: number;
          egressChargeCredits: number;
          storageByteDays: number;
          egressBytes: number;
          chargeStatus: string;
        } | null;
        monthToDate: {
          storageChargeCredits: number;
          egressChargeCredits: number;
          daysCharged: number;
          daysFailed: number;
        };
      };
    }>('GET', `/contributor/arches/${archeId}/billing/summary`);
  }

  async listArcheAssets(
    archeId: string,
    opts?: { status?: string; tag?: string; cursor?: string; limit?: number },
  ) {
    const params = new URLSearchParams();
    if (opts?.status) params.set('status', opts.status);
    if (opts?.tag) params.set('tag', opts.tag);
    if (opts?.cursor) params.set('cursor', opts.cursor);
    if (opts?.limit) params.set('limit', String(opts.limit));
    const qs = params.toString();
    return this.request<{ assets: ContributorArcheAsset[]; nextCursor: string | null }>(
      'GET',
      `/contributor/arches/${archeId}/assets${qs ? `?${qs}` : ''}`,
    );
  }

  async initArcheAssetUpload(
    archeId: string,
    body: {
      contentType: 'image/png' | 'image/jpeg' | 'image/webp';
      sizeBytes: number;
      name?: string;
      altText?: string;
      tags?: string[];
    },
  ) {
    return this.request<{
      assetId: string;
      uploadUrl: string;
      expiresAt: string;
      storageKey: string;
    }>('POST', `/contributor/arches/${archeId}/assets/upload-init`, body);
  }

  async finalizeArcheAsset(
    archeId: string,
    assetId: string,
    body: { name?: string | null; altText?: string | null; tags?: string[] },
  ) {
    return this.request<{ asset: ContributorArcheAsset }>(
      'POST',
      `/contributor/arches/${archeId}/assets/${assetId}/finalize`,
      body,
    );
  }

  async patchArcheAsset(
    archeId: string,
    assetId: string,
    body: { name?: string | null; altText?: string | null; tags?: string[] },
  ) {
    return this.request<{ asset: ContributorArcheAsset }>(
      'PATCH',
      `/contributor/arches/${archeId}/assets/${assetId}`,
      body,
    );
  }

  async softDeleteArcheAsset(archeId: string, assetId: string) {
    return this.request<{ ok: true }>(
      'POST',
      `/contributor/arches/${archeId}/assets/${assetId}/soft-delete`,
    );
  }

  /**
   * Build a contributor-only preview URL for an active asset.
   * Used by the portal grid to render thumbnails for assets that
   * aren't published yet. Auth via session cookie (caller must own
   * the arche).
   */
  buildArcheAssetPreviewUrl(archeId: string, assetId: string): string {
    return `${API_BASE}/contributor/arches/${archeId}/assets/${assetId}/preview`;
  }

  // ==========================================================================
  // OAuth Consent API
  // ==========================================================================

  async getOAuthAuthorizeInfo(params: Record<string, string>) {
    const qs = new URLSearchParams(params).toString();
    return this.request<OAuthAuthorizeInfo>('GET', `/oauth/authorize?${qs}`);
  }

  async submitOAuthConsent(params: {
    clientId: string;
    redirectUri: string;
    responseType: string;
    scope: string;
    state: string;
    codeChallenge: string;
    codeChallengeMethod: string;
    consent: 'granted' | 'denied';
  }) {
    return this.request<{ redirectUrl: string }>('POST', '/oauth/authorize', params);
  }

  // ==========================================================================
  // Connected Apps API
  // ==========================================================================

  async getConnectedApps() {
    return this.request<{ apps: ConnectedApp[] }>('GET', '/settings/connected-apps');
  }

  async revokeConnectedApp(appId: string) {
    return this.request<void>('DELETE', `/settings/connected-apps/${appId}`);
  }

  // ==========================================================================
  // Crafting API
  // ==========================================================================

  async createCraftingRecipe(input: Record<string, unknown>) {
    return this.request<{ recipe: { recipeId: string } }>(
      'POST',
      '/arches/crafting/admin/recipes',
      input,
    );
  }

  async updateCraftingRecipe(recipeId: string, input: Record<string, unknown>) {
    return this.request<{ recipe: { recipeId: string } }>(
      'PATCH',
      `/arches/crafting/admin/recipes/${recipeId}`,
      input,
    );
  }

  // ==========================================================================
  // Mining API
  // ==========================================================================

  async getMiningLands() {
    return this.request<{
      lands: Array<{
        landId: string;
        userId: string;
        landType: string;
        xSize: number;
        ySize: number;
        zSize: number;
        generation: number;
        paidReset: boolean;
        createdAt: string;
        updatedAt: string;
      }>;
    }>('GET', '/arches/mining/lands');
  }

  async getMiningLand(landType: string) {
    return this.request<{
      landType: string;
      dimensions: { x: number; y: number; z: number };
      blocks: Record<string, string>;
      generation: number;
      paidReset: boolean;
    }>('GET', `/arches/mining/lands/${landType}`);
  }

  async getMiningLog(options?: { limit?: number; offset?: number }) {
    const params: Record<string, string> = {};
    if (options?.limit) params.limit = String(options.limit);
    if (options?.offset) params.offset = String(options.offset);

    const queryString = Object.keys(params).length
      ? '?' + new URLSearchParams(params).toString()
      : '';

    return this.request<{
      entries: Array<{
        logId: string;
        userId: string;
        landId: string;
        landType: string;
        blocksMined: number;
        itemsMinted: Array<{ itemName: string; quantity: number }>;
        createdAt: string;
      }>;
      total: number;
    }>('GET', `/arches/mining/log${queryString}`);
  }

  // ==========================================================================
  // Liquidity Caches
  // ==========================================================================

  async listLiquidityCachePools(options?: { limit?: number; offset?: number }) {
    const params = new URLSearchParams();
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset) params.set('offset', String(options.offset));
    const qs = params.toString();
    return this.request<{
      pools: LcPoolResponse[];
      total: number;
      limit: number;
      offset: number;
    }>('GET', `/arches/liquidity-caches${qs ? `?${qs}` : ''}`);
  }

  async getLiquidityCachePool(poolId: string) {
    return this.request<LcPoolResponse>('GET', `/arches/liquidity-caches/${poolId}`);
  }

  async getLiquidityCacheQuote(
    poolId: string,
    direction: string,
    inputAmount: string,
    niceMode?: boolean,
  ) {
    const params = new URLSearchParams();
    params.set('direction', direction);
    params.set('inputAmount', inputAmount);
    if (niceMode) params.set('niceMode', 'true');
    return this.request<{
      amountOut: string;
      feeAmount: string;
      priceImpactBps: number;
      niceMode?: { adjustedInput: string; cleanOutput: string; roundingSavings: string };
    }>('GET', `/arches/liquidity-caches/${poolId}/quote?${params.toString()}`);
  }

  async liquidityCacheInvest(
    poolId: string,
    input: { tokenAAmount: string; tokenBAmount: string },
  ) {
    return this.request<{
      position: { positionId: string; poolId: string; lpShares: string };
      transaction: LcTransactionResponse;
    }>('POST', `/arches/liquidity-caches/${poolId}/invest`, input);
  }

  async liquidityCacheWithdraw(poolId: string, input: { lpShares: string }) {
    return this.request<{
      amountA: string;
      amountB: string;
      transaction: LcTransactionResponse;
    }>('POST', `/arches/liquidity-caches/${poolId}/withdraw`, input);
  }

  async liquidityCacheTrade(
    poolId: string,
    input: {
      direction: string;
      inputAmount: string;
      minOutputAmount?: string;
      deadline?: string;
      niceMode?: boolean;
    },
  ) {
    return this.request<{
      amountOut: string;
      transaction: LcTransactionResponse;
    }>('POST', `/arches/liquidity-caches/${poolId}/trade`, input);
  }

  async getMyLiquidityCachePositions() {
    return this.request<{
      positions: Array<{
        positionId: string;
        poolId: string;
        lpShares: string;
        createdAt: string;
        updatedAt: string;
        pool?: {
          poolId: string;
          name: string;
          tokenASymbol: string;
          tokenBSymbol: string;
          reserveA: string;
          reserveB: string;
          totalLpShares: string;
          feeBps: number;
          status: string;
        };
      }>;
      total: number;
    }>('GET', '/arches/liquidity-caches/me/positions');
  }

  async getMyLiquidityCacheTransactions(options?: {
    type?: string;
    limit?: number;
    offset?: number;
  }) {
    const params = new URLSearchParams();
    if (options?.type) params.set('type', options.type);
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset) params.set('offset', String(options.offset));
    const qs = params.toString();
    return this.request<{
      transactions: LcTransactionResponse[];
      total: number;
      limit: number;
      offset: number;
    }>('GET', `/arches/liquidity-caches/me/transactions${qs ? `?${qs}` : ''}`);
  }

  async getLiquidityCachePosition(poolId: string) {
    return this.request<{
      position: {
        positionId: string;
        poolId: string;
        lpShares: string;
        createdAt: string;
        updatedAt: string;
      } | null;
    }>('GET', `/arches/liquidity-caches/${poolId}/position`);
  }

  async createLiquidityCachePool(input: {
    name: string;
    description?: string;
    tokenA: { itemDefId?: string; symbol: string; name: string; decimals: number; source: string };
    tokenB: { itemDefId?: string; symbol: string; name: string; decimals: number; source: string };
    feeBps?: number;
    minLiquidity?: number;
    maxTradePct?: number;
  }) {
    return this.request<LcPoolResponse>('POST', '/arches/liquidity-caches/admin', input);
  }

  async updateLiquidityCachePool(
    poolId: string,
    input: {
      name?: string;
      description?: string;
      feeBps?: number;
      maxTradePct?: number;
      status?: string;
    },
  ) {
    return this.request<LcPoolResponse>('PATCH', `/arches/liquidity-caches/admin/${poolId}`, input);
  }

  // ==========================================================================
  // Fiardle Methods
  // ==========================================================================

  async createFiardleGame() {
    return this.request<{ gameId: string }>('POST', '/arches/fiardle/games');
  }

  async getFiardleActiveGame() {
    return this.request<{ game: FiardleGameResponse }>('GET', '/arches/fiardle/games/active');
  }

  async getFiardleGame(gameId: string) {
    return this.request<{ game: FiardleGameResponse }>('GET', `/arches/fiardle/games/${gameId}`);
  }

  async submitFiardleGuess(gameId: string, word: string) {
    return this.request<FiardleGuessResponse>('POST', `/arches/fiardle/games/${gameId}/guesses`, {
      word,
    });
  }

  async getFiardleGameHistory(options?: { limit?: number; offset?: number }) {
    const params = new URLSearchParams();
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset) params.set('offset', String(options.offset));
    const qs = params.toString();
    return this.request<FiardleGameHistoryResponse>(
      'GET',
      `/arches/fiardle/games${qs ? `?${qs}` : ''}`,
    );
  }

  async getFiardleStats(userId?: string) {
    const path = userId ? `/arches/fiardle/stats/${userId}` : '/arches/fiardle/stats';
    return this.request<FiardleStatsResponse>('GET', path);
  }

  async getFiardleLeaderboard(options?: {
    sortBy?: 'wins' | 'winRate' | 'streak';
    limit?: number;
    offset?: number;
  }) {
    const params = new URLSearchParams();
    if (options?.sortBy) params.set('sortBy', options.sortBy);
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset) params.set('offset', String(options.offset));
    const qs = params.toString();
    return this.request<FiardleLeaderboardResponse>(
      'GET',
      `/arches/fiardle/leaderboard${qs ? `?${qs}` : ''}`,
    );
  }

  // ==========================================================================
  // Business Methods
  // ==========================================================================

  async listBusinesses() {
    return this.request<{ businesses: BusinessResponse[] }>('GET', '/arches/businesses');
  }

  async getBusiness(businessId: string) {
    return this.request<BusinessResponse>('GET', `/arches/businesses/${businessId}`);
  }

  async createBusiness(input: { businessType: string; name: string }) {
    return this.request<BusinessResponse>('POST', '/arches/businesses', input);
  }

  async dissolveBusiness(businessId: string) {
    return this.request<void>('DELETE', `/arches/businesses/${businessId}`);
  }

  async collectBusinessRevenue(
    businessId: string,
    input: { goods: Record<string, number>; foods: Record<string, number> },
  ) {
    return this.request<CollectRevenueResponse>(
      'POST',
      `/arches/businesses/${businessId}/collect`,
      input,
    );
  }

  async getBusinessParameters(type?: string) {
    const params = new URLSearchParams();
    if (type) params.set('type', type);
    const qs = params.toString();
    return this.request<{ parameters: BusinessParameterResponse[] }>(
      'GET',
      `/arches/businesses/parameters${qs ? `?${qs}` : ''}`,
    );
  }

  async getBusinessHistory(businessId: string, options?: { limit?: number; offset?: number }) {
    const params = new URLSearchParams();
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset) params.set('offset', String(options.offset));
    const qs = params.toString();
    return this.request<BusinessHistoryResponse>(
      'GET',
      `/arches/businesses/${businessId}/history${qs ? `?${qs}` : ''}`,
    );
  }

  async getBusinessStats() {
    return this.request<BusinessStatsResponse>('GET', '/arches/businesses/admin/stats');
  }

  // ==========================================================================
  // API Keys
  // ==========================================================================

  async createApiKey(params: { name: string; scopes?: string[]; expiresAt?: string }) {
    return this.request<{
      keyId: string;
      name: string;
      keyPrefix: string;
      secret: string;
      scopes: string[];
      createdAt: string;
    }>('POST', '/api-keys', params);
  }

  /**
   * Claim a CLI auth code on behalf of the logged-in user, authorizing the
   * paste-code login flow used by `fias-dev login --manual` (Codespaces-
   * friendly). The CLI's poll endpoint returns the freshly-minted API key
   * once this resolves successfully. Throws ApiError on 404/409/410.
   */
  async claimCliAuthCode(code: string, scopes?: string[]) {
    return this.request<void>(
      'POST',
      `/cli-auth/code/${encodeURIComponent(code)}/claim`,
      scopes ? { scopes } : {},
    );
  }

  async listApiKeys() {
    return this.request<{
      keys: Array<{
        keyId: string;
        name: string;
        keyPrefix: string;
        scopes: string[];
        status: string;
        createdAt: string;
        lastUsedAt: string | null;
        expiresAt: string | null;
      }>;
    }>('GET', '/api-keys');
  }

  // ===========================================================================
  // Beta Access
  // ===========================================================================

  async submitBetaAccessRequest(input: BetaAccessSubmitInput) {
    return this.request<BetaAccessSubmitResponse>('POST', '/arches/beta-access/requests', input);
  }

  async listBetaAccessRequests(options?: {
    status?: string;
    search?: string;
    limit?: number;
    offset?: number;
  }) {
    const params = new URLSearchParams();
    if (options?.status) params.set('status', options.status);
    if (options?.search) params.set('search', options.search);
    if (options?.limit != null) params.set('limit', String(options.limit));
    if (options?.offset != null) params.set('offset', String(options.offset));
    const qs = params.toString();
    return this.request<BetaAccessListResponse>(
      'GET',
      `/arches/beta-access/admin/requests${qs ? `?${qs}` : ''}`,
    );
  }

  async getBetaAccessRequest(requestId: string) {
    return this.request<BetaAccessRequestResponse>(
      'GET',
      `/arches/beta-access/admin/requests/${requestId}`,
    );
  }

  async updateBetaAccessRequestStatus(
    requestId: string,
    input: { status: 'accepted' | 'rejected'; adminNotes?: string },
  ) {
    return this.request<BetaAccessRequestResponse>(
      'PATCH',
      `/arches/beta-access/admin/requests/${requestId}`,
      input,
    );
  }

  async getBetaAccessStats() {
    return this.request<BetaAccessStatsResponse>('GET', '/arches/beta-access/admin/stats');
  }

  // ==========================================================================
  // Learn About Fias
  // ==========================================================================

  async getLearnAboutFiasResources() {
    return this.request<LearnAboutFiasResourcesResponse>(
      'GET',
      '/arches/learn-about-fias/resources',
    );
  }

  async adminListLearnAboutFiasDocuments(options?: { limit?: number; offset?: number }) {
    const params = new URLSearchParams();
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset) params.set('offset', String(options.offset));
    const qs = params.toString();
    return this.request<LearnAboutFiasDocumentListResponse>(
      'GET',
      `/arches/learn-about-fias/admin/documents${qs ? `?${qs}` : ''}`,
    );
  }

  async adminGetLearnAboutFiasDocument(docId: string) {
    return this.request<LearnAboutFiasDocumentDetailResponse>(
      'GET',
      `/arches/learn-about-fias/admin/documents/${docId}`,
    );
  }

  async adminCreateLearnAboutFiasDocument(
    input:
      | {
          mode: 'file';
          title: string;
          description?: string;
          fileBase64: string;
          fileName: string;
          sourceUrl?: string;
          showInResources?: boolean;
          ragEnabled?: boolean;
        }
      | {
          mode: 'text';
          title: string;
          description?: string;
          content: string;
          sourceType: string;
          sourceUrl?: string;
          showInResources?: boolean;
          ragEnabled?: boolean;
        }
      | {
          mode: 'link';
          title: string;
          description?: string;
          sourceUrl: string;
          showInResources?: boolean;
          ragEnabled?: boolean;
        },
  ) {
    return this.request<{ docId: string; chunksCreated: number; status: string }>(
      'POST',
      '/arches/learn-about-fias/admin/documents',
      input,
    );
  }

  async adminUpdateLearnAboutFiasDocument(
    docId: string,
    input: {
      title?: string;
      description?: string | null;
      sourceUrl?: string | null;
      showInResources?: boolean;
      ragEnabled?: boolean;
    },
  ) {
    return this.request<LearnAboutFiasDocumentResponse>(
      'PATCH',
      `/arches/learn-about-fias/admin/documents/${docId}`,
      input,
    );
  }

  async adminUpdateLearnAboutFiasDocumentContent(
    docId: string,
    input:
      | { mode: 'file'; fileBase64: string; fileName: string }
      | { mode: 'text'; content: string; sourceType?: string },
  ) {
    return this.request<{ docId: string; chunksCreated: number; status: string }>(
      'PATCH',
      `/arches/learn-about-fias/admin/documents/${docId}/content`,
      input,
    );
  }

  async adminDeleteLearnAboutFiasDocument(docId: string) {
    return this.request<void>('DELETE', `/arches/learn-about-fias/admin/documents/${docId}`);
  }

  async adminGetLearnAboutFiasStats() {
    return this.request<LearnAboutFiasStatsResponse>('GET', '/arches/learn-about-fias/admin/stats');
  }

  async adminReorderLearnAboutFiasDocuments(docIds: string[]) {
    return this.request<{ documents: LearnAboutFiasDocumentResponse[] }>(
      'PUT',
      '/arches/learn-about-fias/admin/documents/reorder',
      { docIds },
    );
  }

  // ==========================================================================
  // Storage Arches (DFE Avatars, DFE Icons, Litcraft Addressables)
  // ==========================================================================

  /**
   * Creates a StorageApi bound to a specific storage arche path.
   */
  private createStorageApi(archePath: string): StorageApi {
    return {
      listFiles: (
        prefix?: string,
        delimiter?: string,
        maxKeys?: number,
        continuationToken?: string,
      ) => {
        const params = new URLSearchParams();
        if (prefix) params.set('prefix', prefix);
        if (delimiter) params.set('delimiter', delimiter);
        if (maxKeys) params.set('maxKeys', String(maxKeys));
        if (continuationToken) params.set('continuationToken', continuationToken);
        const qs = params.toString();
        return this.request<StorageFileListResponse>(
          'GET',
          `/arches/${archePath}/admin/files${qs ? `?${qs}` : ''}`,
        );
      },
      getUploadUrl: (key: string, contentType: string, sizeBytes: number) => {
        return this.request<StorageUploadUrlResponse>(
          'POST',
          `/arches/${archePath}/admin/upload-url`,
          { key, contentType, sizeBytes },
        );
      },
      deleteFile: (key: string) => {
        return this.request<{ success: boolean; key: string }>(
          'DELETE',
          `/arches/${archePath}/admin/files?key=${encodeURIComponent(key)}`,
        );
      },
      deleteGroup: (prefix: string) => {
        return this.request<{
          success: boolean;
          prefix: string;
          deletedCount: number;
          failedCount?: number;
          failedKeys?: string[];
        }>('DELETE', `/arches/${archePath}/admin/groups?prefix=${encodeURIComponent(prefix)}`);
      },
      getMetadata: (key: string) => {
        return this.request<StorageFileMetadataResponse>(
          'GET',
          `/arches/${archePath}/admin/files/metadata?key=${encodeURIComponent(key)}`,
        );
      },
    };
  }

  readonly dfeAvatarsStorage = this.createStorageApi('dfe-avatars');
  readonly dfeIconsStorage = this.createStorageApi('dfe-icons');
  readonly litcraftAddressablesStorage = this.createStorageApi('litcraft-addressables');

  /**
   * Upload the full-size 512x512 avatar PNG for the given username. The
   * server enforces that the authenticated user owns this username.
   * Backed by `PUT /v1/arches/dfe-avatars/Avatars/{username}.png`.
   */
  async uploadDfeAvatarFullSize(username: string, blob: Blob) {
    return this.requestBinary<{ success: boolean; cdnUrl: string | null }>(
      'PUT',
      `/arches/dfe-avatars/Avatars/${encodeURIComponent(username)}.png`,
      blob,
      'image/png',
    );
  }

  /**
   * Upload the 128x128 chat-thumbnail avatar PNG for the given username.
   * Backed by `PUT /v1/arches/dfe-avatars/ChatAvatars/{username}_mini.png`.
   */
  async uploadDfeAvatarThumbnail(username: string, blob: Blob) {
    return this.requestBinary<{ success: boolean; cdnUrl: string | null }>(
      'PUT',
      `/arches/dfe-avatars/ChatAvatars/${encodeURIComponent(username)}_mini.png`,
      blob,
      'image/png',
    );
  }

  // ========================================================================
  // Todo Lists
  // ========================================================================

  async getMyTodoLists(options?: { limit?: number; offset?: number }) {
    const qs = new URLSearchParams();
    if (options?.limit) qs.set('limit', String(options.limit));
    if (options?.offset) qs.set('offset', String(options.offset));
    const q = qs.toString();
    return this.request<TodoListListResponse>('GET', `/arches/todo-lists${q ? `?${q}` : ''}`);
  }

  async getTodoListDetail(listId: string) {
    return this.request<TodoListDetailResponse>('GET', `/arches/todo-lists/${listId}`);
  }

  async createTodoList(input: { name: string; description?: string }) {
    return this.request<TodoListResponse>('POST', '/arches/todo-lists', input);
  }

  async updateTodoList(
    listId: string,
    input: { name?: string; description?: string; isPublic?: boolean; publicSlug?: string },
  ) {
    return this.request<TodoListResponse>('PATCH', `/arches/todo-lists/${listId}`, input);
  }

  async deleteTodoList(listId: string) {
    return this.request<void>('DELETE', `/arches/todo-lists/${listId}`);
  }

  async createTodoSection(
    listId: string,
    input: {
      name: string;
      description?: string;
      parentSectionId?: string;
      sectionType?: 'timeframe' | 'category';
    },
  ) {
    return this.request<TodoSectionResponse>(
      'POST',
      `/arches/todo-lists/${listId}/sections`,
      input,
    );
  }

  async updateTodoSection(
    listId: string,
    sectionId: string,
    input: {
      name?: string;
      description?: string;
      sortOrder?: number;
      sectionType?: 'timeframe' | 'category';
      parentSectionId?: string | null;
      hiddenFromPublic?: boolean;
    },
  ) {
    return this.request<TodoSectionResponse>(
      'PATCH',
      `/arches/todo-lists/${listId}/sections/${sectionId}`,
      input,
    );
  }

  async deleteTodoSection(listId: string, sectionId: string) {
    return this.request<void>('DELETE', `/arches/todo-lists/${listId}/sections/${sectionId}`);
  }

  async reorderTodoSections(listId: string, sectionIds: string[]) {
    return this.request<void>('PUT', `/arches/todo-lists/${listId}/sections/reorder`, {
      sectionIds,
    });
  }

  async createTodoItem(
    listId: string,
    input: { title: string; description?: string; sectionId?: string },
  ) {
    return this.request<TodoItemResponse>('POST', `/arches/todo-lists/${listId}/items`, input);
  }

  async updateTodoItem(
    listId: string,
    itemId: string,
    input: {
      title?: string;
      description?: string;
      status?: 'pending' | 'in_progress' | 'completed';
      sectionId?: string | null;
      sortOrder?: number;
    },
  ) {
    return this.request<TodoItemResponse>(
      'PATCH',
      `/arches/todo-lists/${listId}/items/${itemId}`,
      input,
    );
  }

  async deleteTodoItem(listId: string, itemId: string) {
    return this.request<void>('DELETE', `/arches/todo-lists/${listId}/items/${itemId}`);
  }

  async reorderTodoItems(listId: string, sectionId: string | null, itemIds: string[]) {
    return this.request<void>('PUT', `/arches/todo-lists/${listId}/items/reorder`, {
      sectionId,
      itemIds,
    });
  }

  async getTodoListCompleted(
    listId: string,
    options?: { limit?: number; offset?: number; completedByUserId?: string },
  ) {
    const qs = new URLSearchParams();
    if (options?.limit) qs.set('limit', String(options.limit));
    if (options?.offset) qs.set('offset', String(options.offset));
    if (options?.completedByUserId) qs.set('completedByUserId', options.completedByUserId);
    const q = qs.toString();
    return this.request<TodoCompletedResponse>(
      'GET',
      `/arches/todo-lists/${listId}/completed${q ? `?${q}` : ''}`,
    );
  }

  async getPublicTodoList(slug: string) {
    return this.request<TodoListDetailResponse>('GET', `/arches/todo-lists/public/${slug}`);
  }

  async getPublicTodoListCompleted(slug: string, options?: { limit?: number; offset?: number }) {
    const qs = new URLSearchParams();
    if (options?.limit) qs.set('limit', String(options.limit));
    if (options?.offset) qs.set('offset', String(options.offset));
    const q = qs.toString();
    return this.request<TodoCompletedResponse>(
      'GET',
      `/arches/todo-lists/public/${slug}/completed${q ? `?${q}` : ''}`,
    );
  }

  // Sharing
  async searchTodoUsers(username: string) {
    return this.request<{ user: { userId: string; username: string } | null }>(
      'GET',
      `/arches/todo-lists/users/search?username=${encodeURIComponent(username)}`,
    );
  }

  async getSharedTodoLists() {
    return this.request<{ lists: SharedTodoListResponse[] }>('GET', '/arches/todo-lists/shared');
  }

  async addTodoCollaborator(listId: string, input: { username: string; role?: string }) {
    return this.request<TodoListCollaboratorResponse>(
      'POST',
      `/arches/todo-lists/${listId}/collaborators`,
      input,
    );
  }

  async updateTodoCollaborator(listId: string, userId: string, input: { role: string }) {
    return this.request<TodoListCollaboratorResponse>(
      'PATCH',
      `/arches/todo-lists/${listId}/collaborators/${userId}`,
      input,
    );
  }

  async removeTodoCollaborator(listId: string, userId: string) {
    return this.request<void>('DELETE', `/arches/todo-lists/${listId}/collaborators/${userId}`);
  }

  async assignTodoItem(listId: string, itemId: string, input: { username: string }) {
    return this.request<TodoItemAssignmentResponse>(
      'POST',
      `/arches/todo-lists/${listId}/items/${itemId}/assign`,
      input,
    );
  }

  async unassignTodoItem(listId: string, itemId: string, userId: string) {
    return this.request<void>(
      'DELETE',
      `/arches/todo-lists/${listId}/items/${itemId}/assign/${userId}`,
    );
  }

  async assignTodoSection(listId: string, sectionId: string, input: { username: string }) {
    return this.request<TodoSectionAssignmentResponse>(
      'POST',
      `/arches/todo-lists/${listId}/sections/${sectionId}/assign`,
      input,
    );
  }

  async unassignTodoSection(listId: string, sectionId: string, userId: string) {
    return this.request<void>(
      'DELETE',
      `/arches/todo-lists/${listId}/sections/${sectionId}/assign/${userId}`,
    );
  }

  // ==========================================================================
  // Document Sign API
  // ==========================================================================

  async listDocumentSignEnvelopes(options?: {
    status?: DocumentSignEnvelopeStatus[];
    search?: string;
    sortBy?: 'created_at' | 'sent_at' | 'completed_at';
    limit?: number;
    offset?: number;
  }): Promise<DocumentSignEnvelopeListResponse> {
    const qs = new URLSearchParams();
    if (options?.status) {
      for (const s of options.status) qs.append('status', s);
    }
    if (options?.search) qs.set('search', options.search);
    if (options?.sortBy) qs.set('sortBy', options.sortBy);
    if (options?.limit !== undefined) qs.set('limit', String(options.limit));
    if (options?.offset !== undefined) qs.set('offset', String(options.offset));
    const q = qs.toString();
    return this.request<DocumentSignEnvelopeListResponse>(
      'GET',
      `/arches/document-sign/envelopes${q ? `?${q}` : ''}`,
    );
  }

  async getDocumentSignEnvelope(envelopeId: string) {
    return this.request<{ envelope: DocumentSignEnvelopeDetailResponse }>(
      'GET',
      `/arches/document-sign/envelopes/${envelopeId}`,
    );
  }

  async createDocumentSignEnvelope(input: {
    title: string;
    message?: string;
    signingOrder?: 'parallel' | 'sequential';
  }) {
    return this.request<{ envelope: DocumentSignEnvelopeResponse }>(
      'POST',
      '/arches/document-sign/envelopes',
      input,
    );
  }

  async updateDocumentSignEnvelope(
    envelopeId: string,
    input: {
      title?: string;
      message?: string | null;
      signingOrder?: 'parallel' | 'sequential';
    },
  ) {
    return this.request<{ envelope: DocumentSignEnvelopeResponse }>(
      'PATCH',
      `/arches/document-sign/envelopes/${envelopeId}`,
      input,
    );
  }

  async deleteDocumentSignEnvelope(envelopeId: string) {
    return this.request<void>('DELETE', `/arches/document-sign/envelopes/${envelopeId}`);
  }

  async listDocumentSignFiles(envelopeId: string) {
    return this.request<{ files: DocumentSignFileResponse[] }>(
      'GET',
      `/arches/document-sign/envelopes/${envelopeId}/files`,
    );
  }

  async createDocumentSignUploadUrl(
    envelopeId: string,
    input: { fileName: string; mimeType: string; sizeBytes: number },
  ) {
    return this.request<DocumentSignUploadUrlResponse>(
      'POST',
      `/arches/document-sign/envelopes/${envelopeId}/files/upload-url`,
      input,
    );
  }

  async confirmDocumentSignUpload(
    envelopeId: string,
    input: { fileId: string; s3Key: string; fileName: string; sizeBytes: number },
  ) {
    return this.request<{ file: DocumentSignFileResponse }>(
      'POST',
      `/arches/document-sign/envelopes/${envelopeId}/files/confirm`,
      input,
    );
  }

  async deleteDocumentSignFile(envelopeId: string, fileId: string) {
    return this.request<void>(
      'DELETE',
      `/arches/document-sign/envelopes/${envelopeId}/files/${fileId}`,
    );
  }

  async getDocumentSignFileDownloadUrl(envelopeId: string, fileId: string) {
    return this.request<{ downloadUrl: string; expiresAt: string }>(
      'GET',
      `/arches/document-sign/envelopes/${envelopeId}/files/${fileId}/download-url`,
    );
  }

  async listDocumentSignRecipients(envelopeId: string) {
    return this.request<{ recipients: DocumentSignRecipientResponse[] }>(
      'GET',
      `/arches/document-sign/envelopes/${envelopeId}/recipients`,
    );
  }

  async setDocumentSignRecipients(
    envelopeId: string,
    input: { recipients: DocumentSignRecipientInput[] },
  ) {
    return this.request<{ recipients: DocumentSignRecipientResponse[] }>(
      'PUT',
      `/arches/document-sign/envelopes/${envelopeId}/recipients`,
      input,
    );
  }

  async listDocumentSignFields(envelopeId: string, fileId: string) {
    return this.request<{ fields: DocumentSignFieldResponse[] }>(
      'GET',
      `/arches/document-sign/envelopes/${envelopeId}/files/${fileId}/fields`,
    );
  }

  async setDocumentSignFields(
    envelopeId: string,
    fileId: string,
    input: { fields: DocumentSignFieldInput[] },
  ) {
    return this.request<{ fields: DocumentSignFieldResponse[] }>(
      'PUT',
      `/arches/document-sign/envelopes/${envelopeId}/files/${fileId}/fields`,
      input,
    );
  }

  async sendDocumentSignEnvelope(envelopeId: string) {
    return this.request<DocumentSignSendEnvelopeResponse>(
      'POST',
      `/arches/document-sign/envelopes/${envelopeId}/send`,
    );
  }

  async getDocumentSignSealedDownloadUrl(envelopeId: string) {
    return this.request<{ downloadUrl: string; expiresAt: string }>(
      'GET',
      `/arches/document-sign/envelopes/${envelopeId}/sealed/download-url`,
    );
  }

  async resealDocumentSignEnvelope(envelopeId: string) {
    return this.request<{
      envelopeId: string;
      alreadyExisted: boolean;
      sha256Hex: string;
      pageCount: number;
      sizeBytes: number;
    }>('POST', `/arches/document-sign/envelopes/${envelopeId}/reseal`);
  }

  async getDocumentSignSigningSealedUrl(token: string) {
    return this.request<{ downloadUrl: string; expiresAt: string }>(
      'GET',
      `/public/document-sign/sign/${encodeURIComponent(token)}/sealed/download-url`,
    );
  }

  async listDocumentSignEvents(envelopeId: string, options?: { limit?: number; offset?: number }) {
    const qs = new URLSearchParams();
    if (options?.limit !== undefined) qs.set('limit', String(options.limit));
    if (options?.offset !== undefined) qs.set('offset', String(options.offset));
    const q = qs.toString();
    return this.request<{
      events: DocumentSignEventResponse[];
      total: number;
      limit: number;
      offset: number;
    }>('GET', `/arches/document-sign/envelopes/${envelopeId}/events${q ? `?${q}` : ''}`);
  }

  async voidDocumentSignEnvelope(envelopeId: string, reason: string) {
    return this.request<{ envelope: DocumentSignEnvelopeResponse }>(
      'POST',
      `/arches/document-sign/envelopes/${envelopeId}/void`,
      { reason },
    );
  }

  // -- Public signing endpoints (token auth, used by SigningPage) --
  // These bypass the bearer-token machinery — the token rides in the URL
  // path, and the route lives under /v1/public/document-sign.
  async getDocumentSignSigningSession(token: string) {
    return this.request<DocumentSignSigningSessionResponse>(
      'GET',
      `/public/document-sign/sign/${encodeURIComponent(token)}`,
    );
  }

  async getDocumentSignSigningFileUrl(token: string, fileId: string) {
    return this.request<{ downloadUrl: string; expiresAt: string }>(
      'GET',
      `/public/document-sign/sign/${encodeURIComponent(token)}/files/${fileId}/download-url`,
    );
  }

  async acceptDocumentSignConsent(token: string) {
    return this.request<{ success: true }>(
      'POST',
      `/public/document-sign/sign/${encodeURIComponent(token)}/consent`,
      { acceptedDisclosure: true },
    );
  }

  async submitDocumentSignFields(
    token: string,
    input: { fields: DocumentSignSigningFieldSubmission[] },
  ) {
    return this.request<{ success: true }>(
      'PUT',
      `/public/document-sign/sign/${encodeURIComponent(token)}/fields`,
      input,
    );
  }

  async submitDocumentSignSubmit(token: string) {
    return this.request<{
      success: true;
      envelopeStatus: DocumentSignEnvelopeStatus;
      envelopeCompleted: boolean;
    }>('POST', `/public/document-sign/sign/${encodeURIComponent(token)}/submit`);
  }

  async declineDocumentSignSignature(token: string, reason: string) {
    return this.request<{
      success: true;
      envelopeStatus: DocumentSignEnvelopeStatus;
    }>('POST', `/public/document-sign/sign/${encodeURIComponent(token)}/decline`, { reason });
  }

  // ==========================================================================
  // Customer Support API
  // ==========================================================================

  async trackArcheOpen(archeId: string) {
    return this.request<{ counted: boolean }>('POST', `/arches/${archeId}/track-open`);
  }

  async customerSupportChat(payload: {
    message: string;
    conversationId?: string;
    conversationHistory?: Array<{ role: 'user' | 'assistant'; content: string }>;
  }) {
    return this.request<{
      requestId: string;
      conversationId: string;
      selectedModel: {
        provider: string;
        modelName: string;
        displayName: string;
        tier: string;
      };
    }>('POST', '/arches/customer-support/chat', payload);
  }

  async createSuggestion(input: { title: string; description: string }) {
    return this.request<{
      suggestionId: string;
      title: string;
      description: string;
      status: string;
      createdAt: string;
    }>('POST', '/arches/customer-support/suggestions', input);
  }

  async listSuggestionsAdmin(options?: { status?: string; limit?: number; offset?: number }) {
    const params = new URLSearchParams();
    if (options?.status) params.set('status', options.status);
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.offset) params.set('offset', options.offset.toString());
    const qs = params.toString() ? `?${params.toString()}` : '';
    return this.request<{
      suggestions: Array<{
        suggestionId: string;
        userId: string;
        title: string;
        description: string;
        status: string;
        adminNotes: string | null;
        createdAt: string;
        updatedAt: string;
      }>;
      total: number;
    }>('GET', `/arches/customer-support/admin/suggestions${qs}`);
  }

  async updateSuggestionAdmin(
    suggestionId: string,
    input: { status?: string; adminNotes?: string | null },
  ) {
    return this.request<{
      suggestionId: string;
      title: string;
      description: string;
      status: string;
      adminNotes: string | null;
      updatedAt: string;
    }>('PUT', `/arches/customer-support/admin/suggestions/${suggestionId}`, input);
  }

  async deleteSuggestionAdmin(suggestionId: string) {
    return this.request<{ suggestionId: string }>(
      'DELETE',
      `/arches/customer-support/admin/suggestions/${suggestionId}`,
    );
  }

  async createBugReport(input: { title: string; description: string; stepsToReproduce?: string }) {
    return this.request<{
      bugReportId: string;
      title: string;
      description: string;
      status: string;
      createdAt: string;
    }>('POST', '/arches/customer-support/bug-reports', input);
  }

  async listBugReportsAdmin(options?: { status?: string; limit?: number; offset?: number }) {
    const params = new URLSearchParams();
    if (options?.status) params.set('status', options.status);
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.offset) params.set('offset', options.offset.toString());
    const qs = params.toString() ? `?${params.toString()}` : '';
    return this.request<{
      bugReports: Array<{
        bugReportId: string;
        userId: string;
        title: string;
        description: string;
        stepsToReproduce: string | null;
        status: string;
        adminNotes: string | null;
        createdAt: string;
        updatedAt: string;
      }>;
      total: number;
    }>('GET', `/arches/customer-support/admin/bug-reports${qs}`);
  }

  async updateBugReportAdmin(
    bugReportId: string,
    input: { status?: string; adminNotes?: string | null },
  ) {
    return this.request<{
      bugReportId: string;
      title: string;
      description: string;
      status: string;
      adminNotes: string | null;
      updatedAt: string;
    }>('PUT', `/arches/customer-support/admin/bug-reports/${bugReportId}`, input);
  }

  async deleteBugReportAdmin(bugReportId: string) {
    return this.request<{ bugReportId: string }>(
      'DELETE',
      `/arches/customer-support/admin/bug-reports/${bugReportId}`,
    );
  }

  async createSupportTicket(input: {
    subject: string;
    description: string;
    categoryId?: string;
    priority?: string;
  }) {
    return this.request<SupportTicketResponse>('POST', '/arches/customer-support/tickets', input);
  }

  async listSupportTickets(options?: {
    status?: string;
    categoryId?: string;
    limit?: number;
    offset?: number;
  }) {
    const params = new URLSearchParams();
    if (options?.status) params.set('status', options.status);
    if (options?.categoryId) params.set('categoryId', options.categoryId);
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.offset) params.set('offset', options.offset.toString());
    const qs = params.toString();
    return this.request<SupportTicketListResult>(
      'GET',
      `/arches/customer-support/tickets${qs ? `?${qs}` : ''}`,
    );
  }

  async getSupportTicket(ticketId: string) {
    return this.request<SupportTicketDetailResponse>(
      'GET',
      `/arches/customer-support/tickets/${ticketId}`,
    );
  }

  async listSupportTicketMessages(ticketId: string) {
    return this.request<{ messages: SupportMessageResponse[] }>(
      'GET',
      `/arches/customer-support/tickets/${ticketId}/messages`,
    );
  }

  async createSupportTicketMessage(ticketId: string, input: { content: string }) {
    return this.request<SupportMessageResponse>(
      'POST',
      `/arches/customer-support/tickets/${ticketId}/messages`,
      input,
    );
  }

  async listSupportFaqs(categoryId?: string) {
    const qs = categoryId ? `?categoryId=${categoryId}` : '';
    return this.request<{ faqs: SupportFaqResponse[] }>('GET', `/arches/customer-support/faq${qs}`);
  }

  async searchSupportFaqs(query: string) {
    return this.request<{ faqs: SupportFaqResponse[] }>(
      'GET',
      `/arches/customer-support/faq/search?q=${encodeURIComponent(query)}`,
    );
  }

  async listSupportCategoriesUser() {
    return this.request<{ categories: SupportCategoryResponse[] }>(
      'GET',
      '/arches/customer-support/categories',
    );
  }

  // Admin endpoints
  async listAllSupportTickets(options?: {
    status?: string;
    priority?: string;
    categoryId?: string;
    assignedTo?: string;
    userId?: string;
    search?: string;
    sortBy?: string;
    sortOrder?: string;
    tagId?: string;
    includeDeleted?: boolean;
    limit?: number;
    offset?: number;
  }) {
    const params = new URLSearchParams();
    if (options?.status) params.set('status', options.status);
    if (options?.priority) params.set('priority', options.priority);
    if (options?.categoryId) params.set('categoryId', options.categoryId);
    if (options?.assignedTo) params.set('assignedTo', options.assignedTo);
    if (options?.userId) params.set('userId', options.userId);
    if (options?.search) params.set('search', options.search);
    if (options?.sortBy) params.set('sortBy', options.sortBy);
    if (options?.sortOrder) params.set('sortOrder', options.sortOrder);
    if (options?.tagId) params.set('tagId', options.tagId);
    if (options?.includeDeleted) params.set('includeDeleted', 'true');
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.offset) params.set('offset', options.offset.toString());
    const qs = params.toString();
    return this.request<SupportAdminTicketListResult>(
      'GET',
      `/arches/customer-support/admin/tickets${qs ? `?${qs}` : ''}`,
    );
  }

  async bulkUpdateSupportTickets(input: {
    ticketIds: string[];
    status?: string;
    priority?: string;
    assignedTo?: string | null;
  }) {
    return this.request<{ updated: number }>(
      'PUT',
      '/arches/customer-support/admin/tickets/bulk',
      input,
    );
  }

  async listSupportTicketsByUser(userId: string, options?: { limit?: number }) {
    const params = new URLSearchParams();
    params.set('userId', userId);
    if (options?.limit) params.set('limit', options.limit.toString());
    return this.request<SupportAdminTicketListResult>(
      'GET',
      `/arches/customer-support/admin/tickets?${params.toString()}`,
    );
  }

  async getSupportTicketAdmin(ticketId: string) {
    return this.request<SupportAdminTicketResponse>(
      'GET',
      `/arches/customer-support/admin/tickets/${ticketId}`,
    );
  }

  async updateSupportTicketAdmin(
    ticketId: string,
    input: {
      status?: string;
      priority?: string;
      aiPriority?: string | null;
      assignedTo?: string | null;
      categoryId?: string;
    },
  ) {
    return this.request<SupportAdminTicketResponse>(
      'PUT',
      `/arches/customer-support/admin/tickets/${ticketId}`,
      input,
    );
  }

  async listSupportTicketMessagesAdmin(ticketId: string) {
    return this.request<{ messages: SupportAdminMessageResponse[] }>(
      'GET',
      `/arches/customer-support/admin/tickets/${ticketId}/messages`,
    );
  }

  async createSupportTicketMessageAdmin(
    ticketId: string,
    input: { content: string; isInternal?: boolean },
  ) {
    return this.request<SupportAdminMessageResponse>(
      'POST',
      `/arches/customer-support/admin/tickets/${ticketId}/messages`,
      input,
    );
  }

  async deleteSupportMessageAdmin(ticketId: string, messageId: string) {
    return this.request<{ messageId: string; deletedAt: string }>(
      'DELETE',
      `/arches/customer-support/admin/tickets/${ticketId}/messages/${messageId}`,
    );
  }

  async getSupportAnalytics() {
    return this.request<SupportAnalyticsResponse>(
      'GET',
      '/arches/customer-support/admin/analytics',
    );
  }

  async listSupportCategoriesAdmin() {
    return this.request<{ categories: SupportCategoryAdminResponse[] }>(
      'GET',
      '/arches/customer-support/admin/categories',
    );
  }

  async createSupportCategory(input: {
    name: string;
    description?: string;
    displayOrder?: number;
  }) {
    return this.request<SupportCategoryAdminResponse>(
      'POST',
      '/arches/customer-support/admin/categories',
      input,
    );
  }

  async updateSupportCategory(
    categoryId: string,
    input: {
      name?: string;
      description?: string | null;
      displayOrder?: number;
      isActive?: boolean;
    },
  ) {
    return this.request<SupportCategoryAdminResponse>(
      'PUT',
      `/arches/customer-support/admin/categories/${categoryId}`,
      input,
    );
  }

  async deleteSupportCategory(categoryId: string) {
    return this.request<void>('DELETE', `/arches/customer-support/admin/categories/${categoryId}`);
  }

  async listSupportFaqsAdmin(categoryId?: string) {
    const qs = categoryId ? `?categoryId=${categoryId}` : '';
    return this.request<{ faqs: SupportFaqAdminResponse[] }>(
      'GET',
      `/arches/customer-support/admin/faq${qs}`,
    );
  }

  async createSupportFaq(input: {
    question: string;
    answer: string;
    categoryId?: string;
    displayOrder?: number;
    isPublished?: boolean;
  }) {
    return this.request<SupportFaqAdminResponse>(
      'POST',
      '/arches/customer-support/admin/faq',
      input,
    );
  }

  async updateSupportFaq(
    faqId: string,
    input: {
      question?: string;
      answer?: string;
      categoryId?: string | null;
      displayOrder?: number;
      isPublished?: boolean;
    },
  ) {
    return this.request<SupportFaqAdminResponse>(
      'PUT',
      `/arches/customer-support/admin/faq/${faqId}`,
      input,
    );
  }

  async deleteSupportFaq(faqId: string) {
    return this.request<void>('DELETE', `/arches/customer-support/admin/faq/${faqId}`);
  }

  async listSupportCannedResponses(categoryId?: string) {
    const qs = categoryId ? `?categoryId=${categoryId}` : '';
    return this.request<{ responses: SupportCannedResponseResponse[] }>(
      'GET',
      `/arches/customer-support/admin/canned-responses${qs}`,
    );
  }

  async createSupportCannedResponse(input: {
    title: string;
    content: string;
    categoryId?: string;
  }) {
    return this.request<SupportCannedResponseResponse>(
      'POST',
      '/arches/customer-support/admin/canned-responses',
      input,
    );
  }

  async updateSupportCannedResponse(
    responseId: string,
    input: { title?: string; content?: string; categoryId?: string | null },
  ) {
    return this.request<SupportCannedResponseResponse>(
      'PUT',
      `/arches/customer-support/admin/canned-responses/${responseId}`,
      input,
    );
  }

  async deleteSupportCannedResponse(responseId: string) {
    return this.request<void>(
      'DELETE',
      `/arches/customer-support/admin/canned-responses/${responseId}`,
    );
  }

  // Tag management
  async listSupportTags() {
    return this.request<{ tags: SupportTagResponse[] }>(
      'GET',
      '/arches/customer-support/admin/tags',
    );
  }

  async createSupportTag(input: { name: string; color?: string; description?: string }) {
    return this.request<SupportTagResponse>('POST', '/arches/customer-support/admin/tags', input);
  }

  async updateSupportTag(
    tagId: string,
    input: { name?: string; color?: string; description?: string | null },
  ) {
    return this.request<SupportTagResponse>(
      'PUT',
      `/arches/customer-support/admin/tags/${tagId}`,
      input,
    );
  }

  async deleteSupportTag(tagId: string) {
    return this.request<void>('DELETE', `/arches/customer-support/admin/tags/${tagId}`);
  }

  async assignSupportTicketTags(ticketId: string, tagIds: string[]) {
    return this.request<{ tags: SupportTagResponse[] }>(
      'POST',
      `/arches/customer-support/admin/tickets/${ticketId}/tags`,
      { tagIds },
    );
  }

  async removeSupportTicketTag(ticketId: string, tagId: string) {
    return this.request<void>(
      'DELETE',
      `/arches/customer-support/admin/tickets/${ticketId}/tags/${tagId}`,
    );
  }

  // Knowledge Base management (admin)
  async listSupportKbArticlesAdmin(options?: {
    categoryId?: string;
    search?: string;
    limit?: number;
    offset?: number;
  }) {
    const params = new URLSearchParams();
    if (options?.categoryId) params.set('categoryId', options.categoryId);
    if (options?.search) params.set('search', options.search);
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.offset) params.set('offset', options.offset.toString());
    const qs = params.toString();
    return this.request<{ articles: SupportKbArticleResponse[]; total: number }>(
      'GET',
      `/arches/customer-support/admin/knowledge-base${qs ? `?${qs}` : ''}`,
    );
  }

  async createSupportKbArticle(input: {
    title: string;
    content: string;
    categoryId?: string;
    keywords?: string[];
    isPublished?: boolean;
  }) {
    return this.request<SupportKbArticleResponse>(
      'POST',
      '/arches/customer-support/admin/knowledge-base',
      input,
    );
  }

  async updateSupportKbArticle(
    kbId: string,
    input: {
      title?: string;
      content?: string;
      categoryId?: string | null;
      keywords?: string[];
      isPublished?: boolean;
    },
  ) {
    return this.request<SupportKbArticleResponse>(
      'PUT',
      `/arches/customer-support/admin/knowledge-base/${kbId}`,
      input,
    );
  }

  async deleteSupportKbArticle(kbId: string) {
    return this.request<void>('DELETE', `/arches/customer-support/admin/knowledge-base/${kbId}`);
  }

  // Knowledge Base search (user)
  async searchSupportKbArticles(q: string, limit?: number) {
    const params = new URLSearchParams();
    params.set('q', q);
    if (limit) params.set('limit', limit.toString());
    return this.request<{ articles: SupportKbArticleResponse[] }>(
      'GET',
      `/arches/customer-support/knowledge-base/search?${params.toString()}`,
    );
  }

  // ==========================================================================
  // Leads Companies API
  // ==========================================================================

  async listLeadsCompanies(params?: Record<string, string | number | undefined>) {
    const searchParams = new URLSearchParams();
    if (params) {
      for (const [key, value] of Object.entries(params)) {
        if (value !== undefined) searchParams.set(key, String(value));
      }
    }
    const qs = searchParams.toString();
    return this.request<LeadsListCompaniesResult>(
      'GET',
      `/arches/leads/companies${qs ? `?${qs}` : ''}`,
    );
  }

  async getLeadsCompany(companyId: string) {
    return this.request<LeadsCompany>('GET', `/arches/leads/companies/${companyId}`);
  }

  async createLeadsCompany(input: LeadsCreateCompanyInput) {
    return this.request<LeadsCompany>('POST', '/arches/leads/companies', input);
  }

  async updateLeadsCompany(
    companyId: string,
    input: Partial<LeadsCreateCompanyInput> & { status?: string },
  ) {
    return this.request<LeadsCompany>('PUT', `/arches/leads/companies/${companyId}`, input);
  }

  async deleteLeadsCompany(companyId: string) {
    return this.request<void>('DELETE', `/arches/leads/companies/${companyId}`);
  }

  async archiveLeadsCompany(companyId: string) {
    return this.request<LeadsCompany>('PATCH', `/arches/leads/companies/${companyId}/archive`);
  }

  async restoreLeadsCompany(companyId: string) {
    return this.request<LeadsCompany>('PATCH', `/arches/leads/companies/${companyId}/restore`);
  }

  async permanentlyDeleteLeadsCompany(companyId: string) {
    return this.request<void>('DELETE', `/arches/leads/companies/${companyId}/permanent`);
  }

  async getLeadsCompanyIndustries() {
    return this.request<{ industries: string[] }>('GET', '/arches/leads/companies/industries');
  }

  // ==========================================================================
  // Leads Contacts API
  // ==========================================================================

  async listLeadsContacts(params?: Record<string, string | number | undefined>) {
    const searchParams = new URLSearchParams();
    if (params) {
      for (const [key, value] of Object.entries(params)) {
        if (value !== undefined) searchParams.set(key, String(value));
      }
    }
    const qs = searchParams.toString();
    return this.request<LeadsListContactsResult>(
      'GET',
      `/arches/leads/contacts${qs ? `?${qs}` : ''}`,
    );
  }

  async getLeadsContact(contactId: string) {
    return this.request<LeadsContact>('GET', `/arches/leads/contacts/${contactId}`);
  }

  async createLeadsContact(input: LeadsCreateContactInput) {
    return this.request<LeadsContact>('POST', '/arches/leads/contacts', input);
  }

  async updateLeadsContact(
    contactId: string,
    input: Partial<LeadsCreateContactInput> & { status?: string },
  ) {
    return this.request<LeadsContact>('PUT', `/arches/leads/contacts/${contactId}`, input);
  }

  async deleteLeadsContact(contactId: string) {
    return this.request<void>('DELETE', `/arches/leads/contacts/${contactId}`);
  }

  async archiveLeadsContact(contactId: string) {
    return this.request<LeadsContact>('PATCH', `/arches/leads/contacts/${contactId}/archive`);
  }

  async restoreLeadsContact(contactId: string) {
    return this.request<LeadsContact>('PATCH', `/arches/leads/contacts/${contactId}/restore`);
  }

  async permanentlyDeleteLeadsContact(contactId: string) {
    return this.request<void>('DELETE', `/arches/leads/contacts/${contactId}/permanent`);
  }

  async getLeadsContactLifecycleStages() {
    return this.request<{ stages: Array<{ stage: string; count: number }> }>(
      'GET',
      '/arches/leads/contacts/lifecycle-stages',
    );
  }

  // ==========================================================================
  // Leads Pipelines API
  // ==========================================================================

  async listLeadsPipelines() {
    return this.request<{ pipelines: LeadsPipeline[] }>('GET', '/arches/leads/pipelines');
  }

  async getLeadsPipeline(pipelineId: string) {
    return this.request<LeadsPipeline>('GET', `/arches/leads/pipelines/${pipelineId}`);
  }

  async createLeadsPipeline(input: {
    name: string;
    stages?: Array<{ name: string; stageType?: string; color?: string }>;
  }) {
    return this.request<LeadsPipeline>('POST', '/arches/leads/pipelines', input);
  }

  async updateLeadsPipeline(pipelineId: string, input: { name?: string }) {
    return this.request<LeadsPipeline>('PUT', `/arches/leads/pipelines/${pipelineId}`, input);
  }

  async deleteLeadsPipeline(pipelineId: string) {
    return this.request<void>('DELETE', `/arches/leads/pipelines/${pipelineId}`);
  }

  async addLeadsPipelineStage(
    pipelineId: string,
    input: { name: string; stageType?: string; color?: string },
  ) {
    return this.request<LeadsPipelineStage>(
      'POST',
      `/arches/leads/pipelines/${pipelineId}/stages`,
      input,
    );
  }

  async updateLeadsPipelineStage(
    pipelineId: string,
    stageId: string,
    input: { name?: string; stageType?: string; color?: string },
  ) {
    return this.request<LeadsPipelineStage>(
      'PUT',
      `/arches/leads/pipelines/${pipelineId}/stages/${stageId}`,
      input,
    );
  }

  async deleteLeadsPipelineStage(pipelineId: string, stageId: string) {
    return this.request<void>('DELETE', `/arches/leads/pipelines/${pipelineId}/stages/${stageId}`);
  }

  async reorderLeadsPipelineStages(pipelineId: string, stageIds: string[]) {
    return this.request<{ stages: LeadsPipelineStage[] }>(
      'POST',
      `/arches/leads/pipelines/${pipelineId}/stages/reorder`,
      { stageIds },
    );
  }

  // ==========================================================================
  // Leads Deals API
  // ==========================================================================

  async listLeadsDeals(params?: Record<string, string | number | undefined>) {
    const searchParams = new URLSearchParams();
    if (params) {
      for (const [key, value] of Object.entries(params)) {
        if (value !== undefined) searchParams.set(key, String(value));
      }
    }
    const qs = searchParams.toString();
    return this.request<LeadsListDealsResult>('GET', `/arches/leads/deals${qs ? `?${qs}` : ''}`);
  }

  async getLeadsDeal(dealId: string) {
    return this.request<LeadsDeal>('GET', `/arches/leads/deals/${dealId}`);
  }

  async createLeadsDeal(input: LeadsCreateDealInput) {
    return this.request<LeadsDeal>('POST', '/arches/leads/deals', input);
  }

  async updateLeadsDeal(
    dealId: string,
    input: Partial<LeadsCreateDealInput> & { status?: string },
  ) {
    return this.request<LeadsDeal>('PUT', `/arches/leads/deals/${dealId}`, input);
  }

  async moveLeadsDealToStage(dealId: string, stageId: string) {
    return this.request<LeadsDeal>('PATCH', `/arches/leads/deals/${dealId}/stage`, { stageId });
  }

  async deleteLeadsDeal(dealId: string) {
    return this.request<void>('DELETE', `/arches/leads/deals/${dealId}`);
  }

  async restoreLeadsDeal(dealId: string) {
    return this.request<LeadsDeal>('PATCH', `/arches/leads/deals/${dealId}/restore`);
  }

  async getLeadsPipelineSummary(pipelineId: string) {
    return this.request<LeadsPipelineSummary>(
      'GET',
      `/arches/leads/deals/pipeline-summary/${pipelineId}`,
    );
  }

  // Leads Activities
  async listLeadsActivities(params?: Record<string, string | number>) {
    const qs = params
      ? '?' + new URLSearchParams(Object.entries(params).map(([k, v]) => [k, String(v)])).toString()
      : '';
    return this.request<LeadsListActivitiesResult>('GET', `/arches/leads/activities${qs}`);
  }

  async createLeadsActivity(input: LeadsCreateActivityInput) {
    return this.request<LeadsActivity>('POST', '/arches/leads/activities', input);
  }

  async deleteLeadsActivity(id: string) {
    return this.request<void>('DELETE', `/arches/leads/activities/${id}`);
  }

  // Leads Tasks
  async listLeadsTasks(params?: Record<string, string | number | boolean>) {
    const qs = params
      ? '?' + new URLSearchParams(Object.entries(params).map(([k, v]) => [k, String(v)])).toString()
      : '';
    return this.request<LeadsListTasksResult>('GET', `/arches/leads/tasks${qs}`);
  }

  async getLeadsTask(id: string) {
    return this.request<LeadsTask>('GET', `/arches/leads/tasks/${id}`);
  }

  async createLeadsTask(input: LeadsCreateTaskInput) {
    return this.request<LeadsTask>('POST', '/arches/leads/tasks', input);
  }

  async updateLeadsTask(id: string, input: Partial<LeadsCreateTaskInput>) {
    return this.request<LeadsTask>('PUT', `/arches/leads/tasks/${id}`, input);
  }

  async completeLeadsTask(id: string) {
    return this.request<LeadsTask>('PATCH', `/arches/leads/tasks/${id}/complete`);
  }

  async deleteLeadsTask(id: string) {
    return this.request<void>('DELETE', `/arches/leads/tasks/${id}`);
  }

  async getLeadsOverdueTasks() {
    return this.request<{ tasks: LeadsTask[] }>('GET', '/arches/leads/tasks/overdue');
  }

  // Leads Custom Fields
  async listLeadsCustomFields(entityType?: string) {
    const qs = entityType ? `?entityType=${entityType}` : '';
    return this.request<{ fields: LeadsCustomFieldDefinition[] }>(
      'GET',
      `/arches/leads/custom-fields${qs}`,
    );
  }

  async createLeadsCustomField(input: LeadsCreateCustomFieldInput) {
    return this.request<LeadsCustomFieldDefinition>('POST', '/arches/leads/custom-fields', input);
  }

  async updateLeadsCustomField(id: string, input: Partial<LeadsCreateCustomFieldInput>) {
    return this.request<LeadsCustomFieldDefinition>(
      'PUT',
      `/arches/leads/custom-fields/${id}`,
      input,
    );
  }

  async deleteLeadsCustomField(id: string) {
    return this.request<void>('DELETE', `/arches/leads/custom-fields/${id}`);
  }

  // Leads Automations
  async listLeadsAutomations() {
    return this.request<{ rules: LeadsAutomationRule[] }>('GET', '/arches/leads/automations');
  }

  async getLeadsAutomation(id: string) {
    return this.request<LeadsAutomationRule>('GET', `/arches/leads/automations/${id}`);
  }

  async createLeadsAutomation(input: LeadsCreateAutomationInput) {
    return this.request<LeadsAutomationRule>('POST', '/arches/leads/automations', input);
  }

  async updateLeadsAutomation(id: string, input: Partial<LeadsCreateAutomationInput>) {
    return this.request<LeadsAutomationRule>('PUT', `/arches/leads/automations/${id}`, input);
  }

  async toggleLeadsAutomation(id: string) {
    return this.request<LeadsAutomationRule>('POST', `/arches/leads/automations/${id}/toggle`);
  }

  async deleteLeadsAutomation(id: string) {
    return this.request<void>('DELETE', `/arches/leads/automations/${id}`);
  }

  async getLeadsAutomationExecutions(id: string) {
    return this.request<{ executions: LeadsAutomationExecution[] }>(
      'GET',
      `/arches/leads/automations/${id}/executions`,
    );
  }

  // Leads Dashboard
  async getLeadsDashboardMetrics(pipelineId?: string) {
    const qs = pipelineId ? `?pipelineId=${pipelineId}` : '';
    return this.request<LeadsDashboardMetrics>('GET', `/arches/leads/dashboard/metrics${qs}`);
  }

  // ==========================================================================
  // Leads Email Templates API
  // ==========================================================================

  async listLeadsEmailTemplates(params?: Record<string, string | number>) {
    const qs = params
      ? '?' + new URLSearchParams(Object.entries(params).map(([k, v]) => [k, String(v)])).toString()
      : '';
    return this.request<LeadsListEmailTemplatesResult>('GET', `/arches/leads/email-templates${qs}`);
  }

  async getLeadsEmailTemplate(id: string) {
    return this.request<LeadsEmailTemplate>('GET', `/arches/leads/email-templates/${id}`);
  }

  async createLeadsEmailTemplate(input: LeadsCreateEmailTemplateInput) {
    return this.request<LeadsEmailTemplate>('POST', '/arches/leads/email-templates', input);
  }

  async updateLeadsEmailTemplate(id: string, input: Partial<LeadsCreateEmailTemplateInput>) {
    return this.request<LeadsEmailTemplate>('PUT', `/arches/leads/email-templates/${id}`, input);
  }

  async deleteLeadsEmailTemplate(id: string) {
    return this.request<void>('DELETE', `/arches/leads/email-templates/${id}`);
  }

  async previewLeadsEmailTemplate(id: string, variables: Record<string, string>) {
    return this.request<LeadsRenderedTemplate>(
      'POST',
      `/arches/leads/email-templates/${id}/preview`,
      {
        variables,
      },
    );
  }

  // ==========================================================================
  // Leads Timeline API
  // ==========================================================================

  async getLeadsTimeline(params?: Record<string, string | number>) {
    const qs = params
      ? '?' + new URLSearchParams(Object.entries(params).map(([k, v]) => [k, String(v)])).toString()
      : '';
    return this.request<LeadsTimelineResult>('GET', `/arches/leads/timeline${qs}`);
  }

  // ==========================================================================
  // Leads Reporting API
  // ==========================================================================

  async getLeadsPipelineVelocity(pipelineId: string) {
    return this.request<LeadsPipelineVelocityResult>(
      'GET',
      `/arches/leads/reports/pipeline-velocity?pipelineId=${pipelineId}`,
    );
  }

  async getLeadsSalesForecast(pipelineId?: string) {
    const qs = pipelineId ? `?pipelineId=${pipelineId}` : '';
    return this.request<LeadsSalesForecastResult>('GET', `/arches/leads/reports/forecast${qs}`);
  }

  async getLeadsActivityTrends(period?: string) {
    const qs = period ? `?period=${period}` : '';
    return this.request<LeadsActivityTrendsResult>(
      'GET',
      `/arches/leads/reports/activity-trends${qs}`,
    );
  }

  async getLeadsLostDealAnalysis(pipelineId?: string) {
    const qs = pipelineId ? `?pipelineId=${pipelineId}` : '';
    return this.request<LeadsLostDealAnalysisResult>(
      'GET',
      `/arches/leads/reports/lost-deals${qs}`,
    );
  }

  // ==========================================================================
  // Leads Import/Export API
  // ==========================================================================

  async exportLeadsEntities(entityType: string, filters?: { search?: string; status?: string }) {
    const params = new URLSearchParams();
    if (filters?.search) params.set('search', filters.search);
    if (filters?.status) params.set('status', filters.status);
    const qs = params.toString();

    // Cookie-based auth — credentials:'include' attaches the session cookie
    // automatically. Matches the platform's standard request flow.
    const url = `${API_BASE}/arches/leads/export/${entityType}${qs ? `?${qs}` : ''}`;
    const response = await fetch(url, { method: 'GET', credentials: 'include' });
    if (!response.ok) {
      throw new Error(`Export failed with status ${response.status}`);
    }
    return response.text();
  }

  async previewLeadsImport(entityType: string, csvData: string) {
    return this.request<LeadsImportPreviewResult>(
      'POST',
      `/arches/leads/import/${entityType}/preview`,
      {
        csvData,
      },
    );
  }

  async executeLeadsImport(
    entityType: string,
    rows: Record<string, string>[],
    fieldMapping: Record<string, string>,
  ) {
    return this.request<LeadsImportExecuteResult>(
      'POST',
      `/arches/leads/import/${entityType}/execute`,
      {
        rows,
        fieldMapping,
      },
    );
  }

  // Leads AI
  async scoreLead(contactId: string) {
    return this.request<LeadsLeadScoreResult>('POST', `/arches/leads/ai/score-lead/${contactId}`);
  }

  async generateAISummary(entityType: 'contact' | 'company', entityId: string) {
    return this.request<LeadsAISummaryResult>(
      'POST',
      `/arches/leads/ai/summarize/${entityType}/${entityId}`,
    );
  }

  async computeWinLikelihood(dealId: string) {
    return this.request<LeadsWinLikelihoodResult>(
      'POST',
      `/arches/leads/ai/win-likelihood/${dealId}`,
    );
  }

  async semanticSearch(entityType: 'contact' | 'company', query: string, limit?: number) {
    const params = new URLSearchParams({ q: query });
    if (limit) params.set('limit', String(limit));
    return this.request<{ results: LeadsSemanticSearchResult[] }>(
      'GET',
      `/arches/leads/ai/search/${entityType}?${params}`,
    );
  }

  async findSimilarEntities(entityType: 'contact' | 'company', entityId: string, limit?: number) {
    const params = limit ? `?limit=${limit}` : '';
    return this.request<{ results: LeadsSemanticSearchResult[] }>(
      'GET',
      `/arches/leads/ai/similar/${entityType}/${entityId}${params}`,
    );
  }

  async generateLeadsEmbedding(entityType: 'contact' | 'company', entityId: string) {
    return this.request<{ success: boolean }>(
      'POST',
      `/arches/leads/ai/embed/${entityType}/${entityId}`,
    );
  }

  // Leads Deal Risk
  async getDealRiskAssessment() {
    return this.request<LeadsDealRiskAssessment>('GET', '/arches/leads/deal-risk');
  }

  async getDealRiskAlerts() {
    return this.request<LeadsDealRiskAssessment>('GET', '/arches/leads/deal-risk/alerts');
  }

  // Leads Bulk Actions
  async bulkUpdateContacts(ids: string[], updates: LeadsBulkContactUpdates) {
    return this.request<{ updated: number }>('POST', '/arches/leads/bulk/contacts/update', {
      ids,
      updates,
    });
  }

  async bulkDeleteContacts(ids: string[]) {
    return this.request<{ deleted: number }>('POST', '/arches/leads/bulk/contacts/delete', { ids });
  }

  async bulkArchiveContacts(ids: string[]) {
    return this.request<{ updated: number }>('POST', '/arches/leads/bulk/contacts/archive', {
      ids,
    });
  }

  async bulkUpdateCompanies(ids: string[], updates: LeadsBulkCompanyUpdates) {
    return this.request<{ updated: number }>('POST', '/arches/leads/bulk/companies/update', {
      ids,
      updates,
    });
  }

  async bulkDeleteCompanies(ids: string[]) {
    return this.request<{ deleted: number }>('POST', '/arches/leads/bulk/companies/delete', {
      ids,
    });
  }

  // Leads Deduplication
  async findDuplicateContacts() {
    return this.request<{ groups: LeadsDuplicateGroup[] }>('GET', '/arches/leads/dedup/contacts');
  }

  async mergeContacts(primaryId: string, secondaryIds: string[]) {
    return this.request<LeadsMergeResult>('POST', '/arches/leads/dedup/contacts/merge', {
      primaryId,
      secondaryIds,
    });
  }

  // Leads Saved Views
  async listLeadsSavedViews(entityType?: string) {
    const params = entityType ? `?entityType=${entityType}` : '';
    return this.request<{ views: LeadsSavedView[] }>('GET', `/arches/leads/views${params}`);
  }

  async createLeadsSavedView(input: LeadsCreateSavedViewInput) {
    return this.request<LeadsSavedView>('POST', '/arches/leads/views', input);
  }

  async updateLeadsSavedView(id: string, input: Partial<LeadsCreateSavedViewInput>) {
    return this.request<LeadsSavedView>('PUT', `/arches/leads/views/${id}`, input);
  }

  async deleteLeadsSavedView(id: string) {
    return this.request<void>('DELETE', `/arches/leads/views/${id}`);
  }

  // Leads Contact Relationships
  async getContactRelationships(contactId: string) {
    return this.request<{ relationships: LeadsContactRelationship[] }>(
      'GET',
      `/arches/leads/contacts/${contactId}/relationships`,
    );
  }

  async addContactRelationship(contactId: string, input: LeadsCreateRelationshipInput) {
    return this.request<LeadsContactRelationship>(
      'POST',
      `/arches/leads/contacts/${contactId}/relationships`,
      input,
    );
  }

  async removeContactRelationship(contactId: string, relationshipId: string) {
    return this.request<void>(
      'DELETE',
      `/arches/leads/contacts/${contactId}/relationships/${relationshipId}`,
    );
  }

  // ==========================================================================
  // Email API
  // ==========================================================================

  async listEmailAccounts() {
    return this.request<{ accounts: EmailAccount[] }>('GET', '/arches/email/accounts');
  }

  async createEmailAccount(input: EmailCreateAccountInput) {
    return this.request<EmailAccount>('POST', '/arches/email/accounts', input);
  }

  async getEmailAccount(accountId: string) {
    return this.request<EmailAccount>('GET', `/arches/email/accounts/${accountId}`);
  }

  async updateEmailAccount(accountId: string, input: { displayName?: string }) {
    return this.request<EmailAccount>('PATCH', `/arches/email/accounts/${accountId}`, input);
  }

  async deleteEmailAccount(accountId: string) {
    return this.request<void>('DELETE', `/arches/email/accounts/${accountId}`);
  }

  async getEmailAccountSettings(accountId: string) {
    return this.request<EmailAccountSettings>(
      'GET',
      `/arches/email/accounts/${accountId}/settings`,
    );
  }

  async updateEmailAccountSettings(accountId: string, input: Partial<EmailAccountSettingsInput>) {
    return this.request<EmailAccountSettings>(
      'PATCH',
      `/arches/email/accounts/${accountId}/settings`,
      input,
    );
  }

  async listEmailAccountModules(accountId: string) {
    return this.request<{ modules: EmailAccountModule[] }>(
      'GET',
      `/arches/email/accounts/${accountId}/modules`,
    );
  }

  async updateEmailAccountModule(
    accountId: string,
    moduleName: string,
    input: { enabled?: boolean; config?: Record<string, unknown> },
  ) {
    return this.request<EmailAccountModule>(
      'PATCH',
      `/arches/email/accounts/${accountId}/modules/${moduleName}`,
      input,
    );
  }

  // Email Messages

  async listEmailMessages(options: EmailMessageListOptions) {
    const params = new URLSearchParams();
    params.set('accountId', options.accountId);
    if (options.folderId) params.set('folderId', options.folderId);
    if (options.labelId) params.set('labelId', options.labelId);
    if (options.threadId) params.set('threadId', options.threadId);
    if (options.isRead !== undefined) params.set('isRead', String(options.isRead));
    if (options.isStarred !== undefined) params.set('isStarred', String(options.isStarred));
    if (options.importance) params.set('importance', options.importance);
    if (options.limit) params.set('limit', String(options.limit));
    if (options.offset) params.set('offset', String(options.offset));
    if (options.sort) params.set('sort', options.sort);
    return this.request<EmailMessageListResult>(
      'GET',
      `/arches/email/messages?${params.toString()}`,
    );
  }

  async getEmailMessage(messageId: string, accountId: string) {
    return this.request<EmailMessageDetail>(
      'GET',
      `/arches/email/messages/${messageId}?accountId=${accountId}`,
    );
  }

  async composeEmailMessage(input: EmailComposeInput) {
    return this.request<EmailMessage>('POST', '/arches/email/messages', input);
  }

  async updateEmailMessage(messageId: string, accountId: string, input: EmailMessageUpdateInput) {
    return this.request<EmailMessage>(
      'PATCH',
      `/arches/email/messages/${messageId}?accountId=${accountId}`,
      input,
    );
  }

  async deleteEmailMessage(messageId: string, accountId: string) {
    return this.request<void>(
      'DELETE',
      `/arches/email/messages/${messageId}?accountId=${accountId}`,
    );
  }

  async replyToEmailMessage(messageId: string, input: EmailReplyInput) {
    return this.request<EmailMessage>('POST', `/arches/email/messages/${messageId}/reply`, input);
  }

  async forwardEmailMessage(messageId: string, input: EmailForwardInput) {
    return this.request<EmailMessage>('POST', `/arches/email/messages/${messageId}/forward`, input);
  }

  async bulkEmailMessageAction(input: EmailBulkActionInput) {
    return this.request<{ success: boolean; affected: number }>(
      'POST',
      '/arches/email/messages/bulk',
      input,
    );
  }

  async undoSendEmailMessage(messageId: string, accountId: string) {
    return this.request<{ success: boolean; messageId: string }>(
      'POST',
      `/arches/email/messages/${messageId}/undo-send?accountId=${accountId}`,
    );
  }

  // Email Threads

  async listEmailThreads(options: EmailThreadListOptions) {
    const params = new URLSearchParams();
    params.set('accountId', options.accountId);
    if (options.folderId) params.set('folderId', options.folderId);
    if (options.labelId) params.set('labelId', options.labelId);
    if (options.isRead !== undefined) params.set('isRead', String(options.isRead));
    if (options.isStarred !== undefined) params.set('isStarred', String(options.isStarred));
    if (options.smartCategory) params.set('smartCategory', options.smartCategory);
    if (options.limit) params.set('limit', String(options.limit));
    if (options.offset) params.set('offset', String(options.offset));
    if (options.sort) params.set('sort', options.sort);
    return this.request<EmailThreadListResult>('GET', `/arches/email/threads?${params.toString()}`);
  }

  async getEmailThread(threadId: string, accountId: string) {
    return this.request<EmailThreadDetail>(
      'GET',
      `/arches/email/threads/${threadId}?accountId=${accountId}`,
    );
  }

  async updateEmailThread(threadId: string, accountId: string, input: EmailThreadUpdateInput) {
    return this.request<EmailThread>(
      'PATCH',
      `/arches/email/threads/${threadId}?accountId=${accountId}`,
      input,
    );
  }

  async deleteEmailThread(threadId: string, accountId: string) {
    return this.request<void>('DELETE', `/arches/email/threads/${threadId}?accountId=${accountId}`);
  }

  async bulkEmailThreadAction(input: EmailBulkThreadActionInput) {
    return this.request<{ success: boolean; affected: number }>(
      'POST',
      '/arches/email/threads/bulk',
      input,
    );
  }

  // Email Folders

  async listEmailFolders(accountId: string) {
    return this.request<{ folders: EmailFolder[] }>(
      'GET',
      `/arches/email/folders?accountId=${accountId}`,
    );
  }

  async createEmailFolder(input: EmailCreateFolderInput) {
    return this.request<EmailFolderBase>('POST', '/arches/email/folders', input);
  }

  async updateEmailFolder(folderId: string, accountId: string, input: EmailUpdateFolderInput) {
    return this.request<EmailFolderBase>(
      'PATCH',
      `/arches/email/folders/${folderId}?accountId=${accountId}`,
      input,
    );
  }

  async deleteEmailFolder(folderId: string, accountId: string) {
    return this.request<void>('DELETE', `/arches/email/folders/${folderId}?accountId=${accountId}`);
  }

  // Email Labels

  async listEmailLabels(accountId: string) {
    return this.request<{ labels: EmailLabel[] }>(
      'GET',
      `/arches/email/labels?accountId=${accountId}`,
    );
  }

  async createEmailLabel(input: EmailCreateLabelInput) {
    return this.request<EmailLabelBase>('POST', '/arches/email/labels', input);
  }

  async updateEmailLabel(labelId: string, accountId: string, input: EmailUpdateLabelInput) {
    return this.request<EmailLabelBase>(
      'PATCH',
      `/arches/email/labels/${labelId}?accountId=${accountId}`,
      input,
    );
  }

  async deleteEmailLabel(labelId: string, accountId: string) {
    return this.request<void>('DELETE', `/arches/email/labels/${labelId}?accountId=${accountId}`);
  }

  // Email Contacts

  async listEmailContacts(options: EmailContactListOptions) {
    const params = new URLSearchParams();
    params.set('accountId', options.accountId);
    if (options.search) params.set('search', options.search);
    if (options.isFavorite !== undefined) params.set('isFavorite', String(options.isFavorite));
    if (options.limit) params.set('limit', String(options.limit));
    if (options.offset) params.set('offset', String(options.offset));
    if (options.sort) params.set('sort', options.sort);
    return this.request<EmailContactListResult>(
      'GET',
      `/arches/email/contacts?${params.toString()}`,
    );
  }

  async getEmailContact(contactId: string, accountId: string) {
    return this.request<EmailContact>(
      'GET',
      `/arches/email/contacts/${contactId}?accountId=${accountId}`,
    );
  }

  async createEmailContact(input: EmailCreateContactInput) {
    return this.request<EmailContact>('POST', '/arches/email/contacts', input);
  }

  async updateEmailContact(contactId: string, accountId: string, input: EmailUpdateContactInput) {
    return this.request<EmailContact>(
      'PATCH',
      `/arches/email/contacts/${contactId}?accountId=${accountId}`,
      input,
    );
  }

  async deleteEmailContact(contactId: string, accountId: string) {
    return this.request<void>(
      'DELETE',
      `/arches/email/contacts/${contactId}?accountId=${accountId}`,
    );
  }

  async toggleEmailContactFavorite(contactId: string, accountId: string) {
    return this.request<EmailContact>(
      'POST',
      `/arches/email/contacts/${contactId}/favorite?accountId=${accountId}`,
    );
  }

  async listEmailContactGroups(accountId: string) {
    return this.request<{ groups: EmailContactGroup[] }>(
      'GET',
      `/arches/email/contact-groups?accountId=${accountId}`,
    );
  }

  async createEmailContactGroup(input: EmailCreateContactGroupInput) {
    return this.request<EmailContactGroup>('POST', '/arches/email/contact-groups', input);
  }

  async updateEmailContactGroup(
    groupId: string,
    accountId: string,
    input: EmailUpdateContactGroupInput,
  ) {
    return this.request<EmailContactGroup>(
      'PATCH',
      `/arches/email/contact-groups/${groupId}?accountId=${accountId}`,
      input,
    );
  }

  async deleteEmailContactGroup(groupId: string, accountId: string) {
    return this.request<void>(
      'DELETE',
      `/arches/email/contact-groups/${groupId}?accountId=${accountId}`,
    );
  }

  async listEmailContactGroupMembers(groupId: string, accountId: string) {
    return this.request<{ members: EmailContactGroupMember[] }>(
      'GET',
      `/arches/email/contact-groups/${groupId}/members?accountId=${accountId}`,
    );
  }

  async addEmailContactGroupMembers(groupId: string, accountId: string, contactIds: string[]) {
    return this.request<{ added: number }>(
      'POST',
      `/arches/email/contact-groups/${groupId}/members?accountId=${accountId}`,
      { contactIds },
    );
  }

  async removeEmailContactGroupMember(groupId: string, contactId: string, accountId: string) {
    return this.request<void>(
      'DELETE',
      `/arches/email/contact-groups/${groupId}/members/${contactId}?accountId=${accountId}`,
    );
  }

  // Email Filters

  async listEmailFilters(accountId: string) {
    return this.request<{ filters: EmailFilter[] }>(
      'GET',
      `/arches/email/filters?accountId=${accountId}`,
    );
  }

  async createEmailFilter(input: EmailCreateFilterInput) {
    return this.request<EmailFilter>('POST', '/arches/email/filters', input);
  }

  async updateEmailFilter(filterId: string, accountId: string, input: EmailUpdateFilterInput) {
    return this.request<EmailFilter>(
      'PATCH',
      `/arches/email/filters/${filterId}?accountId=${accountId}`,
      input,
    );
  }

  async deleteEmailFilter(filterId: string, accountId: string) {
    return this.request<void>('DELETE', `/arches/email/filters/${filterId}?accountId=${accountId}`);
  }

  async testEmailFilter(filterId: string, accountId: string) {
    return this.request<{ matchingMessageIds: string[]; totalTested: number }>(
      'POST',
      `/arches/email/filters/${filterId}/test?accountId=${accountId}`,
    );
  }

  async applyEmailFilters(accountId: string) {
    return this.request<{ applied: number; affected: number }>(
      'POST',
      `/arches/email/filters/apply?accountId=${accountId}`,
    );
  }

  // Email Templates

  async listEmailTemplates(accountId: string, options?: { category?: string; search?: string }) {
    const params = new URLSearchParams();
    params.set('accountId', accountId);
    if (options?.category) params.set('category', options.category);
    if (options?.search) params.set('search', options.search);
    return this.request<{ templates: EmailTemplate[] }>(
      'GET',
      `/arches/email/templates?${params.toString()}`,
    );
  }

  async createEmailTemplate(input: EmailCreateTemplateInput) {
    return this.request<EmailTemplate>('POST', '/arches/email/templates', input);
  }

  async updateEmailTemplate(
    templateId: string,
    accountId: string,
    input: EmailUpdateTemplateInput,
  ) {
    return this.request<EmailTemplate>(
      'PATCH',
      `/arches/email/templates/${templateId}?accountId=${accountId}`,
      input,
    );
  }

  async deleteEmailTemplate(templateId: string, accountId: string) {
    return this.request<void>(
      'DELETE',
      `/arches/email/templates/${templateId}?accountId=${accountId}`,
    );
  }

  // Email Snippets

  async listEmailSnippets(accountId: string) {
    return this.request<{ snippets: EmailSnippet[] }>(
      'GET',
      `/arches/email/snippets?accountId=${accountId}`,
    );
  }

  async createEmailSnippet(input: EmailCreateSnippetInput) {
    return this.request<EmailSnippet>('POST', '/arches/email/snippets', input);
  }

  async updateEmailSnippet(snippetId: string, accountId: string, input: EmailUpdateSnippetInput) {
    return this.request<EmailSnippet>(
      'PATCH',
      `/arches/email/snippets/${snippetId}?accountId=${accountId}`,
      input,
    );
  }

  async deleteEmailSnippet(snippetId: string, accountId: string) {
    return this.request<void>(
      'DELETE',
      `/arches/email/snippets/${snippetId}?accountId=${accountId}`,
    );
  }

  // Email Signatures

  async listEmailSignatures(accountId: string) {
    return this.request<{ signatures: EmailSignature[] }>(
      'GET',
      `/arches/email/signatures?accountId=${accountId}`,
    );
  }

  async createEmailSignature(input: EmailCreateSignatureInput) {
    return this.request<EmailSignature>('POST', '/arches/email/signatures', input);
  }

  async updateEmailSignature(
    signatureId: string,
    accountId: string,
    input: EmailUpdateSignatureInput,
  ) {
    return this.request<EmailSignature>(
      'PATCH',
      `/arches/email/signatures/${signatureId}?accountId=${accountId}`,
      input,
    );
  }

  async deleteEmailSignature(signatureId: string, accountId: string) {
    return this.request<void>(
      'DELETE',
      `/arches/email/signatures/${signatureId}?accountId=${accountId}`,
    );
  }

  // Email Search

  async searchEmailMessages(options: EmailSearchOptions) {
    const params = new URLSearchParams();
    params.set('accountId', options.accountId);
    if (options.q) params.set('q', options.q);
    if (options.from) params.set('from', options.from);
    if (options.to) params.set('to', options.to);
    if (options.subject) params.set('subject', options.subject);
    if (options.hasAttachment !== undefined)
      params.set('hasAttachment', String(options.hasAttachment));
    if (options.isRead !== undefined) params.set('isRead', String(options.isRead));
    if (options.isStarred !== undefined) params.set('isStarred', String(options.isStarred));
    if (options.folder) params.set('folder', options.folder);
    if (options.label) params.set('label', options.label);
    if (options.dateFrom) params.set('dateFrom', options.dateFrom);
    if (options.dateTo) params.set('dateTo', options.dateTo);
    if (options.importance) params.set('importance', options.importance);
    if (options.limit) params.set('limit', String(options.limit));
    if (options.offset) params.set('offset', String(options.offset));
    return this.request<EmailSearchResult>('GET', `/arches/email/search?${params.toString()}`);
  }

  async naturalSearchEmail(accountId: string, naturalQuery: string) {
    return this.request<EmailNaturalSearchResult>('POST', '/arches/email/search/natural', {
      accountId,
      query: naturalQuery,
    });
  }

  // Email Screener

  async listPendingScreenedSenders(accountId: string) {
    return this.request<{ senders: EmailScreenedSenderWithMessage[] }>(
      'GET',
      `/arches/email/screener?accountId=${accountId}`,
    );
  }

  async listApprovedScreenedSenders(accountId: string) {
    return this.request<{ senders: EmailScreenedSender[] }>(
      'GET',
      `/arches/email/screener/approved?accountId=${accountId}`,
    );
  }

  async listRejectedScreenedSenders(accountId: string) {
    return this.request<{ senders: EmailScreenedSender[] }>(
      'GET',
      `/arches/email/screener/rejected?accountId=${accountId}`,
    );
  }

  async approveScreenedSender(senderId: string, accountId: string) {
    return this.request<EmailScreenerApproveResult>(
      'POST',
      `/arches/email/screener/${senderId}/approve?accountId=${accountId}`,
    );
  }

  async rejectScreenedSender(senderId: string, accountId: string) {
    return this.request<EmailScreenerRejectResult>(
      'POST',
      `/arches/email/screener/${senderId}/reject?accountId=${accountId}`,
    );
  }

  async updateScreenedSender(
    senderId: string,
    accountId: string,
    input: { status: 'pending' | 'approved' | 'rejected' },
  ) {
    return this.request<EmailScreenedSender>(
      'PATCH',
      `/arches/email/screener/${senderId}?accountId=${accountId}`,
      input,
    );
  }

  // Email Scheduling

  async scheduleEmailSend(input: EmailScheduleInput) {
    return this.request<EmailScheduledMessage>('POST', '/arches/email/schedule', input);
  }

  async listScheduledEmails(accountId: string) {
    return this.request<{ scheduled: EmailScheduledMessage[] }>(
      'GET',
      `/arches/email/scheduled?accountId=${accountId}`,
    );
  }

  async rescheduleEmail(scheduleId: string, accountId: string, scheduledFor: string) {
    return this.request<EmailScheduledMessage>(
      'PATCH',
      `/arches/email/schedule/${scheduleId}?accountId=${accountId}`,
      { scheduledFor },
    );
  }

  async cancelScheduledEmail(scheduleId: string, accountId: string) {
    return this.request<EmailScheduledMessage>(
      'DELETE',
      `/arches/email/schedule/${scheduleId}?accountId=${accountId}`,
    );
  }

  async snoozeEmail(input: EmailSnoozeInput) {
    return this.request<EmailSnoozedMessage>('POST', '/arches/email/snooze', input);
  }

  async listSnoozedEmails(accountId: string) {
    return this.request<{ snoozed: EmailSnoozedMessage[] }>(
      'GET',
      `/arches/email/snoozed?accountId=${accountId}`,
    );
  }

  async updateEmailSnooze(snoozeId: string, accountId: string, snoozeUntil: string) {
    return this.request<EmailSnoozedMessage>(
      'PATCH',
      `/arches/email/snooze/${snoozeId}?accountId=${accountId}`,
      { snoozeUntil },
    );
  }

  async cancelEmailSnooze(snoozeId: string, accountId: string) {
    return this.request<{ success: boolean; messageId: string }>(
      'DELETE',
      `/arches/email/snooze/${snoozeId}?accountId=${accountId}`,
    );
  }

  async returnDueSnoozedEmails(accountId: string) {
    return this.request<{ returned: number }>(
      'POST',
      `/arches/email/snooze/return-due?accountId=${accountId}`,
    );
  }

  // Email AI

  async aiComposeEmail(input: EmailAIComposeInput) {
    return this.request<EmailAIComposeResult>('POST', '/arches/email/ai/compose', input);
  }

  async aiEmailReplySuggestions(accountId: string, messageId: string) {
    return this.request<{ suggestions: EmailAIReplySuggestion[] }>(
      'POST',
      '/arches/email/ai/reply-suggestions',
      { accountId, messageId },
    );
  }

  async aiSummarizeEmailThread(accountId: string, threadId: string) {
    return this.request<EmailAISummarizeResult>('POST', '/arches/email/ai/summarize', {
      accountId,
      threadId,
    });
  }

  async aiAdjustEmailTone(
    accountId: string,
    bodyHtml: string,
    targetTone: 'formal' | 'casual' | 'diplomatic' | 'assertive' | 'friendly',
  ) {
    return this.request<{ bodyHtml: string }>('POST', '/arches/email/ai/tone-adjust', {
      accountId,
      bodyHtml,
      targetTone,
    });
  }

  async aiSuggestEmailSubject(accountId: string, bodyText: string) {
    return this.request<{ suggestions: string[] }>('POST', '/arches/email/ai/subject-suggest', {
      accountId,
      bodyText,
    });
  }

  async aiCategorizeEmails(accountId: string, messageIds: string[]) {
    return this.request<{ categories: EmailAICategoryResult[] }>(
      'POST',
      '/arches/email/ai/categorize',
      { accountId, messageIds },
    );
  }

  async aiPhishingCheck(accountId: string, messageId: string) {
    return this.request<EmailAIPhishingResult>('POST', '/arches/email/ai/phishing-check', {
      accountId,
      messageId,
    });
  }

  // Email Attachments

  async uploadEmailAttachment(input: EmailAttachmentUploadInput) {
    return this.request<EmailAttachmentUploadResult>(
      'POST',
      '/arches/email/attachments/upload',
      input,
    );
  }

  async getEmailAttachment(attachmentId: string, accountId: string) {
    return this.request<EmailAttachmentMeta>(
      'GET',
      `/arches/email/attachments/${attachmentId}?accountId=${accountId}`,
    );
  }

  async downloadEmailAttachment(attachmentId: string, accountId: string) {
    return this.request<EmailAttachmentDownloadResult>(
      'GET',
      `/arches/email/attachments/${attachmentId}/download?accountId=${accountId}`,
    );
  }

  async deleteEmailAttachment(attachmentId: string, accountId: string) {
    return this.request<void>(
      'DELETE',
      `/arches/email/attachments/${attachmentId}?accountId=${accountId}`,
    );
  }
}

// ==========================================================================
// Liquidity Cache Types
// ==========================================================================

export interface LcPoolResponse {
  poolId: string;
  name: string;
  description: string | null;
  tokenA: {
    itemDefId: string | null;
    symbol: string;
    name: string;
    decimals: number;
    source: string;
  };
  tokenB: {
    itemDefId: string | null;
    symbol: string;
    name: string;
    decimals: number;
    source: string;
  };
  reserveA: string;
  reserveB: string;
  totalLpShares: string;
  feeBps: number;
  minLiquidity: number;
  maxTradePct: number;
  status: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface LcTransactionResponse {
  transactionId: string;
  poolId: string;
  transactionType: string;
  tokenAAmount: string | null;
  tokenBAmount: string | null;
  lpSharesAmount: string | null;
  tradeDirection: string | null;
  feeAmount: string | null;
  priceAtTrade: string | null;
  slippageBps: number | null;
  roundingAdjusted: boolean;
  originalAmount: string | null;
  adjustedAmount: string | null;
  roundingSavings: string | null;
  inventoryTransfersExecuted: boolean;
  createdAt: string;
}

// ==========================================================================
// Conversation Types
// ==========================================================================

export interface ConversationResponse {
  conversationId: string;
  archeId: string;
  title: string | null;
  messageCount: number;
  status: 'active' | 'archived' | 'deleted';
  createdAt: string;
  updatedAt: string;
}

export interface ConversationListResult {
  conversations: ConversationResponse[];
  total: number;
  limit: number;
  offset: number;
}

export interface ConversationMessageInput {
  role: 'user' | 'assistant';
  content: string;
  thinking?: string;
  provider?: ModelProvider;
  modelName?: string;
  modelEntityId?: string;
  inputTokens?: number;
  outputTokens?: number;
  thinkingTokens?: number;
  costUsd?: number;
  latencyMs?: number;
  metadata?: Record<string, unknown>;
}

export interface ConversationMessageResponse {
  messageId: string;
  sequenceNum: number;
  role: 'user' | 'assistant';
  content: string;
  thinking: string | null;
  provider: ModelProvider | null;
  modelName: string | null;
  inputTokens: number | null;
  outputTokens: number | null;
  costUsd: number | null;
  latencyMs: number | null;
  metadata: Record<string, unknown> | null;
  createdAt: string;
}

export interface ConversationWithMessages extends ConversationResponse {
  messages: ConversationMessageResponse[];
}

export interface DeleteAllConversationsResult {
  success: boolean;
  deletedCount: number;
}

export interface ShareConversationResponse {
  shareId: string;
  expiresAt: string;
}

export interface SharedMessage {
  role: 'user' | 'assistant';
  content: string;
  provider: ModelProvider | null;
  modelName: string | null;
  timestamp: string;
}

export interface SharedConversationResponse {
  shareId: string;
  archeId: string;
  title: string | null;
  messages: SharedMessage[];
  createdAt: string;
  expiresAt: string;
}

// ==========================================================================
// Arche Session Types
// ==========================================================================

export interface SingleAIPreferences {
  activeConversationId: string | null;
  selectedProvider: ModelProvider;
  selectedModelEntityId: string | null;
  includeContext: boolean;
  showThinking: boolean;
  providerSettings: {
    openai?: {
      gpt4MaxOutputTokens?: number;
      gpt5Reasoning?: boolean;
      reasoningEffort?: 'low' | 'medium' | 'high' | 'xhigh';
      verbosity?: 'low' | 'medium' | 'high';
      gptMaxOutputTokens?: number;
    };
    anthropic?: {
      maxTokens?: number;
      budgetTokens?: number;
      temperature?: number;
    };
    google?: {
      maxOutputTokens?: number;
      temperature?: number;
      thinkingLevel?: 'LOW' | 'HIGH';
    };
  };
}

export interface MultiAIColumnConfig {
  provider: ModelProvider;
  modelEntityId: string;
  showThinking: boolean;
  gpt5Reasoning?: boolean;
  providerSettings: Record<string, unknown>;
}

export interface MultiAIPreferences {
  activeConversationId: string | null;
  columnConfigs: MultiAIColumnConfig[];
  selectedTier: 'fast' | 'balanced' | 'powerful' | null;
  includeContext: boolean;
  enabledPanels: boolean[];
}

export interface SingleAISessionResponse {
  type: 'single-ai';
  archeId: string;
  preferences: SingleAIPreferences;
  createdAt: string;
  updatedAt: string;
}

export interface MultiAISessionResponse {
  type: 'multi-ai';
  archeId: string;
  preferences: MultiAIPreferences;
  createdAt: string;
  updatedAt: string;
}

export interface ImageGeneratorPreferences {
  selectedProvider: 'openai' | 'google' | 'bfl' | 'recraft';
  selectedModelId: string | null;
  imageSettings: {
    size: string;
    quality: string;
    style: string;
  };
}

export interface ImageGeneratorSessionResponse {
  type: 'image-generator';
  archeId: string;
  preferences: ImageGeneratorPreferences;
  createdAt: string;
  updatedAt: string;
}

export type ArcheSessionResponse =
  | SingleAISessionResponse
  | MultiAISessionResponse
  | ImageGeneratorSessionResponse;

export type ArcheSessionUpdateInput =
  | Partial<SingleAIPreferences>
  | Partial<MultiAIPreferences>
  | Partial<ImageGeneratorPreferences>;

// ==========================================================================
// Arche Metadata Types
// ==========================================================================

/**
 * Lightweight arche metadata for route protection and embed eligibility
 */
export interface ArcheMetadata {
  archId: string;
  isPublic: boolean;
  isFullScreen: boolean;
  hasWorkflow: boolean;
  /** Whether the arche can be embedded in an iframe (public and no workflow) */
  isEmbeddable: boolean;
  renderMode: string;
  sessionType: string;
  isTrusted: boolean;
  suggestedArches: string[];
  redirectPath: string | null;
}

// ==========================================================================
// Favorites Types
// ==========================================================================

export interface UserFavoriteWithArche {
  userId: string;
  archId: string;
  position: number;
  createdAt: string;
  archeName: string;
  archeSlug: string | null;
  archeSimpleDescription: string;
  archeImageRef: string | null;
}

// ==========================================================================
// Marketplace Types
// ==========================================================================

export type ArcheType = 'tool' | 'site';

export interface MarketplaceArche {
  archId: string;
  slug: string | null;
  name: string;
  simpleDescription: string;
  imageRef: string | null;
  categoryId: string | null;
  categoryName: string | null;
  categorySlug: string | null;
  tags: string[];
  fitnessScore: number;
  invocationCount: number;
  pricing: ArchePricing;
  actionPricing: ActionPricing | null;
  hasPerUseCredits: boolean;
  isStatic: boolean;
  archeType: ArcheType;
  relevance?: number;
  contributorId: string;
  isPublic: boolean;
  isFullScreen: boolean;
}

export interface MarketplaceSearchResult {
  arches: MarketplaceArche[];
  total: number;
  limit: number;
  offset: number;
  hasMore: boolean;
  query?: string;
}

export interface MarketplaceArcheDetail {
  archId: string;
  slug: string | null;
  name: string;
  simpleDescription: string;
  expandedDescription: string | null;
  imageRef: string | null;
  categoryId: string | null;
  categoryName: string | null;
  categorySlug: string | null;
  tags: string[];
  fitnessScore: number;
  successRate: number;
  avgCompletionTimeMs: number | null;
  invocationCount: number;
  pricing: ArchePricing;
  actionPricing: ActionPricing | null;
  hasPerUseCredits: boolean;
  costPerformanceConfig: CostPerformanceConfig;
  isStatic: boolean;
  archeType: ArcheType;
  inputSchema: Record<string, unknown> | null;
  contributorId: string;
  publishedAt: string | null;
  isPublic: boolean;
  isFullScreen: boolean;
}

export interface MarketplaceCategory {
  categoryId: string;
  name: string;
  slug: string;
  description: string;
  parentCategoryId: string | null;
  depth: number;
  archeCount: number;
}

export interface ArchePricing {
  model: 'free' | 'fixed' | 'per_use' | 'tiered' | 'no_base_cost' | 'included';
  currency: 'usd';
  priceCents?: number;
  baseFeeCents?: number;
  platformFeePercent?: number;
  tiers?: Array<{ upTo: number; priceCents: number }>;
}

export interface ActionPrice {
  costCredits: number;
  platformFeePercent: number;
}

export type ActionPricing = Record<string, ActionPrice>;

export interface CostPerformanceConfig {
  defaultWeight: number;
  userAdjustable: boolean;
  minWeight: number | null;
  maxWeight: number | null;
}

// ==========================================================================
// Marketplace Entity Types
// ==========================================================================

export type EntityType =
  | 'specialized-model'
  | 'human'
  | 'integration'
  | 'code-block'
  | 'model-definition'
  | 'service';

export interface EntityPricing {
  model: 'free' | 'fixed' | 'per_invocation' | 'no_base_cost' | 'included';
  currency: 'usd';
  priceCents?: number;
}

export interface MarketplaceEntity {
  entityId: string;
  type: EntityType;
  name: string;
  description: string;
  categoryId: string | null;
  categoryName: string | null;
  categorySlug: string | null;
  fitnessScore: number;
  invocationCount: number;
  avgLatencyMs: number | null;
  successRate: number;
  pricing: EntityPricing;
  contributorId: string;
  relevance?: number;
  semanticSimilarity?: number;
}

export interface MarketplaceEntityDetail extends MarketplaceEntity {
  inputSchema: Record<string, unknown>;
  outputSchema: Record<string, unknown>;
  publishedAt: string | null;
  modelProvider?: string;
  modelName?: string;
}

export interface MarketplaceEntitySearchResult {
  entities: MarketplaceEntity[];
  total: number;
  limit: number;
  offset: number;
  hasMore: boolean;
  query?: string;
}

export interface MarketplaceCategoryWithEntityCount {
  categoryId: string;
  name: string;
  slug: string;
  description: string;
  parentCategoryId: string | null;
  depth: number;
  entityCount: number;
}

// ==========================================================================
// Contributor Types
// ==========================================================================

export interface ContributorMetricsSummary {
  totalEntities: number;
  publishedEntities: number;
  totalInvocations: number;
  avgFitness: number;
}

export interface ContributorEntityMetric {
  entityId: string;
  name: string;
  type: string;
  status: string;
  fitnessScore: number;
  invocationCount: number;
  successRate: number;
  avgLatencyMs: number | null;
  totalInputTokens: number;
  totalOutputTokens: number;
  totalCostUsd: number;
  lastInvokedAt: string | null;
}

export interface ContributorMetricsResult {
  summary: ContributorMetricsSummary;
  entities: ContributorEntityMetric[];
}

export interface ContributorArcheDashboardSummary {
  totalArches: number;
  publishedArches: number;
  totalInvocations: number;
  estimatedRevenueCents: number;
  avgFitness: number;
}

export interface ContributorArcheMetric {
  archId: string;
  name: string;
  simpleDescription: string;
  status: string;
  pricingModel: string;
  pricingLabel: string;
  invocationCount: number;
  successRate: number;
  fitnessScore: number;
  estimatedRevenueCents: number;
  publishedAt: string | null;
  updatedAt: string;
  editSessionId: string | null;
}

export interface ContributorArcheDashboardResult {
  summary: ContributorArcheDashboardSummary;
  arches: ContributorArcheMetric[];
}

// ==========================================================================
// Credits Types
// ==========================================================================

export type CreditBucket = 'subscription' | 'fias' | 'system';

export interface CreditBalance {
  userId: string;
  balance: number;
  buckets: {
    subscription: number;
    fias: number;
    system: number;
  };
  subscription: {
    tier: 'free' | 'standard' | 'pro';
    status: 'active' | 'canceled' | 'past_due' | 'trialing' | 'incomplete';
    isOnSubscription: boolean;
    cancelAtPeriodEnd: boolean;
    currentPeriodEnd: string | null;
    monthlyCreditAllocation: number;
  };
  lifetimeUsed: number;
  lifetimeGranted: number;
  lastUpdated: string;
}

export interface CreditTransaction {
  transactionId: string;
  type:
    | 'grant'
    | 'usage'
    | 'refund'
    | 'adjustment'
    | 'system_grant'
    | 'welcome_grant'
    | 'subscription_reset'
    | 'system_reset'
    | 'purchase'
    | 'subscription_credit'
    | string;
  amount: number;
  balanceAfter: number;
  bucket?: 'subscription' | 'fias' | 'system' | null;
  requestId: string | null;
  description: string | null;
  expenseCategory: string | null;
  metadata: Record<string, unknown> | null;
  createdAt: string;
}

export interface CreditTransactionsResult {
  transactions: CreditTransaction[];
  total: number;
  limit: number;
  offset: number;
}

export interface SpendingCategorySummary {
  category: string;
  totalCents: number;
  count: number;
}

export interface CreditGrantResult {
  success: boolean;
  newBalance: number;
  transactionId: string;
}

// ==========================================================================
// Payment Types
// ==========================================================================

export interface PaymentConfig {
  publishableKey: string;
  purchaseAmounts: Array<{
    amountCents: number;
    displayPrice: string;
    credits: number;
  }>;
  minAmountCents: number;
  maxAmountCents: number;
  currency: string;
}

export interface CheckoutSessionResult {
  sessionId: string;
  url: string;
  publishableKey: string;
}

export interface CryptoCheckoutResult {
  chargeId: string;
  hostedUrl: string;
  expiresAt: string;
}

export interface PaymentHistoryItem {
  paymentId: string;
  amountCents: number;
  displayAmount: string;
  currency: string;
  creditsPurchased: number;
  paymentProvider: 'stripe' | 'coinbase';
  status: 'succeeded' | 'pending' | 'failed' | 'refunded' | 'partially_refunded';
  description: string | null;
  createdAt: string;
}

export interface PaymentHistoryResult {
  payments: PaymentHistoryItem[];
  total: number;
  limit: number;
  offset: number;
}

// ==========================================================================
// Admin Arche Management Types
// ==========================================================================

export type ArcheLifecycleState = 'active' | 'disabled' | 'deleted';

export interface ArcheManagementSummary {
  archId: string;
  name: string;
  simpleDescription: string;
  imageRef: string | null;
  archeType: string;
  status: string;
  isListed: boolean;
  lifecycleState: ArcheLifecycleState;
  lifecycleChangedAt: string | null;
  lifecycleChangeReason: string | null;
  contributorId: string;
  suggestedArches: string[];
}

// ==========================================================================
// Admin Payments Types
// ==========================================================================

export interface MaintenanceStatus {
  enabled: boolean;
  message: string | null;
  enabledAt: string | null;
  enabledBy: string | null;
}

export interface AdminPaymentsDashboard {
  period: string;
  profitAndLoss: {
    revenue: {
      totalCents: number;
      display: string;
      purchases: { totalCents: number; display: string; count: number };
      subscriptions: { totalCents: number; display: string; activeCount: number };
    };
    expense: { totalCents: number; display: string; transactionCount: number };
    systemCreditsAbsorbed: { totalCredits: number; display: string; grantCount: number };
    systemCreditUsage: {
      recipients: number;
      byGrantType: Array<{
        grantType: string;
        totalCredits: number;
        display: string;
        count: number;
      }>;
      recipientUsage: { totalConsumed: number; display: string; transactionCount: number };
      byCategory: Array<{ category: string; totalCredits: number; display: string; count: number }>;
    };
    grossMargin: { totalCents: number; display: string; percent: string; isPositive: boolean };
  };
  platform: {
    totalUsers: number;
    activeSubscribers: number;
    averageRevenuePerUser: string;
  };
  expenseByCategory: Array<{
    category: string;
    totalCredits: number;
    display: string;
    count: number;
  }>;
  topSpendingUsers: Array<{
    userId: string;
    totalSpent: number;
    displaySpent: string;
    transactionCount: number;
  }>;
}

export interface AdminPaymentsCosts {
  period: string;
  byCategory: Array<{ category: string; totalCredits: number; displayCost: string; count: number }>;
  byArche: Array<{
    archeId: string;
    archeName: string;
    totalCredits: number;
    displayCost: string;
    count: number;
  }>;
}

// ==========================================================================
// Admin Payment Management Types
// ==========================================================================

export interface AdminUserPaymentDetail {
  userId: string;
  credits: { balance: number; lifetimeUsed: number; lifetimeGranted: number };
  systemCreditsGranted: number;
  subscription: { tier: string; status: string };
  recentPayments: Array<{
    paymentId: string;
    amountCents: number;
    currency: string;
    status: string;
    description: string | null;
    createdAt: string;
  }>;
  recentTransactions: Array<{
    transactionId: string;
    type: string;
    amount: number;
    balanceAfter: number;
    bucket: string | null;
    description: string;
    createdAt: string;
  }>;
}

export interface AdminPaymentSearchParams {
  status?: string;
  userId?: string;
  startDate?: string;
  endDate?: string;
  minAmountCents?: number;
  maxAmountCents?: number;
  limit?: number;
  offset?: number;
}

export interface AdminPaymentRecord {
  paymentId: string;
  userId: string;
  amountCents: number;
  displayAmount: string;
  currency: string;
  creditsPurchased: number;
  paymentProvider: string;
  providerPaymentId: string | null;
  providerChargeId: string | null;
  status: string;
  description: string | null;
  createdAt: string;
}

export interface AdminPaymentSearchResult {
  payments: AdminPaymentRecord[];
  total: number;
  limit: number;
  offset: number;
}

export interface AdminSubscriptionListParams {
  tier?: string;
  status?: string;
  limit?: number;
  offset?: number;
}

export interface AdminSubscriptionRecord {
  subscriptionId: string;
  userId: string;
  tier: string;
  status: string;
  stripeSubscriptionId: string | null;
  stripeCustomerId: string | null;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  monthlyCreditAllocation: number;
  createdAt: string;
  updatedAt: string;
}

export interface AdminSubscriptionListResult {
  subscriptions: AdminSubscriptionRecord[];
  total: number;
  limit: number;
  offset: number;
}

export interface AdminStripeEventListParams {
  eventType?: string;
  startDate?: string;
  endDate?: string;
  processedOk?: boolean;
  limit?: number;
  offset?: number;
}

export interface AdminStripeEvent {
  eventId: string;
  eventType: string;
  receivedAt: string;
  processedAt: string | null;
  processedOk: boolean | null;
  errorMessage: string | null;
}

export interface AdminStripeEventDetail extends AdminStripeEvent {
  payload: unknown;
}

export interface AdminStripeEventListResult {
  events: AdminStripeEvent[];
  total: number;
  limit: number;
  offset: number;
}

// ==========================================================================
// Admin Platform Usage Analytics — Layer 2 dashboard response shapes
// ==========================================================================

export type AnalyticsPeriod = '7d' | '30d' | '90d';

export interface AdminAnalyticsOverview {
  period: AnalyticsPeriod;
  dau: number;
  wau: number;
  mau: number;
  totalUsers: number;
  newUsersPeriod: number;
  activeSubscribers: number;
  totalSpendCredits: number;
  totalSpendDisplay: string;
  dailySeries: Array<{
    date: string;
    activeUsers: number;
    sessions: number;
    pageViews: number;
  }>;
  dailySpendSeries: Array<{
    date: string;
    spendCredits: number;
    invocations: number;
  }>;
  topPagesByViews: Array<{ pageName: string; views: number }>;
  topArchesByOpens: Array<{ archeId: string; opens: number; uniqueUsers: number }>;
  topInvocationCategories: Array<{
    category: string;
    invocations: number;
    totalCredits: number;
  }>;
}

export interface AdminAnalyticsEngagement {
  period: AnalyticsPeriod;
  topArchesByUniqueUsers: Array<{ archeId: string; uniqueUsers: number }>;
  topArchesByEstimatedActiveTime: Array<{ archeId: string; estimatedActiveMinutes: number }>;
  topArchesByOpens: Array<{ archeId: string; opens: number }>;
  topArchesByCredits: Array<{ archeId: string; totalCredits: number; count: number }>;
  featureAdoption: Array<{ feature: string; uniqueUsers: number; totalUses: number }>;
  workflowFunnel: Array<{
    workflow: string;
    started: number;
    completed: number;
    abandoned: number;
    completionRate: number;
  }>;
  bucketSpendDaily: Array<{
    date: string;
    subscription: number;
    fias: number;
    system: number;
  }>;
  expenseByCategory: Array<{ category: string; totalCredits: number; count: number }>;
}

export interface AdminAnalyticsHealth {
  period: AnalyticsPeriod;
  dailyBurnCapCredits: number;
  entityHealth: Array<{
    entityId: string;
    entityName: string;
    invocations: number;
    successes: number;
    failures: number;
    successRate: number;
    p95LatencyMs: number;
  }>;
  dailyInvocationHealth: Array<{ date: string; success: number; failure: number }>;
  usersNearBurnCap: Array<{
    userId: string;
    peakDay: string;
    peakSpendCredits: number;
    capCredits: number;
    daysOverEighty: number;
  }>;
  systemBucketByFeature: Array<{ feature: string; totalCredits: number; count: number }>;
  analyticsIngestionHealth: { hourly: Array<{ hour: string; eventCount: number }> };
}

export interface AdminAnalyticsUserDetail {
  userId: string;
  profile: {
    balance: number;
    subscriptionCreditBalance: number;
    fiasCreditBalance: number;
    systemCreditBalance: number;
    lifetimeUsed: number;
    lifetimeGranted: number;
    subscription: { tier: string; status: string };
  };
  summary: {
    activeDaysInPeriod: number;
    totalSessions: number;
    estimatedActiveMinutes: number;
    totalSpendCredits: number;
    totalInvocations: number;
    topArche: string | null;
  };
  dailyActivity: Array<{
    date: string;
    sessions: number;
    estimatedActiveMinutes: number;
    spendCredits: number;
  }>;
  eventTimeline: Array<{
    occurredAt: string;
    eventType: string;
    eventName: string;
    archeId: string | null;
    pagePath: string | null;
    sessionId: string;
  }>;
  recentTransactions: unknown[];
  recentSessions: Array<{
    requestId: string;
    archId: string;
    messageCount: number;
    totalCreditsUsed: number;
    lastRequestAt: string;
  }>;
  recentBuilderSessions: Array<{
    sessionId: string;
    archId: string | null;
    status: string;
    totalCostCents: number | null;
    lastClaudeResponseAt: string | null;
    updatedAt: string;
  }>;
}

// ==========================================================================
// Subscription Types
// ==========================================================================

export interface SubscriptionTier {
  tier: 'free' | 'standard' | 'pro';
  name: string;
  priceMonthly: number;
  displayPrice: string;
  monthlyCreditAllocation: number;
  storageLimitGb: number;
  features: string[];
}

export interface SubscriptionTiersResult {
  tiers: SubscriptionTier[];
}

export interface SubscriptionInfo {
  subscriptionId: string;
  tier: 'free' | 'standard' | 'pro';
  tierName: string;
  status: 'active' | 'canceled' | 'past_due' | 'trialing' | 'incomplete';
  cancelAtPeriodEnd: boolean;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  monthlyCreditAllocation: number;
  storageLimitGb: number;
  features: string[];
}

// ==========================================================================
// Models Types
// ==========================================================================

export type ModelProvider = 'openai' | 'anthropic' | 'google' | 'xai';
export type ModelTier = 'fast' | 'balanced' | 'powerful';

export interface ModelCapabilities {
  tier: ModelTier;
  isRecommended: boolean;
  supportsThinking: boolean;
  supportsVision: boolean;
  supportsFunctionCalling: boolean;
  maxContextTokens: number;
  maxOutputTokens: number;
  temperatureRange: { min: number; max: number } | null;
  defaultTemperature: number;
  isReasoningModel: boolean;
  apiEndpoint: 'chat' | 'responses';
}

export interface ModelEntity {
  entityId: string;
  name: string;
  description: string;
  provider: ModelProvider;
  providerDisplayName: string;
  modelName: string;
  tier: ModelTier;
  isRecommended: boolean;
  capabilities: ModelCapabilities;
  pricing: ArchePricing;
}

export interface ProviderInfo {
  key: ModelProvider;
  displayName: string;
  models: string[];
  recommendedModelId: string;
}

export interface ModelListResponse {
  models: ModelEntity[];
  providers: ProviderInfo[];
}

// ==========================================================================
// Model Definition Types (Entity-driven parameter schema)
// ==========================================================================

/**
 * Parameter data types for model definition schema
 */
export type ParameterType = 'number' | 'enum' | 'boolean' | 'string';

/**
 * UI control types for parameter rendering
 */
export type ParameterControl = 'slider' | 'input' | 'select' | 'checkbox' | 'toggle' | 'textarea';

/**
 * Parameter categories for UI grouping
 */
export type ParameterCategory = 'output' | 'reasoning' | 'behavior' | 'advanced';

/**
 * Numeric range constraint for parameters
 */
export interface ParameterRange {
  min: number;
  max: number;
  step?: number;
  default: number;
}

/**
 * Option for enum parameters
 */
export interface ParameterOption {
  value: string;
  label: string;
  description?: string;
}

/**
 * Conditional dependency for parameter visibility
 */
export interface ParameterDependency {
  parameter: string;
  value: unknown;
}

/**
 * Conditional default values based on another parameter's value
 */
export interface ConditionalDefault {
  dependsOn: string;
  values: Record<string, unknown>;
}

/**
 * Complete parameter definition from Model Definition Entity
 *
 * Defines everything needed to:
 * 1. Validate parameter values
 * 2. Render UI controls
 * 3. Apply constraints
 * 4. Show/hide conditionally
 */
export interface ParameterDefinition {
  name: string;
  type: ParameterType;
  label: string;
  description: string;
  category: ParameterCategory;
  range?: ParameterRange;
  options?: ParameterOption[];
  defaultValue?: unknown;
  required: boolean;
  dependsOn?: ParameterDependency;
  conditionalDefault?: ConditionalDefault;
  control: ParameterControl;
  apiName?: string;
}

/**
 * API endpoint configuration for model definitions
 */
export interface ApiEndpointConfig {
  url: string;
  method: 'POST';
  format: 'chat' | 'responses' | 'completions' | 'custom';
  streaming: boolean;
}

/**
 * Model definition capabilities
 */
export interface ModelDefinitionCapabilities {
  tier: ModelTier;
  isRecommended: boolean;
  supportsThinking: boolean;
  supportsVision: boolean;
  supportsFunctionCalling: boolean;
  supportsStreaming: boolean;
  maxContextTokens: number;
  maxOutputTokens: number;
  isReasoningModel: boolean;
  inputTokenCostPer1k?: number;
  outputTokenCostPer1k?: number;
  /** Allow provider-specific capability extensions (e.g., providerParams for image models) */
  [key: string]: unknown;
}

/**
 * Model type discriminator
 */
export type ModelType = 'text' | 'image';

/**
 * Model Definition Entity response from API
 */
export interface ModelDefinition {
  entityId: string;
  name: string;
  description: string;
  status: string;
  provider: ModelProvider;
  modelName: string;
  modelType: ModelType;
  displayName: string;
  shortDescription: string;
  apiEndpoint: ApiEndpointConfig;
  parameterSchema: ParameterDefinition[];
  executionConfig: unknown;
  capabilities: ModelDefinitionCapabilities;
}

/**
 * Model Definition list item (lighter weight for lists)
 */
export interface ModelDefinitionListItem {
  entityId: string;
  name: string;
  displayName: string;
  shortDescription: string;
  provider: ModelProvider;
  modelName: string;
  modelType: ModelType;
  tier: ModelTier;
  isRecommended: boolean;
  supportsThinking: boolean;
  maxOutputTokens: number;
  status: string;
  capabilities: ModelDefinitionCapabilities;
}

/**
 * Model Definition list response
 */
export interface ModelDefinitionListResponse {
  definitions: ModelDefinitionListItem[];
  total: number;
}

// ==========================================================================
// File Management Types
// ==========================================================================

export type FileCategory = 'general' | 'document' | 'financial' | 'media' | 'generated';
export type FileStatus = 'active' | 'processing' | 'archived' | 'deleted';
export type StorageClass = 'standard' | 'infrequent' | 'glacier';
export type StorageTier = 'free' | 'basic' | 'premium';

export interface PresignedUploadResponse {
  uploadUrl: string;
  fileId: string;
  s3Key: string;
  expiresAt: string;
}

export interface MultipartUploadResponse {
  uploadId: string;
  fileId: string;
  s3Key: string;
  partSize: number;
  totalParts: number;
}

export interface PresignedPartResponse {
  uploadUrl: string;
  partNumber: number;
  expiresAt: string;
}

export interface UserFileResponse {
  fileId: string;
  fileName: string;
  fileExtension: string | null;
  mimeType: string | null;
  sizeBytes: number;
  checksumSha256?: string | null;
  category: FileCategory;
  sensitivity?: string;
  folderId: string | null;
  tags: string[];
  metadata?: Record<string, unknown>;
  status: FileStatus;
  storageClass: StorageClass;
  sourceType?: string;
  sourceArcheId?: string | null;
  createdAt: string;
  updatedAt: string;
  accessedAt: string;
  /** Lifecycle state of the small WebP thumbnail variant. */
  thumbnailStatus?: 'pending' | 'ready' | 'failed' | 'skipped';
  /** Inlined presigned URL for the WebP thumbnail (only when thumbnailStatus === 'ready'). */
  thumbnailUrl?: string;
  /** Pre-minted presigned URL for the original image, inlined by /files to avoid N+1 fetches. */
  downloadUrl?: string;
}

export interface FileListResult {
  files: UserFileResponse[];
  total: number;
  limit: number;
  offset: number;
}

export interface FolderResponse {
  folderId: string;
  name: string;
  path: string;
  parentFolderId: string | null;
  createdAt: string;
  updatedAt?: string;
}

export interface ContextFolderResponse {
  folderId: string;
  name: string;
  path: string;
  parentFolderId: string | null;
  createdAt: string;
  isProtected: boolean;
}

export interface StorageUsageBreakdown {
  category: FileCategory;
  bytes: number;
  formatted: string;
  fileCount: number;
  percentage: number;
}

export interface StorageUsageResponse {
  userId: string;
  totalBytes: number;
  totalFormatted: string;
  quotaBytes: number;
  quotaFormatted: string;
  usedPercentage: number;
  fileCount: number;
  storageTier: StorageTier;
  breakdown: StorageUsageBreakdown[];
  lastCalculatedAt: string;
}

export interface FileOperationResponse {
  operationId: string;
  fileId: string | null;
  operationType: string;
  bytesTransferred: number;
  bytesFormatted: string;
  costCredits: number;
  archeId: string | null;
  createdAt: string;
}

export interface FileOperationsListResult {
  operations: FileOperationResponse[];
  total: number;
  limit: number;
  offset: number;
}

// ==========================================================================
// Image Generation Types
// ==========================================================================

export interface ImageGenerationInput {
  prompt: string;
  provider?: string;
  model?: string;
  size?: string;
  quality?: string;
  style?: string;
  providerParams?: Record<string, unknown>;
  referenceImageRef?: string;
  referenceImageFileId?: string;
  referenceStrength?: number;
}

export interface ImageGenerationResponse {
  requestId: string;
  archId: string;
  status: 'pending';
}

export interface ImageModel {
  id: string;
  name: string;
  provider: string;
  supportedSizes: string[];
  supportedQualities?: string[];
  supportedStyles?: string[];
  supportsReferenceImage?: boolean;
}

export interface ImageProviderInfo {
  key: string;
  displayName: string;
  models: string[];
  recommendedModelId: string;
}

export interface ImageModelsResponse {
  models: ImageModel[];
  providers: ImageProviderInfo[];
}

// ============================================================================
// Voice Studio Types
// ============================================================================

export interface TranscriptionResult {
  transcriptionId: string;
  text: string;
  detectedLanguage: string | null;
  durationSeconds: number | null;
}

export interface SynthesisResult {
  /** user_files row id; undefined if the optional file registration failed. */
  fileId?: string;
  /** S3 key for the stored audio; usable to re-presign through the standard files API. */
  audioRef: string;
  /** Short-lived presigned URL for immediate playback / download. */
  downloadUrl: string;
}

export interface VoiceModel {
  voiceModelId: string;
  name: string;
  description: string | null;
  provider: string;
  providerVoiceId: string;
  status: string;
  sampleFileIds: string[];
  createdAt: string;
  updatedAt: string;
}

export interface PlatformVoice {
  voiceId: string;
  name: string;
  description: string | null;
  labels: Record<string, string>;
}

export const api = new ApiClient();
export default api;

// ==========================================================================
// Contributor Asset Library Types
// ==========================================================================

export interface ContributorAssetLibraryConfig {
  enabled: boolean;
  suspendedAt: string | null;
  suspendReason: string | null;
  effectiveCaps: {
    maxAssets: number;
    maxStorageBytes: number;
    maxUploadBytes: number;
    maxDecodedPixels: number;
  };
  usage: {
    activeStorageBytes: number;
    activeAssetCount: number;
  };
}

export interface ContributorArcheAsset {
  assetId: string;
  archeId: string;
  contributorId: string;
  storageKey: string;
  contentType: 'image/png' | 'image/jpeg' | 'image/webp';
  contentHash: string;
  sizeBytes: number;
  widthPx: number;
  heightPx: number;
  decodedPixels: number;
  name: string | null;
  altText: string | null;
  tags: string[];
  status: string;
  statusReason: string | null;
  statusChangedAt: string;
  uploadedBy: string;
  uploadedAt: string;
}

// ==========================================================================
// Plugin Types
// ==========================================================================

export interface PluginSubmissionSummary {
  submissionId: string;
  archId: string;
  manifestName: string;
  manifestVersion: string;
  status: string;
  reviewId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PluginSubmissionListResult {
  submissions: PluginSubmissionSummary[];
  total: number;
  limit: number;
  offset: number;
}

export interface PluginSubmissionDetailResult {
  submission: {
    submissionId: string;
    archId: string;
    sourceRef: string;
    manifest: Record<string, unknown>;
    status: string;
    reviewId: string | null;
    buildRef: string | null;
    errorMessage: string | null;
    createdAt: string;
    updatedAt: string;
  };
  review: {
    reviewId: string;
    status: string;
    reviewerType: string;
    riskScore: number | null;
    criticalCount: number;
    highCount: number;
    filesAnalyzed: number;
    dependenciesAnalyzed: number;
    codeFindings: unknown[] | null;
    dependencyFindings: unknown[] | null;
    policyFindings: unknown[] | null;
    qualityFindings: unknown[] | null;
    createdAt: string;
    completedAt: string | null;
  } | null;
}

// ==========================================================================
// Chat Types
// ==========================================================================

export interface ChatMemberPermissions {
  sendMessages?: boolean;
  sendImages?: boolean;
  sendVideos?: boolean;
  sendAudio?: boolean;
  sendFiles?: boolean;
  sendLinks?: boolean;
  sendStickers?: boolean;
  addUsers?: boolean;
  pinMessages?: boolean;
  changeGroupInfo?: boolean;
  banUsers?: boolean;
  deleteMessages?: boolean;
  addAdmins?: boolean;
}

export interface ChatChannelResponse {
  channelId: string;
  channelType: 'direct' | 'global' | 'guild' | 'group';
  name: string | null;
  description: string | null;
  imageRef: string | null;
  avatarFileId: string | null;
  guildId: string | null;
  createdBy: string;
  maxMembers: number | null;
  isReadOnly: boolean;
  isPrivate: boolean;
  inviteCode: string | null;
  defaultPermissions: ChatMemberPermissions;
  status: 'active' | 'archived' | 'suspended';
  createdAt: string;
  updatedAt: string;
  memberCount?: number;
  unreadCount?: number;
  hasLeft?: boolean;
  isPinned?: boolean;
  dmPartnerId?: string | null;
  dmPartnerDisplayName?: string | null;
  lastMessage?: {
    content: string;
    senderId: string;
    senderName: string | null;
    createdAt: string;
  } | null;
}

export interface ChatChannelMemberResponse {
  channelId: string;
  userId: string;
  role: 'owner' | 'admin' | 'moderator' | 'member';
  nickname: string | null;
  isMuted: boolean;
  permissions: ChatMemberPermissions;
  lastReadAt: string | null;
  joinedAt: string;
  displayName?: string;
  avatarImageId?: string | null;
}

export interface ChatChannelBanResponse {
  banId: string;
  channelId: string;
  bannedUserId: string;
  bannedBy: string;
  reason: string | null;
  bannedAt: string;
}

export interface ChatDmPartnerProfile {
  userId: string;
  displayName: string;
  username: string;
  bio: string;
  bannerColor: string;
  avatarColor: string;
  avatarImageId: string | null;
}

/** Structured reference to a file attached to a chat message. */
export interface ChatAttachmentRef {
  fileId: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
}

export interface ChatMessageResponse {
  messageId: string;
  channelId: string;
  senderId: string;
  senderName?: string | null;
  senderAvatarUrl?: string | null;
  senderRole?: 'admin' | 'moderator' | 'ambassador' | 'user';
  content: string;
  messageType: 'text' | 'system' | 'attachment' | 'reply';
  replyToId: string | null;
  replyPreview?: { senderName: string; content: string };
  attachmentRefs: ChatAttachmentRef[] | null;
  mentions: Array<{ userId: string; displayName: string }> | null;
  isEdited: boolean;
  editedAt: string | null;
  isDeleted: boolean;
  createdAt: string;
  reactions?: Array<{ emoji: string; count: number; userIds: string[] }>;
}

export interface ChatChannelListResult {
  channels: ChatChannelResponse[];
  total: number;
}

export interface ChatMessageListResult {
  messages: ChatMessageResponse[];
  hasMore: boolean;
  oldestMessageId: string | null;
  newestMessageId: string | null;
}

export interface ChatUnreadCount {
  channelId: string;
  unreadCount: number;
}

export interface ChatPinnedMessageResponse {
  messageId: string;
  channelId: string;
  content: string;
  senderId: string;
  senderName: string | null;
  pinnedBy: string;
  pinnedAt: string;
  createdAt: string;
}

export interface ChatBlockResponse {
  blockId: string;
  blockedId: string;
  createdAt: string;
}

export interface ChatUserSettingsResponse {
  userId: string;
  displayName: string;
  bio: string;
  bannerColor: string;
  avatarColor: string;
  avatarImageId: string | null;
  notificationNewMessages: 'all' | 'dms' | 'never';
  notificationReactions: boolean;
  notificationChannelAdded: boolean;
  notificationSoundDMs: boolean;
  notificationSoundChannels: boolean;
  appearanceShowEmojis: boolean;
  appearanceShowReactions: boolean;
  appearanceCompactMode: boolean;
  appearanceAccentColor: string;
  appearanceChannelListView: 'sections' | 'unified';
  backgroundImageId: string | null;
  backgroundColor: string;
  backgroundBlur: boolean;
  privacyWhoCanDM: 'everyone' | 'nobody';
  privacyReadReceipts: boolean;
  privacyShowOnlineStatus: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface UpdateChatUserSettingsInput {
  displayName?: string;
  bio?: string;
  bannerColor?: string;
  avatarColor?: string;
  avatarImageId?: string | null;
  notificationNewMessages?: 'all' | 'dms' | 'never';
  notificationReactions?: boolean;
  notificationChannelAdded?: boolean;
  notificationSoundDMs?: boolean;
  notificationSoundChannels?: boolean;
  appearanceShowEmojis?: boolean;
  appearanceShowReactions?: boolean;
  appearanceCompactMode?: boolean;
  appearanceAccentColor?: string;
  appearanceChannelListView?: 'sections' | 'unified';
  backgroundImageId?: string | null;
  backgroundColor?: string;
  backgroundBlur?: boolean;
  privacyWhoCanDM?: 'everyone' | 'nobody';
  privacyReadReceipts?: boolean;
  privacyShowOnlineStatus?: boolean;
}

export interface CreateChatChannelInput {
  channelType: 'group' | 'guild';
  name: string;
  description?: string;
  guildId?: string;
}

// ==========================================================================
// Guild Types
// ==========================================================================

export type GuildType = 'regular' | 'earning';
export type GuildLevel = 'bronze' | 'iron' | 'silver' | 'gold' | 'diamond';
export type GuildVisibility = 'public' | 'private';
export type GuildMemberRole = 'owner' | 'admin' | 'moderator' | 'member';

export interface GuildSummary {
  guildId: string;
  name: string;
  description: string | null;
  imageRef: string | null;
  guildType: GuildType;
  level: GuildLevel;
  visibility: GuildVisibility;
  ownerUsername?: string | null;
  memberCount: number;
  lifetimeEarnings: string;
  status: string;
  createdAt: string;
}

export interface GuildDetailResponse extends GuildSummary {
  ownerId: string;
  maxMembers: number | null;
  settings?: Record<string, unknown>;
  myRole?: GuildMemberRole | null;
  updatedAt: string;
}

export interface GuildListResult {
  guilds: GuildSummary[];
  total: number;
  limit: number;
  offset: number;
}

export interface GuildMemberResponse {
  guildId: string;
  userId: string;
  username?: string | null;
  role: GuildMemberRole;
  memberSociat: string;
  joinedAt: string;
}

export interface GuildMemberListResult {
  members: GuildMemberResponse[];
  bans?: GuildBanResponse[];
  total: number;
  limit: number;
  offset: number;
}

export interface GuildInviteResponse {
  inviteId: string;
  guildId: string;
  inviteCode: string;
  createdBy?: string;
  maxUses: number | null;
  useCount: number;
  expiresAt: string | null;
  status: string;
  createdAt: string;
}

export interface GuildInviteInfo {
  inviteCode: string;
  expiresAt: string | null;
}

export interface GuildBanResponse {
  banId: string;
  guildId: string;
  userId: string;
  username?: string | null;
  bannedBy: string;
  bannedByUsername?: string | null;
  reason: string | null;
  createdAt: string;
}

export interface GuildActivityEntry {
  activityId: string;
  guildId: string;
  userId: string;
  username?: string | null;
  activityType: string;
  metadata: Record<string, unknown>;
  createdAt: string;
}

export interface GuildActivityResult {
  activities: GuildActivityEntry[];
  total: number;
  limit: number;
  offset: number;
}

export interface GuildTreasuryResponse {
  guildId: string;
  balance: string;
  lifetimeEarned: string;
  lifetimeDistributed: string;
  distributionRule: string;
  lastDistributionAt: string | null;
  updatedAt: string;
}

export interface GuildTreasuryTransactionResponse {
  transactionId: string;
  guildId: string;
  type: string;
  amount: string;
  balanceAfter: string;
  recipientId: string | null;
  description: string | null;
  metadata: Record<string, unknown>;
  createdAt: string;
}

export interface CreateGuildPayload {
  name: string;
  description?: string;
  imageRef?: string;
  guildType: GuildType;
  visibility: GuildVisibility;
}

export interface UpdateGuildPayload {
  name?: string;
  description?: string;
  imageRef?: string;
  visibility?: GuildVisibility;
  maxMembers?: number | null;
  settings?: Record<string, unknown>;
}

// ==========================================================================
// Builder Types
// ==========================================================================

export interface BuilderSessionSummary {
  sessionId: string;
  archId: string | null;
  currentName: string | null;
  status: 'active' | 'paused' | 'completed' | 'abandoned';
  updatedAt: string;
  totalCostCents: number;
}

export interface BuilderMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
  artifacts?: BuilderArtifact[];
}

export interface BuilderArtifact {
  type: 'ui_definition' | 'workflow' | 'entity_selection' | 'settings';
  content: unknown;
  description: string;
}

export interface BuilderSession {
  sessionId: string;
  userId: string;
  archId: string | null;
  templateId: string | null;
  conversationHistory: BuilderMessage[];
  currentName: string | null;
  currentDescription: string | null;
  currentUiDefinition: unknown | null;
  currentWorkflow: unknown | null;
  currentSettings: unknown | null;
  status: 'active' | 'paused' | 'completed' | 'abandoned';
  lastClaudeResponseAt: string | null;
  totalInputTokens: number;
  totalOutputTokens: number;
  totalCostCents: number;
  createdAt: string;
  updatedAt: string;
}

export interface BuilderTemplate {
  templateId: string;
  name: string;
  description: string | null;
  category: string | null;
  thumbnailRef: string | null;
  uiDefinition: unknown;
  workflow: unknown | null;
  defaultSettings: unknown | null;
  displayOrder: number;
  isFeatured: boolean;
}

export interface ArcheReviewSummary {
  reviewId: string;
  version: number;
  reviewerType: 'ai' | 'human';
  status: 'pending' | 'in_progress' | 'approved' | 'rejected';
  riskScore: number | null;
  createdAt: string;
  completedAt: string | null;
}

export interface ReviewFindingSummary {
  total: number;
  critical: number;
  high: number;
  medium: number;
  low: number;
}

export interface ReviewFinding {
  id: string;
  severity: 'critical' | 'high' | 'medium' | 'low';
  category: string;
  message: string;
  location?: string;
  suggestion?: string;
}

export interface ArcheReviewDetail {
  reviewId: string;
  archId: string;
  version: number;
  reviewerType: 'ai' | 'human';
  status: 'pending' | 'in_progress' | 'approved' | 'rejected';
  riskScore: number | null;
  securityFindings: {
    findings: ReviewFinding[];
    summary: ReviewFindingSummary;
  } | null;
  policyFindings: {
    findings: ReviewFinding[];
    summary: ReviewFindingSummary;
  } | null;
  qualityFindings: {
    findings: ReviewFinding[];
    summary: ReviewFindingSummary;
  } | null;
  reviewedBy: string | null;
  humanNotes: string | null;
  reviewCostCents: number | null;
  createdAt: string;
  completedAt: string | null;
}

export interface ClaudeMessageResponse {
  message: string;
  artifacts: BuilderArtifact[];
  tokensUsed: {
    input: number;
    output: number;
  };
  suggestedNextSteps?: string[];
  creditsCost: number;
  validationWarnings?: string[];
}

// ==========================================================================
// Contact Book Types
// ==========================================================================

export interface LabeledValue {
  value: string;
  label: string;
}

export interface ContactDetail {
  id: string;
  userId: string;
  categoryId: string | null;
  priorityId: string | null;
  firstName: string;
  lastName: string;
  emails: LabeledValue[];
  phones: LabeledValue[];
  company: string | null;
  title: string | null;
  notes: string | null;
  source: string | null;
  gatheredAt: string;
  tags: string[];
  metadata: Record<string, unknown>;
  status: 'active' | 'archived' | 'deleted';
  createdAt: string;
  updatedAt: string;
  categoryName?: string | null;
  categoryColor?: string | null;
  priorityName?: string | null;
  priorityColor?: string | null;
}

export interface ContactInput {
  firstName: string;
  lastName?: string;
  emails?: LabeledValue[];
  phones?: LabeledValue[];
  company?: string | null;
  title?: string | null;
  notes?: string | null;
  source?: string | null;
  gatheredAt?: string | null;
  categoryId?: string | null;
  priorityId?: string | null;
  tags?: string[];
  metadata?: Record<string, unknown>;
}

export interface ContactCategory {
  id: string;
  name: string;
  color: string;
  sortOrder: number;
  contactCount: number;
}

export interface ContactCategoryInput {
  name: string;
  color: string;
}

export interface ContactPriority {
  id: string;
  name: string;
  color: string;
  sortOrder: number;
  contactCount: number;
}

export interface ContactPriorityInput {
  name: string;
  color: string;
  sortOrder?: number;
}

export interface ContactAttachment {
  id: string;
  contactId: string;
  fileId: string;
  label: string | null;
  displayOrder: number;
  createdAt: string;
  fileName?: string;
  fileSize?: number;
  mimeType?: string;
}

// ==========================================================================
// DFE Inventory Types
// ==========================================================================

export type DfeFungibility = 'fungible' | 'non_fungible';
export type DfeTransferType =
  | 'transfer'
  | 'mint'
  | 'burn'
  | 'admin_grant'
  | 'legacy_import'
  | 'game_reward_mint'
  | 'game_reward_burn'
  | 'mining_mint'
  | 'mining_burn'
  | 'crafting_mint'
  | 'crafting_burn'
  | 'liquidity_mint'
  | 'liquidity_burn'
  | 'guild_mint';

export interface DfeDefinitionSummary {
  itemDefId: string;
  name: string;
  description: string | null;
  fungibility: DfeFungibility;
  thumbnailUri: string | null;
  imageUri: string | null;
  metadata: Record<string, unknown>;
  maxSupply: number | null;
  currentSupply: number;
  marketCategory: string;
  status: string;
  createdAt: string;
}

export interface DfeDefinitionDetail extends DfeDefinitionSummary {
  createdBy: string;
  updatedAt: string;
}

export interface DfeDefinitionListResult {
  definitions: DfeDefinitionSummary[];
  total: number;
  limit: number;
  offset: number;
}

export interface DfeInventoryEntry {
  itemDefId: string;
  name: string;
  description: string | null;
  fungibility: DfeFungibility;
  thumbnailUri: string | null;
  imageUri: string | null;
  definitionMetadata: Record<string, unknown>;
  category: string;
  position: string;
  balanceId: string | null;
  quantity: number | null;
  itemId: string | null;
  instanceNumber: number | null;
  itemMetadata: Record<string, unknown> | null;
  acquiredAt: string | null;
}

export interface DfeInventoryListResult {
  items: DfeInventoryEntry[];
  total: number;
  limit: number;
  offset: number;
}

export interface DfeInventoryItemDetail {
  itemId: string;
  itemDefId: string;
  ownerId: string;
  instanceNumber: number;
  metadata: Record<string, unknown>;
  category: string;
  position: string;
  acquiredAt: string;
  status: string;
  createdAt: string;
  updatedAt: string;
}

export interface DfeTransferEntry {
  transferId: string;
  itemDefId: string;
  itemId: string | null;
  fromUserId: string | null;
  toUserId: string | null;
  quantity: number;
  transferType: DfeTransferType;
  notes: string | null;
  createdAt: string;
}

export interface DfeTransferListResult {
  transfers: DfeTransferEntry[];
  total: number;
  limit: number;
  offset: number;
}

export interface DfeEnrichedTransferEntry extends DfeTransferEntry {
  fromUsername: string | null;
  toUsername: string | null;
  itemName: string | null;
}

export interface DfeEnrichedTransferListResult {
  transfers: DfeEnrichedTransferEntry[];
  total: number;
  limit: number;
  offset: number;
}

export interface DfeMintResult {
  success: boolean;
  items?: Array<{ itemId: string; instanceNumber: number; metadata: Record<string, unknown> }>;
  balance?: { balanceId: string; quantity: number };
}

// DFE Legacy Import types
//
// `itemsImported`/`itemsSkipped`/`skippedItems` are cross-source totals
// (DFE + LitSquad combined); the per-source `dfe*`/`litsquad*` counters
// drive the split view in the ImportResultDisplay UI.
export interface DfeLegacyImportResult {
  importId: string;
  legacyServer: string;
  legacyUsername: string;
  itemsImported: number;
  itemsSkipped: number;
  skippedItems: string[];
  dfeItemsImported: number;
  dfeItemsSkipped: number;
  dfeSkippedItems: string[];
  litsquadItemsImported: number;
  litsquadItemsSkipped: number;
  litsquadSkippedItems: string[];
  importStatus: string;
  errorMessage: string | null;
}

export interface DfeLegacyImportEntry extends DfeLegacyImportResult {
  createdAt: string;
  updatedAt: string | null;
}

export interface AdminDfeLegacyImportEntry extends DfeLegacyImportEntry {
  fiasUserId: string;
  fiasUsername: string | null;
  fiasDisplayName: string | null;
}

export interface AdminDfeLegacyImportListResult {
  imports: AdminDfeLegacyImportEntry[];
  total: number;
  limit: number;
  offset: number;
}

// ===========================================================================
// LitSquad (arche_litsquad) — NFT-only in v1
// ===========================================================================

export interface LitSquadDefinitionSummary {
  itemDefId: string;
  name: string;
  description: string | null;
  fungibility: DfeFungibility;
  thumbnailUri: string | null;
  imageUri: string | null;
  metadata: Record<string, unknown>;
  maxSupply: number | null;
  currentSupply: number;
  status: string;
  createdAt: string;
}

export interface LitSquadDefinitionDetail extends LitSquadDefinitionSummary {
  createdBy: string;
  updatedAt: string;
}

export interface LitSquadDefinitionListResult {
  definitions: LitSquadDefinitionSummary[];
  total: number;
  limit: number;
  offset: number;
}

export interface LitSquadInventoryEntry {
  itemDefId: string;
  /** Species name from the definition (e.g. "LitSquad Elephant"). */
  speciesName: string;
  description: string | null;
  fungibility: DfeFungibility;
  definitionThumbnailUri: string | null;
  definitionImageUri: string | null;
  definitionMetadata: Record<string, unknown>;
  category: string;
  position: string;
  itemId: string | null;
  instanceNumber: number | null;
  /** Per-item display name (falls back to speciesName if null). */
  displayName: string | null;
  /** Per-item thumbnail URL (falls back to definitionThumbnailUri if null). */
  thumbnailUri: string | null;
  itemMetadata: Record<string, unknown> | null;
  acquiredAt: string | null;
}

export interface LitSquadInventoryListResult {
  items: LitSquadInventoryEntry[];
  total: number;
  limit: number;
  offset: number;
}

export interface LitSquadInventoryItemDetail {
  itemId: string;
  itemDefId: string;
  ownerId: string;
  instanceNumber: number;
  displayName: string | null;
  thumbnailUri: string | null;
  metadata: Record<string, unknown>;
  category: string;
  position: string;
  acquiredAt: string;
  status: string;
  createdAt: string;
  updatedAt: string;
}

export interface LitSquadEnrichedTransferEntry {
  transferId: string;
  itemDefId: string;
  itemName: string | null;
  itemId: string | null;
  fromUserId: string | null;
  fromUsername: string | null;
  toUserId: string | null;
  toUsername: string | null;
  quantity: number;
  transferType: string;
  notes: string | null;
  createdAt: string;
}

export interface LitSquadEnrichedTransferListResult {
  transfers: LitSquadEnrichedTransferEntry[];
  total: number;
  limit: number;
  offset: number;
}

export interface CreateLitSquadDefinitionInput {
  name: string;
  description?: string;
  fungibility: string;
  thumbnailUri?: string;
  imageUri?: string;
  metadata?: Record<string, unknown>;
  maxSupply?: number | null;
  status?: string;
}

// ==========================================================================
// LitSquad Accessory Crafting
//
// These types MUST stay in sync with `packages/db-types/src/litsquad-crafting.ts`.
// The webapp-client package deliberately does not depend on `@fias/db-types`
// (that package ships Node-only deps), so the shapes are duplicated here.
// When you change one, change both — the backend tests and frontend tests
// both assert on the wire shape.
// ==========================================================================

export type LitSquadCraftSlotName =
  | 'ficiant'
  | 'classCoin'
  | 'lituCloth'
  | 'mmc'
  | 'expertise'
  | 'themeCoin';

export interface LitSquadCraftInput {
  ficiant: string;
  classCoin: string;
  lituCloth: string;
  mmc?: string;
  expertise?: string;
  themeCoin?: string;
}

export interface LitSquadCraftIngredient {
  /**
   * `null` when the user has no stock of this named ingredient. Callers
   * that render zero-count entries should display the name but must not
   * send the null back as a craft input.
   */
  itemDefId: string | null;
  name: string;
  thumbnailUri: string | null;
  userCount: number;
}

export interface LitSquadCraftSlot {
  slot: LitSquadCraftSlotName;
  required: boolean;
  quantity: number;
  eligibleIngredients: LitSquadCraftIngredient[];
}

export interface LitSquadCraftIngredientsResult {
  slots: LitSquadCraftSlot[];
}

export interface LitSquadCraftPreviewItem {
  identifier: string;
  partialName: string;
  displayName: string;
  thumbnailUri: string;
  group: string;
  rarity: 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary';
  avatarItemGroup: string;
  themeCoin: string;
  probability: number;
}

export interface LitSquadCraftPreviewResult {
  species: string;
  possibleGroups: string[];
  ficiantLevel: number;
  useBetterOdds: boolean;
  rarityLadder: Array<{ rarity: string; probability: number }>;
  possibleOutcomes: LitSquadCraftPreviewItem[];
  themeCoinApplies: boolean;
  /**
   * Probability that a craft attempt will abort pre-burn because the
   * rolled rarity has no matching accessory in the possible groups.
   * `possibleOutcomes[].probability + failureProbability === 1`.
   */
  failureProbability: number;
}

export interface LitSquadCraftResult {
  itemId: string;
  itemDefId: string;
  species: string;
  displayName: string;
  thumbnailUri: string;
  group: string;
  rarity: 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary';
  identifier: string;
  avatarItemGroup: string;
  metadata: Record<string, unknown>;
}

export interface UpdateLitSquadDefinitionInput {
  name?: string;
  description?: string | null;
  thumbnailUri?: string | null;
  imageUri?: string | null;
  metadata?: Record<string, unknown>;
  maxSupply?: number | null;
  status?: string;
}

export interface TransferLitSquadItemInput {
  toUserId?: string;
  toUsername?: string;
  itemId: string;
  notes?: string;
  category?: string;
  position?: string;
}

export interface LitSquadMintResult {
  success: boolean;
  items: Array<{
    itemId: string;
    instanceNumber: number;
    displayName: string | null;
    thumbnailUri: string | null;
    metadata: Record<string, unknown>;
  }>;
}

export interface CreateDfeDefinitionInput {
  name: string;
  description?: string;
  fungibility: string;
  thumbnailUri?: string;
  imageUri?: string;
  metadata?: Record<string, unknown>;
  maxSupply?: number | null;
  marketCategory?: string;
  status?: string;
}

export interface UpdateDfeDefinitionInput {
  name?: string;
  description?: string | null;
  thumbnailUri?: string | null;
  imageUri?: string | null;
  metadata?: Record<string, unknown>;
  maxSupply?: number | null;
  marketCategory?: string;
  status?: string;
}

export interface TransferDfeItemInput {
  toUserId?: string;
  toUsername?: string;
  itemDefId?: string;
  quantity?: number;
  itemId?: string;
  notes?: string;
  category?: string;
  position?: string;
}

export interface DfePositionConflict {
  category: string;
  position: string;
  items: { itemDefId: string; name: string }[];
}

// ==========================================================================
// Game Rewards Types
// ==========================================================================

export type GrSubscriptionTier = 'bronze' | 'silver' | 'gold';

export interface GrSubscriptionResponse {
  subscriptionId: string;
  tier: GrSubscriptionTier;
  startsAt: string;
  expiresAt: string;
  purchasedVia?: string;
  isActive?: boolean;
  userId?: string;
  username?: string | null;
  metadata?: Record<string, unknown>;
  createdAt?: string;
}

export interface GrTierResponse {
  tierId: string;
  tier: GrSubscriptionTier;
  displayName: string;
  currencyItemDefId: string;
  currencyItemName?: string | null;
  priceAmount: number;
  durationDays: number;
  description: string | null;
}

export interface GrTierListResult {
  tiers: GrTierResponse[];
  total: number;
}

export interface GrSubscriptionHistoryResult {
  subscriptions: GrSubscriptionResponse[];
  total: number;
  limit: number;
  offset: number;
}

// LitCraft Store Types
export interface LitCraftStoreProduct {
  productId: string;
  productIdentifier: string;
  productType: string;
  name: string;
  description: string | null;
  priceCredits: number;
  priceUsd: string;
  metadata: Record<string, unknown>;
  subscriptionPeriod?: string;
}

export interface LitCraftStoreResponse {
  gemBundles: LitCraftStoreProduct[];
  subscriptions: LitCraftStoreProduct[];
}

export interface LitCraftStorePurchaseResponse {
  success: boolean;
  purchaseId: string;
  priceCredits: number;
  newBalance: number;
  gemsAwarded?: number;
  subscription?: {
    subscriptionId: string;
    tier: string;
    expiresAt: string;
    bonusDays: number;
  };
  product: {
    productIdentifier: string;
    productType: string;
    name: string;
  };
}

export interface GrRewardDelivered {
  rewardType: string;
  itemDefId: string;
  rawAmount: number;
  multiplier: number;
  finalAmount: number;
}

export interface GrRewardLogEntry {
  logId: string;
  gameConfigId: string;
  /**
   * Resolved keyword/name of the claim (gr_game_configs.game_name). Surfaced
   * server-side via JOIN so the admin audit log can render the human-readable
   * game name. Null when the underlying config row was deleted.
   */
  gameName?: string | null;
  /** Resolved display name (gr_game_configs.display_name). Null if missing. */
  displayName?: string | null;
  gameEvent: string;
  gameScore: number;
  subscriptionTier: GrSubscriptionTier | null;
  rewardsDelivered: GrRewardDelivered[];
  createdAt: string;
  userId?: string;
  /** Resolved server-side via batchGetUsernames. Null if the user record is missing. */
  username?: string | null;
  requestNonce?: string | null;
  ipAddress?: string | null;
}

export interface GrRewardLogListResult {
  entries: GrRewardLogEntry[];
  total: number;
  limit: number;
  offset: number;
}

export type GrLeaderboardType = 'high_score' | 'low_score' | 'none';

export type GrRewardMode = 'standard' | 'item_list' | 'recipe';

export type GrItemListFormat = 'repeated' | 'counted';

export interface GrClaimCostRule {
  pattern: 'exact' | 'prefix';
  names?: string[];
  value?: string;
  currency: string;
  amount: number;
}

export type GrGameConfigReviewStatus = 'pending' | 'approved' | 'rejected';

export interface GrGameConfigDetail {
  gameConfigId: string;
  gameName: string;
  displayName: string;
  description: string | null;
  apiKeyPrefix: string;
  minScore: number;
  maxScore: number;
  leaderboardType: GrLeaderboardType;
  rewardMode: GrRewardMode;
  itemListFormat?: GrItemListFormat;
  allowedItems: string[] | null;
  claimCosts: GrClaimCostRule[] | null;
  claimStateKeyword: string | null;
  allowedItemPrefixes: string[] | null;
  rewardLookupEvent?: string | null;
  isActive: boolean;
  /** Server-side enforced: claims for non-'approved' configs grant nothing. */
  reviewStatus: GrGameConfigReviewStatus;
  /** Plugin arche id this game is linked to (1:1, platform-admin only). */
  linkedArcheId?: string | null;
  /** When set, leaderboard writes redirect to this game (single-hop only). */
  leaderboardTargetGameId?: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  /**
   * Soft-delete metadata. When `deletedAt` is non-null the config is
   * tombstoned: hidden from default admin lists and the claim auth path,
   * but still surfaced when `includeDeleted` or `onlyDeleted` is set.
   */
  deletedAt?: string | null;
  deletedBy?: string | null;
}

export interface GrGameConfigCreateResult {
  gameConfigId: string;
  gameName: string;
  displayName: string;
  apiKey: string;
  apiKeyPrefix: string;
}

export interface GrGameConfigListResult {
  gameConfigs: GrGameConfigDetail[];
  total: number;
  limit: number;
  offset: number;
}

export interface GrStepDefinition {
  minScore: number;
  maxScore: number;
  reward: number;
}

export interface GrRewardRuleResponse {
  ruleId: string;
  gameConfigId: string;
  gameEvent: string;
  rewardType: string;
  itemDefId: string;
  m: number;
  b: number;
  maxRewardCount: number;
  bronzeMultiplier: number;
  silverMultiplier: number;
  goldMultiplier: number;
  steps: GrStepDefinition[] | null;
  baseMultiplier: number;
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface GrRewardRuleListResult {
  rules: GrRewardRuleResponse[];
  total: number;
  limit: number;
  offset: number;
}

export type GrRejectionTrustLevel = 'internal' | 'plugin' | 'oauth';

export interface GrClaimRejectionEntry {
  rejectionId: string;
  occurredAt: string;
  trustLevel: GrRejectionTrustLevel;
  archeId: string | null;
  oauthAppId: string | null;
  userId: string | null;
  /** Resolved server-side via batchGetUsernames. Null if userId is missing or unknown. */
  username: string | null;
  attemptedGameName: string | null;
  gameConfigId: string | null;
  gameEvent: string | null;
  gameScore: number | null;
  attemptedItemNames: string[] | null;
  rejectionCode: string;
  rejectionMessage: string;
}

export interface GrClaimRejectionListResult {
  rejections: GrClaimRejectionEntry[];
  total: number;
  limit: number;
  offset: number;
}

export interface GrRewardTypeLimitResponse {
  limitId: string;
  rewardType: string;
  minSecondsBetweenRewards: number;
  maxRewardsPerDay: number;
  createdAt?: string;
}

export interface GrRewardTypeLimitListResult {
  limits: GrRewardTypeLimitResponse[];
  total: number;
  limit: number;
  offset: number;
}

export interface GrTierConfigResponse {
  tierId: string;
  tier: GrSubscriptionTier;
  displayName: string;
  priceAmount: number;
  durationDays: number;
  isActive?: boolean;
  updatedAt?: string;
}

export interface GrAdminTierListResult {
  tiers: Array<GrTierResponse & { isActive: boolean; createdAt: string }>;
  total: number;
}

export interface GrAdminSubscriptionListResult {
  subscriptions: GrSubscriptionResponse[];
  total: number;
  limit: number;
  offset: number;
}

export interface GrTimePlayedAggregates {
  totalSeconds: number;
  totalSessions: number;
  games: Array<{
    gameConfigId: string;
    gameName: string;
    displayName: string;
    totalSeconds: number;
    sessionCount: number;
  }>;
}

export interface GrLeaderboardEntryResponse {
  rank: number;
  userId: string;
  username: string | null;
  bestScore: number;
  playCount: number;
  lastPlayedAt: string;
}

export interface GrLeaderboardResult {
  entries: GrLeaderboardEntryResponse[];
  total: number;
  leaderboardType: GrLeaderboardType;
  limit: number;
  offset: number;
  myRank?: { rank: number; bestScore: number; playCount: number } | null;
}

export interface GrUserBestScoresResult {
  scores: Array<{
    gameConfigId: string;
    gameName: string;
    displayName: string;
    gameEvent: string;
    bestScore: number;
    playCount: number;
    lastPlayedAt: string;
    leaderboardType: GrLeaderboardType;
  }>;
  total: number;
  limit: number;
  offset: number;
}

export interface GrLeaderboardGameInfo {
  gameConfigId: string;
  gameName: string;
  displayName: string;
  leaderboardType: GrLeaderboardType;
}

// ==========================================================================
// OAuth / Connected Apps Types
// ==========================================================================

export interface OAuthAuthorizeInfo {
  app: {
    name: string;
    description: string | null;
    logoUrl: string | null;
  };
  scopes: Array<{
    scope: string;
    description: string;
  }>;
  redirectDomain: string;
  existingConsent: boolean;
}

export interface ConnectedApp {
  appId: string;
  appName: string;
  appLogoUrl: string | null;
  scopes: { scope: string; description: string }[];
  grantedAt: string;
}

// ==========================================================================
// DFE Game State Types
// ==========================================================================

export interface GameStateResponse {
  gameStateId: string;
  keyword: string;
  dataType: string;
  data?: string;
  dataBinary?: string;
  version: number;
  dataSizeBytes: number;
  checksum?: string;
  metadata: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

// ==========================================================================
// Polls Types
// ==========================================================================

export interface PollQuestionPayload {
  questionId: string;
  text: string;
  type: string;
  options?: string[];
  required: boolean;
}

export interface CreatePollPayload {
  title: string;
  description?: string;
  questions: PollQuestionPayload[];
  resultsVisibility?: string;
  closesAt?: string;
}

export interface UpdatePollPayload {
  title?: string;
  description?: string | null;
  questions?: PollQuestionPayload[];
  resultsVisibility?: string;
  closesAt?: string | null;
}

export interface PollAnswerPayload {
  questionId: string;
  selectedOptions?: number[];
  textAnswer?: string;
}

export interface PollDetailResponse {
  pollId: string;
  title: string;
  description: string | null;
  questions: PollQuestionPayload[];
  resultsVisibility: string;
  status: string;
  closesAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface GameStateListResult {
  states: GameStateResponse[];
  total: number;
  limit: number;
  offset: number;
}

export interface PollFullDetailResponse extends PollDetailResponse {
  createdBy: string;
  responseCount: number;
  hasResponded: boolean;
  isCreator: boolean;
}

export interface PollListResult {
  polls: Array<{
    pollId: string;
    title: string;
    status: string;
    resultsVisibility: string;
    closesAt: string | null;
    createdAt: string;
    updatedAt: string;
  }>;
  total: number;
  limit: number;
  offset: number;
}

export interface GameStateAuditEntry {
  auditId: string;
  gameStateId: string;
  userId: string;
  /** Resolved server-side via batchGetUsernames; null when the user has been
   * deleted from the user-pool. UI falls back to userId in that case. */
  username: string | null;
  keyword: string;
  action: string;
  oldVersion: number | null;
  newVersion: number | null;
  dataSizeBytes: number | null;
  actorId: string;
  /** Resolved server-side; null fallback semantics same as `username`. */
  actorUsername: string | null;
  /** Plugin arche that drove the mutation, if any. */
  viaArcheId: string | null;
  createdAt: string;
}

export interface GameStateAuditLogResult {
  entries: GameStateAuditEntry[];
  total: number;
  limit: number;
  offset: number;
}

// ==========================================================================
// DFE Game State — Plugin Resource Grant types
// ==========================================================================

export type GsResourceType = 'state' | 'task';
export type GsAccessLevel = 'read' | 'read_write';
export type GsGrantReviewStatus = 'pending' | 'approved' | 'rejected';

export interface GameStateGrant {
  archeId: string;
  /** Display name of the arche; null when the arche has been deleted. */
  archeName: string | null;
  resourceType: GsResourceType;
  resourceName: string;
  access: GsAccessLevel;
  createdBy: string;
  /**
   * Username of the requestor (resolved from `createdBy`). For admin-
   * created grants this is the admin who pre-approved access; for auto-
   * requested grants it is the end user whose plugin invocation triggered
   * the request. Null when the user has been deleted.
   */
  createdByUsername: string | null;
  createdAt: string;
  updatedAt: string;
  reviewStatus: GsGrantReviewStatus;
  reviewedBy: string | null;
  /** Username of the admin who approved/rejected; null for pending grants. */
  reviewedByUsername: string | null;
  reviewedAt: string | null;
  reviewReason: string | null;
}

export interface GameStateGrantListResult {
  grants: GameStateGrant[];
  total: number;
  limit: number;
  offset: number;
}

export interface SetGameStateGrantResult {
  grant: GameStateGrant;
  created: boolean;
  accessChanged: boolean;
  reviewReset: boolean;
}

export interface ApproveRejectGameStateGrantResult {
  grant: GameStateGrant;
  statusChanged: boolean;
}

// ==========================================================================
// Admin User Types
// ==========================================================================

export interface InviteCodeResponse {
  code: string;
  status: 'active' | 'used' | 'expired' | 'revoked';
  createdAt: string;
  expiresAt: string | null;
  usedAt?: string;
  usedByEmail?: string;
  usedByUserId?: string;
  grantRoles: string[];
  metadata?: { description?: string; notes?: string; createdBy?: string; batchId?: string };
}

export interface AdminUserLookupResult {
  userId: string;
  username: string;
  roles: string[];
  status: string;
  createdAt: string;
}

export interface PollResponseResult {
  responseId: string;
  pollId: string;
  answers: PollAnswerPayload[];
  submittedAt: string;
}

export interface PollQuestionResultData {
  questionId: string;
  text: string;
  type: string;
  options?: string[];
  optionCounts?: number[];
  textAnswers?: string[];
  totalResponses: number;
}

export interface PollResultsResponse {
  pollId: string;
  title: string;
  totalResponses: number;
  questions: PollQuestionResultData[];
}

export interface PollPublicViewResponse {
  pollId: string;
  title: string;
  description: string | null;
  questions: PollQuestionPayload[];
  resultsVisibility: string;
  status: string;
  closesAt: string | null;
  responseCount: number;
  createdAt: string;
}

// ==========================================================================
// Fiardle Types
// ==========================================================================

export interface FiardleGuessData {
  word: string;
  result: ('green' | 'yellow' | 'gray')[];
}

export interface FiardleGameResponse {
  gameId: string;
  userId: string;
  guesses: FiardleGuessData[];
  status: 'in_progress' | 'won' | 'lost';
  answer?: string;
  completedAt: string | null;
  createdAt: string;
}

export interface FiardleGuessResponse {
  guess: FiardleGuessData;
  game: FiardleGameResponse;
  /**
   * Rewards minted by the game-rewards pipeline when the game ended on this
   * guess. Only populated when game.status is 'won' or 'lost'. Null on
   * in-progress games or when the claim failed (gameplay is unaffected).
   * Already filtered to entries with finalAmount > 0.
   */
  rewards: GrRewardDelivered[] | null;
}

export interface FiardleGameHistoryResponse {
  games: FiardleGameResponse[];
  total: number;
  limit: number;
  offset: number;
}

export interface FiardleStatsResponse {
  gamesPlayed: number;
  gamesWon: number;
  currentStreak: number;
  maxStreak: number;
  winRate: number;
  guessDistribution: Record<string, number>;
}

export interface FiardleLeaderboardEntry {
  userId: string;
  username: string | null;
  gamesPlayed: number;
  gamesWon: number;
  winRate: number;
  currentStreak: number;
  maxStreak: number;
}

export interface FiardleLeaderboardResponse {
  entries: FiardleLeaderboardEntry[];
  total: number;
  limit: number;
  offset: number;
}

// ==========================================================================
// Social Media Types
// ==========================================================================

export type SocialPlatform =
  | 'twitter'
  | 'linkedin'
  | 'facebook'
  | 'instagram'
  | 'tiktok'
  | 'youtube'
  | 'telegram';
export type SocialAccountStatus = 'active' | 'error' | 'revoked' | 'expired';
export type SocialPostStatus =
  | 'draft'
  | 'scheduled'
  | 'publishing'
  | 'published'
  | 'failed'
  | 'cancelled';

export interface BasePostSettings {
  taggedPeople?: { id: string; name: string }[];
  location?: { id: string; name: string };
}

export interface TwitterPollSettings {
  options: string[];
  durationMinutes: number;
}

/** A single tweet within a thread, with optional per-tweet media */
export interface ThreadTweet {
  text: string;
  /** fileIds of media to attach to this specific tweet (independent of parent post media) */
  mediaFileIds?: string[];
}

export interface TwitterPostSettings extends BasePostSettings {
  format: 'tweet' | 'poll' | 'reply' | 'thread';
  includeLink: boolean;
  tagUser?: string;
  replyToUrl?: string;
  poll?: TwitterPollSettings;
  /** Individual tweets for thread format — each may have its own media */
  threadTweets?: ThreadTweet[];
}

export interface InstagramPostSettings extends BasePostSettings {
  format: 'post' | 'reel' | 'carousel' | 'story';
  hashtagsInFirstComment?: boolean;
  collaboratorTag?: string;
  locationName?: string;
  coverImageFileId?: string;
}

export interface FacebookPostSettings extends BasePostSettings {
  format: 'post' | 'reel' | 'story' | 'event';
  includeLink: boolean;
  audience?: 'public' | 'friends' | 'custom';
}

export interface LinkedInMention {
  name: string;
  urn: string;
  type: 'organization';
}

export interface LinkedInPostSettings extends BasePostSettings {
  includeLink: boolean;
  postType?: 'post' | 'article' | 'poll' | 'event';
  tagCompanyPage?: string;
  /** LinkedIn post visibility — defaults to PUBLIC per LinkedIn spec */
  visibility?: 'PUBLIC' | 'CONNECTIONS' | 'LOGGED_IN';
  /** Inline organization mentions — each name must appear verbatim in the post text */
  mentions?: LinkedInMention[];
  /** File IDs from the post's media pool to include in this LinkedIn post (up to 9 images or 1 video) */
  selectedMediaFileIds?: string[];
}

export interface TikTokPostSettings extends BasePostSettings {
  postType?: 'video' | 'photo_mode' | 'live';
  whoCanView?: 'everyone' | 'friends' | 'private';
  allowDuets?: boolean;
  allowStitch?: boolean;
  disclosePaidPartnership?: boolean;
  selectedVideoFileId?: string;
}

export type PlatformPostSettings =
  | TwitterPostSettings
  | InstagramPostSettings
  | FacebookPostSettings
  | LinkedInPostSettings;

export interface AdaptedPlatformContent {
  text: string;
  hashtags: string[];
  characterCount: number;
  suggestions: string[];
  adaptedAt: string;
  postType?: string;
  settings?: PlatformPostSettings;
}

export interface PublishAttemptResult {
  success: boolean;
  accountId?: string;
  platformPostId?: string;
  url?: string;
  error?: string;
  errorCode?: string;
}

export interface SocialMediaPost {
  postId: string;
  userId: string;
  originalContent: string;
  platformContent: Record<string, AdaptedPlatformContent>;
  targetAccountIds: string[];
  mediaFileIds: string[];
  status: SocialPostStatus;
  publishAt: string;
  publishResults: Partial<Record<SocialPlatform, PublishAttemptResult>>;
  hasAiContent: boolean;
  timezone: string;
  failureCount: number;
  title: string | null;
  notes: string | null;
  tags: string[];
  link: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface SocialMediaAccountPublic {
  accountId: string;
  userId: string;
  platform: SocialPlatform;
  platformUserId: string;
  platformUsername: string | null;
  platformName: string | null;
  platformAvatarUrl: string | null;
  status: SocialAccountStatus;
  lastError: string | null;
  tokenExpiresAt: string | null;
  scopes: string[];
  createdAt: string;
  updatedAt: string;
}

export interface SocialPostSummary {
  postId: string;
  userId: string;
  status: SocialPostStatus;
  publishAt: string;
  hasAiContent: boolean;
  failureCount: number;
  targetAccountIds: string[];
  title: string | null;
  tags: string[];
  createdAt: string;
  updatedAt: string;
}

export interface SocialMediaPublishLogEntry {
  logId: string;
  postId: string;
  accountId: string;
  platform: SocialPlatform;
  attemptAt: string;
  success: boolean;
  platformPostId: string | null;
  platformPostUrl: string | null;
  errorCode: string | null;
  errorMessage: string | null;
}

export interface SocialMediaPostCounts {
  total: number;
  draft: number;
  scheduled: number;
  published: number;
  failed: number;
  cancelled: number;
  publishing: number;
}

export interface SocialMediaDailyCount {
  /** YYYY-MM-DD */
  date: string;
  count: number;
}

// ==========================================================================
// Social Media Analytics Types
// ==========================================================================

export type SocialAnalyticsRange = '7d' | '30d' | '90d' | '6m' | '1y' | '1y+';

export interface AudienceTimePoint {
  /** YYYY-MM-DD */
  date: string;
  byPlatform: Partial<Record<SocialPlatform, number>>;
}

export interface SocialEngagementByDay {
  /** YYYY-MM-DD */
  date: string;
  engagements: number;
}

/** Per-platform aggregate stats for the selected range */
export interface PlatformAnalyticsStat {
  platform: SocialPlatform;
  /** @handle for the connected account */
  platformUsername: string | null;
  /** Current audience size (followers/page likes/etc); null if no snapshots yet */
  audienceSize: number | null;
  /** % change over the range; null if insufficient data */
  audienceGrowthPct: number | null;
  totalPosts: number;
  /** null = platform does not expose this metric */
  likes: number | null;
  comments: number | null;
  shares: number | null;
  views: number | null;
}

/** Per-post engagement snapshot (most recent for the range) */
export interface PostMediaItem {
  fileId: string;
  url: string;
  mimeType: string | null;
}

export interface PostEngagementRow {
  postId: string;
  platform: SocialPlatform;
  platformPostId: string;
  /** The post copy / caption text */
  content: string;
  /** Resolved media attachments (image or video URLs) */
  mediaItems: PostMediaItem[];
  /** null = platform does not expose this metric */
  likes: number | null;
  comments: number | null;
  shares: number | null;
  views: number | null;
  publishedAt: string; // ISO timestamp
  fetchedAt: string; // ISO timestamp
}

export interface SocialAnalyticsResponse {
  platforms: PlatformAnalyticsStat[];
  audienceTimeSeries: AudienceTimePoint[];
  engagementByDay: SocialEngagementByDay[];
  postEngagement: PostEngagementRow[];
  postsHeatmap: Array<{ date: string; count: number }>;
  generatedAt: string; // ISO timestamp
}

// ==========================================================================
// Content Inspiration Types
// ==========================================================================

export type InspirationPlatform = 'twitter' | 'instagram' | 'tiktok' | 'rss' | 'manual';
export type ContentStatus = 'new' | 'reviewed' | 'saved' | 'used' | 'discarded';
export type EngagementScoreType = 'full' | 'partial' | 'estimated';
export type InspirationSourceType = 'competitor' | 'rss' | 'manual';

export interface UnifiedContentItem {
  contentId: string;
  userId: string;
  sourceType: InspirationSourceType;
  sourceId: string;
  platform: InspirationPlatform;
  author: string;
  contentPreview: string;
  draftPostId: string | null;
  mediaUrl: string | null;
  externalId: string;
  externalUrl: string | null;
  likes: number | null;
  comments: number | null;
  shares: number | null;
  views: number | null;
  engagementScore: number;
  scoreType: EngagementScoreType;
  status: ContentStatus;
  isFavorited: boolean;
  usedCount: number;
  pillarTag: string | null;
  postedAt: string | null;
  fetchedAt: string;
  createdAt: string;
  updatedAt: string;
}

export interface SocialMediaCompetitorItem {
  competitorId: string;
  platform: InspirationPlatform;
  username: string;
  /** Platform-native user ID (e.g. Twitter/X numeric user ID). NULL until resolved on first fetch. */
  platformUserId: string | null;
  displayName: string | null;
  avatarUrl: string | null;
  isEnabled: boolean;
  fetchIntervalHours: number;
  lastFetchedAt: string | null;
  fetchStatus: 'idle' | 'fetching' | 'error';
  fetchError: string | null;
  fetchStartedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface SocialMediaRssFeedItem {
  feedId: string;
  title: string;
  url: string;
  isEnabled: boolean;
  fetchIntervalHours: number;
  lastFetchedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface MediaAttachment {
  /** Temporary ID during upload, then the real fileId after confirm */
  fileId: string;
  fileName: string;
  mimeType: string;
  /** Blob URL for local preview (or presigned URL once uploaded) */
  previewUrl: string;
  sizeBytes: number;
  uploadState: 'uploading' | 'done' | 'error';
  errorMessage?: string;
}

// ==========================================================================
// Calendar Types
// ==========================================================================

export interface CalendarCollectionDetail {
  id: string;
  userId: string;
  name: string;
  description: string;
  color: string;
  timezone: string;
  isDefault: boolean;
  isVisible: boolean;
  displayOrder: number;
  createdAt: string;
  updatedAt: string;
  eventCount?: number;
}

export interface CalendarCollectionInput {
  name: string;
  description?: string;
  color?: string;
  timezone?: string;
  isDefault?: boolean;
  isVisible?: boolean;
}

export interface CalendarEventDetail {
  id: string;
  calendarId: string;
  userId: string;
  title: string;
  description: string;
  location: string;
  startAt: string;
  endAt: string;
  isAllDay: boolean;
  timezone: string;
  status: 'confirmed' | 'tentative' | 'cancelled';
  color: string | null;
  url: string;
  isRecurring: boolean;
  rrule: string | null;
  reminders: CalendarEventReminder[];
  tags: string[];
  metadata: Record<string, unknown>;
  icalUid: string | null;
  originalEventId: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  calendarName?: string;
  calendarColor?: string;
}

export interface CalendarEventCreateInput {
  calendarId: string;
  title: string;
  description?: string;
  location?: string;
  startAt: string;
  endAt: string;
  isAllDay?: boolean;
  timezone?: string;
  status?: 'confirmed' | 'tentative' | 'cancelled';
  color?: string | null;
  url?: string;
  isRecurring?: boolean;
  rrule?: string | null;
  reminders?: CalendarEventReminder[];
  tags?: string[];
  metadata?: Record<string, unknown>;
}

export interface CalendarEventInstance {
  id: string;
  eventId: string;
  calendarId: string;
  title: string;
  description: string;
  location: string;
  startAt: string;
  endAt: string;
  isAllDay: boolean;
  timezone: string;
  status: 'confirmed' | 'tentative' | 'cancelled';
  color: string | null;
  url: string;
  isRecurring: boolean;
  rrule: string | null;
  reminders: CalendarEventReminder[];
  tags: string[];
  calendarName?: string;
  calendarColor?: string;
  isException: boolean;
  originalStartAt?: string;
  metadata?: Record<string, unknown>;
}

export interface CalendarEventReminder {
  type: 'notification' | 'email';
  minutesBefore: number;
}

export interface CalendarParsedImportResult {
  events: CalendarImportedEvent[];
  warnings: string[];
  errors: string[];
  calendarName?: string;
  calendarColor?: string;
  calendarTimezone?: string;
  totalParsed: number;
}

export interface CalendarImportedEvent extends CalendarEventCreateInput {
  originalSource?: string;
  exdates?: string[];
  icalUid?: string;
}

export interface CalendarImportConfirmRequest {
  calendarId: string;
  events: CalendarImportedEvent[];
  duplicateStrategy: 'skip' | 'overwrite' | 'create_new';
}

export interface CalendarImportConfirmResult {
  imported: number;
  skipped: number;
  updated: number;
  errors: string[];
}

export interface ParsedEventFields {
  title: string;
  startAt?: string;
  endAt?: string;
  isAllDay?: boolean;
  location?: string;
  description?: string;
  isRecurring?: boolean;
  rrule?: string;
  confidence: number;
}

export interface CalendarEventTemplateDetail {
  id: string;
  userId: string;
  name: string;
  title: string;
  description: string;
  location: string;
  durationMinutes: number;
  isAllDay: boolean;
  status: 'confirmed' | 'tentative' | 'cancelled';
  color: string | null;
  url: string;
  calendarId: string | null;
  isRecurring: boolean;
  rrule: string | null;
  reminders: CalendarEventReminder[];
  tags: string[];
  metadata: Record<string, unknown>;
  displayOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface CalendarEventTemplateInput {
  name: string;
  title: string;
  description?: string;
  location?: string;
  durationMinutes?: number;
  isAllDay?: boolean;
  status?: 'confirmed' | 'tentative' | 'cancelled';
  color?: string;
  url?: string;
  calendarId?: string;
  isRecurring?: boolean;
  rrule?: string;
  reminders?: CalendarEventReminder[];
  tags?: string[];
  metadata?: Record<string, unknown>;
  displayOrder?: number;
}

// ==========================================================================
// Calendar Sharing Types
// ==========================================================================

export type CalendarSharePermission = 'read' | 'write' | 'admin';

export interface CalendarShareDetail {
  id: string;
  calendarId: string;
  sharedWithUserId: string;
  sharedWithUsername?: string;
  permission: CalendarSharePermission;
  showFreeBusyOnly: boolean;
  createdAt: string;
}

export interface CalendarShareInput {
  username: string;
  permission: CalendarSharePermission;
  showFreeBusyOnly?: boolean;
}

export interface CalendarShareUpdate {
  permission?: CalendarSharePermission;
  showFreeBusyOnly?: boolean;
}

export interface SharedCalendarSummary {
  id: string;
  calendarId: string;
  calendarName: string;
  calendarColor: string;
  ownerUserId: string;
  ownerUsername: string;
  permission: CalendarSharePermission;
  showFreeBusyOnly: boolean;
}

export interface UserSearchResult {
  userId: string;
  username: string;
  displayName: string;
}

export interface BusyBlock {
  startAt: string;
  endAt: string;
}

export interface ParticipantAvailability {
  userId: string;
  username: string;
  busyBlocks: BusyBlock[];
}

export interface FreeSlot {
  startAt: string;
  endAt: string;
}

export interface FindTimeResult {
  participants: ParticipantAvailability[];
  freeSlots: FreeSlot[];
}

export interface FindTimeInput {
  participantUsernames: string[];
  rangeStart: string;
  rangeEnd: string;
  durationMinutes: number;
}

// =============================================================================
// Scheduling Links Types
// =============================================================================

export interface AvailabilityWindow {
  dayOfWeek: number;
  startTime: string;
  endTime: string;
}

export interface SchedulingLinkDetail {
  id: string;
  userId: string;
  slug: string;
  title: string;
  description: string;
  durationMinutes: number;
  location: string;
  color: string | null;
  bufferBeforeMinutes: number;
  bufferAfterMinutes: number;
  dailyCap: number | null;
  availabilityWindows: AvailabilityWindow[];
  calendarId: string | null;
  timezone: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface SchedulingLinkInput {
  slug: string;
  title: string;
  description?: string;
  durationMinutes?: number;
  location?: string;
  color?: string;
  bufferBeforeMinutes?: number;
  bufferAfterMinutes?: number;
  dailyCap?: number | null;
  availabilityWindows?: AvailabilityWindow[];
  calendarId?: string | null;
  timezone?: string;
  isActive?: boolean;
}

export interface BookingDetail {
  id: string;
  schedulingLinkId: string;
  bookerName: string;
  bookerEmail: string;
  eventId: string | null;
  startAt: string;
  endAt: string;
  status: 'confirmed' | 'cancelled';
  notes: string;
  cancellationToken: string;
  createdAt: string;
}

export interface AvailableSlotDetail {
  startAt: string;
  endAt: string;
}

export interface PublicSchedulingInfo {
  schedulingLink: {
    slug: string;
    title: string;
    description: string;
    durationMinutes: number;
    location: string;
    color: string | null;
    timezone: string;
    availabilityWindows: AvailabilityWindow[];
  };
  availableSlots: AvailableSlotDetail[];
}

export interface BookingConfirmation {
  booking: {
    id: string;
    startAt: string;
    endAt: string;
    status: string;
    cancellationToken: string;
  };
}

// =============================================================================
// Schedulable Tasks
// =============================================================================

export interface SchedulableTaskDetail {
  id: string;
  userId: string;
  title: string;
  description: string;
  deadline: string;
  estimatedMinutes: number;
  priority: 'high' | 'medium' | 'low';
  status: 'pending' | 'scheduled' | 'completed' | 'cancelled';
  eventId: string | null;
  calendarId: string | null;
  scheduledStart: string | null;
  scheduledEnd: string | null;
  aiSuggestions: AiScheduleSuggestion[];
  metadata: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface SchedulableTaskInput {
  title: string;
  description?: string;
  deadline: string;
  estimatedMinutes?: number;
  priority?: 'high' | 'medium' | 'low';
  calendarId?: string;
  metadata?: Record<string, unknown>;
}

export interface AiScheduleSuggestion {
  startAt: string;
  endAt: string;
  reasoning: string;
  score: number;
}

// =============================================================================
// Focus Time
// =============================================================================

export interface FocusTimeSettingsDetail {
  id: string;
  userId: string;
  isEnabled: boolean;
  preferredTimeOfDay: 'morning' | 'afternoon' | 'evening';
  minBlockMinutes: number;
  maxBlockMinutes: number;
  daysPerWeek: number;
  preferredDays: number[];
  preferredStartHour: number;
  preferredEndHour: number;
  lookaheadWeeks: number;
  calendarId: string | null;
  color: string;
  createdAt: string;
  updatedAt: string;
}

export interface FocusTimeSettingsInput {
  isEnabled?: boolean;
  preferredTimeOfDay?: 'morning' | 'afternoon' | 'evening';
  minBlockMinutes?: number;
  maxBlockMinutes?: number;
  daysPerWeek?: number;
  preferredDays?: number[];
  preferredStartHour?: number;
  preferredEndHour?: number;
  lookaheadWeeks?: number;
  calendarId?: string | null;
  color?: string;
}

export interface CalendarSubscriptionDetail {
  id: string;
  calendarId: string;
  feedUrl: string;
  lastFetchedAt: string | null;
  refreshIntervalMinutes: number;
  status: 'active' | 'error';
  errorMessage: string | null;
  calendarName: string;
  calendarColor: string;
  eventCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface CalendarAnalyticsHoursPerCalendar {
  calendarId: string;
  calendarName: string;
  calendarColor: string;
  hours: number;
  eventCount: number;
}

export interface CalendarAnalyticsHoursPerWeek {
  weekStart: string;
  hours: number;
  eventCount: number;
}

export interface CalendarAnalyticsHoursPerDayOfWeek {
  dayOfWeek: number;
  hours: number;
  eventCount: number;
}

export interface CalendarAnalyticsSummary {
  totalEvents: number;
  totalHours: number;
  avgDailyHours: number;
  avgEventDurationMinutes: number;
}

export interface CalendarAnalyticsResult {
  summary: CalendarAnalyticsSummary;
  hoursPerCalendar: CalendarAnalyticsHoursPerCalendar[];
  hoursPerWeek: CalendarAnalyticsHoursPerWeek[];
  hoursPerDayOfWeek: CalendarAnalyticsHoursPerDayOfWeek[];
}

// ==========================================================================
// Lit Legion Types
// ==========================================================================

export interface LitLegionNpc {
  npcId: string;
  name: string;
  image: string;
  createdAt: string;
  updatedAt: string;
}

export interface LitLegionUserLevel {
  levelId: string;
  levelNumber: number;
  name: string;
  xpRequired: number;
  createdAt: string;
  updatedAt: string;
}

export interface LitLegionBadge {
  badgeId: string;
  name: string;
  image: string;
  description: string;
  category: string;
  createdAt: string;
  updatedAt: string;
}

export interface LitLegionTask {
  taskId: string;
  parentId: string | null;
  npcId: string | null;
  minLevelId: string | null;
  taskType: string;
  name: string;
  description: string;
  category: string | null;
  xpReward: number;
  maxCompletions: number | null;
  maxRetakesPerUser: number | null;
  startDate: string | null;
  endDate: string | null;
  timeIntervalMs: number | null;
  definition: Record<string, unknown> | null;
  npcTextIntro: string;
  npcTextObjectives: string;
  npcTextInProgress: string;
  npcTextSuccess: string;
  createdAt: string;
  updatedAt: string;
}

export interface LitLegionAdminTask extends LitLegionTask {
  privateDefinition: Record<string, unknown> | null;
}

export interface LitLegionTaskSubmission {
  checkboxes: number[];
  multipleChoices: { index: number; optionId: string }[];
  textInputs: { responseTag: string; value: string }[];
}

export interface LitLegionTaskStatusResponse {
  taskStatusId: string;
  userId: string;
  taskId: string;
  status: string;
  completedAt: string | null;
  xpAmountReceived: number | null;
  userInput: string | null;
  submission: LitLegionTaskSubmission | null;
  createdAt: string;
  updatedAt: string;
}

export interface LitLegionAdminTaskStatusWithTask extends LitLegionTaskStatusResponse {
  task: LitLegionAdminTask | null;
}

export interface LitLegionCoinReward {
  coinRewardId: string;
  taskId: string;
  coinId: string;
  coinName: string;
  quantity: number;
  createdAt: string;
}

export interface LitLegionBadgeReward {
  badgeRewardId: string;
  taskId: string;
  badgeId: string;
  createdAt: string;
}

export interface LitLegionTaskWithStatus {
  task: LitLegionTask;
  taskStatus: LitLegionTaskStatusResponse | null;
  npc: LitLegionNpc | null;
  coinRewards: LitLegionCoinReward[];
  badgeRewards: LitLegionBadgeReward[];
}

export interface LitLegionTaskStatusListResult {
  taskStatuses: LitLegionTaskWithStatus[];
  total: number;
  limit: number;
  offset: number;
}

export interface LitLegionEarnedBadge {
  badgeId: string;
  name: string;
  image: string;
  description: string;
  category: string;
  earliestAwardedAt: string;
}

export interface LitLegionPlayerStats {
  userId: string;
  xpAmount: number;
  level: LitLegionUserLevel;
  nextLevel: LitLegionUserLevel | null;
  tasksCompleted: number;
  tasksInProgress: number;
  earnedBadges: LitLegionEarnedBadge[];
}

export interface LitLegionAdminTaskListResult {
  tasks: LitLegionAdminTask[];
  total: number;
  limit: number;
  offset: number;
}

export interface CreateLitLegionTaskPayload {
  name: string;
  description: string;
  taskType: string;
  xpReward: number;
  category?: string;
  npcId?: string;
  parentId?: string;
  minLevelId?: string;
  npcTextIntro?: string;
  npcTextObjectives?: string;
  npcTextInProgress?: string;
  npcTextSuccess?: string;
  definition?: Record<string, unknown> | null;
  privateDefinition?: Record<string, unknown> | null;
  coinRewards?: { coinId: string; coinName: string; quantity: number }[];
  badgeRewards?: { badgeId: string }[];
}

export interface UpdateLitLegionTaskPayload {
  name?: string;
  description?: string;
  taskType?: string;
  xpReward?: number;
  category?: string | null;
  npcId?: string | null;
  parentId?: string | null;
  minLevelId?: string | null;
  npcTextIntro?: string;
  npcTextObjectives?: string;
  npcTextInProgress?: string;
  npcTextSuccess?: string;
  definition?: Record<string, unknown> | null;
  privateDefinition?: Record<string, unknown> | null;
  coinRewards?: { coinId: string; coinName: string; quantity: number }[];
  badgeRewards?: { badgeId: string }[];
}

// ==========================================================================
// Business Types
// ==========================================================================

export interface BusinessResponse {
  businessId: string;
  userId?: string;
  businessType: string;
  name: string;
  level: number;
  status: string;
  lastCollectedAt: string | null;
  cooldownRemaining: number;
  canCollect: boolean;
  dailyCollectionsUsed: number;
  createdAt: string;
  updatedAt?: string;
}

export interface CollectRevenueResponse {
  heptalsEarned: number;
  bonusItemName: string | null;
  bonusItemCount: number;
  subscriptionTier: string;
  subscriptionMultiplier: number;
  revenueMultiplier: number;
  logId: string;
}

export interface BusinessParameterResponse {
  paramId: string;
  businessType: string;
  level: number;
  requirements: {
    jion: number;
    freshWater: number;
    cogsValue: number;
    components: Record<string, number>;
    foodValue: number;
  };
  rewards: {
    mode: string;
    heptals: number;
    bonusItem: string | null;
    bonusCount: number;
  };
  creationCost: {
    heptals: number;
    spentCoinName: string;
  };
}

export interface BusinessHistoryResponse {
  logs: Array<{
    logId: string;
    businessId: string;
    businessType: string;
    level: number;
    heptalsEarned: number;
    bonusItemName: string | null;
    bonusItemCount: number;
    subscriptionTier: string;
    subscriptionMultiplier: number;
    revenueMultiplier: number;
    cogsConsumed: Record<string, number>;
    foodsConsumed: Record<string, number>;
    materialsConsumed: Record<string, number>;
    createdAt: string;
  }>;
  total: number;
  limit: number;
  offset: number;
}

export interface BusinessStatsResponse {
  totalBusinesses: number;
  activeBusinesses: number;
  dissolvedBusinesses: number;
  businessesByType: Record<string, number>;
  totalCollections: number;
  totalHeptalsEarned: number;
}

// =============================================================================
// Beta Access Types
// =============================================================================

export interface BetaAccessSubmitInput {
  name: string;
  email: string;
  telegram?: string;
  description: string;
}

export interface BetaAccessSubmitResponse {
  message: string;
}

export interface BetaAccessRequestResponse {
  requestId: string;
  name: string;
  email: string;
  telegram: string | null;
  description: string;
  status: 'pending' | 'accepted' | 'rejected';
  accessCode: string | null;
  adminNotes: string | null;
  reviewedBy: string | null;
  reviewedAt: string | null;
  ipAddress: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface BetaAccessListResponse {
  requests: BetaAccessRequestResponse[];
  total: number;
  limit: number;
  offset: number;
}

export interface BetaAccessStatsResponse {
  total: number;
  pending: number;
  accepted: number;
  rejected: number;
}

// ==========================================================================
// Learn About Fias Types
// ==========================================================================

export interface LearnAboutFiasResourceResponse {
  docId: string;
  title: string;
  description: string | null;
  sourceUrl: string | null;
  sourceType: string;
  iconType: string | null;
  buttonLabel: string | null;
  fileKey: string | null;
  ragEnabled: boolean;
  totalChunks: number;
  createdAt: string;
}

export interface LearnAboutFiasResourcesResponse {
  resources: LearnAboutFiasResourceResponse[];
}

export interface LearnAboutFiasDocumentResponse {
  docId: string;
  title: string;
  description: string | null;
  sourceUrl: string | null;
  sourceType: string;
  metadata: Record<string, unknown>;
  status: string;
  totalChunks: number;
  errorMessage: string | null;
  showInResources: boolean;
  ragEnabled: boolean;
  fileKey: string | null;
  sortOrder: number;
  createdAt: string;
  processedAt: string | null;
}

export interface LearnAboutFiasDocumentDetailResponse extends LearnAboutFiasDocumentResponse {
  content: string | null;
}

export interface LearnAboutFiasDocumentListResponse {
  documents: LearnAboutFiasDocumentResponse[];
  total: number;
  limit: number;
  offset: number;
}

export interface LearnAboutFiasStatsResponse {
  documentCount: number;
  chunkCount: number;
  pendingDocuments: number;
  failedDocuments: number;
  avgChunksPerDocument: number;
}

// ==========================================================================
// Storage Types (shared by DFE Avatars, DFE Icons, Litcraft Addressables)
// ==========================================================================

export interface StorageFile {
  key: string;
  size?: number;
  lastModified?: string;
}

export interface StorageFileListResponse {
  files: StorageFile[];
  folders: string[];
  cdnBaseUrl: string | null;
  continuationToken: string | null;
  isTruncated: boolean;
}

export interface StorageUploadUrlResponse {
  uploadUrl: string;
  cdnUrl: string | null;
  expiresIn: number;
}

export interface StorageFileMetadataResponse {
  key: string;
  size?: number;
  lastModified?: string;
  contentType?: string;
  etag?: string;
  cdnUrl: string | null;
}

/**
 * API interface for a storage arche.
 * Created by ApiClient.createStorageApi() for each storage arche.
 */
export interface StorageApi {
  listFiles(
    prefix?: string,
    delimiter?: string,
    maxKeys?: number,
    continuationToken?: string,
  ): Promise<StorageFileListResponse>;
  getUploadUrl(
    key: string,
    contentType: string,
    sizeBytes: number,
  ): Promise<StorageUploadUrlResponse>;
  deleteFile(key: string): Promise<{ success: boolean; key: string }>;
  deleteGroup(prefix: string): Promise<{
    success: boolean;
    prefix: string;
    deletedCount: number;
    failedCount?: number;
    failedKeys?: string[];
  }>;
  getMetadata(key: string): Promise<StorageFileMetadataResponse>;
}

// ==========================================================================
// Todo List Types
// ==========================================================================

export interface TodoListResponse {
  listId: string;
  ownerId: string;
  name: string;
  description: string | null;
  isPublic: boolean;
  publicSlug: string | null;
  status: string;
  createdAt: string;
  updatedAt: string;
}

export interface TodoSectionResponse {
  sectionId: string;
  listId: string;
  parentSectionId: string | null;
  name: string;
  description: string | null;
  sortOrder: number;
  sectionType: 'timeframe' | 'category';
  hiddenFromPublic: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface TodoItemResponse {
  itemId: string;
  listId: string;
  sectionId: string | null;
  title: string;
  description: string | null;
  status: 'pending' | 'in_progress' | 'completed';
  sortOrder: number;
  completedAt: string | null;
  completedByUserId: string | null;
  completedByUsername: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface TodoListCollaboratorResponse {
  listId: string;
  userId: string;
  username: string;
  role: 'viewer' | 'editor';
  createdAt: string;
}

export interface TodoItemAssignmentResponse {
  itemId: string;
  userId: string;
  username: string;
  assignedAt: string;
}

export interface TodoSectionAssignmentResponse {
  sectionId: string;
  userId: string;
  username: string;
  assignedAt: string;
}

export interface TodoListDetailResponse extends TodoListResponse {
  sections: TodoSectionResponse[];
  items: TodoItemResponse[];
  collaborators?: TodoListCollaboratorResponse[];
  itemAssignments?: TodoItemAssignmentResponse[];
  sectionAssignments?: TodoSectionAssignmentResponse[];
  userRole?: 'owner' | 'viewer' | 'editor';
}

export interface SharedTodoListResponse {
  listId: string;
  name: string;
  description: string | null;
  ownerUsername: string;
  collaboratorRole: 'viewer' | 'editor';
}

export interface TodoListListResponse {
  lists: TodoListResponse[];
  total: number;
  limit: number;
  offset: number;
}

export interface TodoCompletedResponse {
  items: TodoItemResponse[];
  total: number;
}

// ==========================================================================
// Customer Support Types
// ==========================================================================

export interface SupportTicketResponse {
  ticketId: string;
  userId: string;
  categoryId: string | null;
  subject: string;
  description: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  tags?: SupportTagResponse[];
}

export interface SupportTicketDetailResponse extends SupportTicketResponse {
  assignedTo: string | null;
  resolvedAt: string | null;
}

export interface SupportTicketListResult {
  tickets: Array<{
    ticketId: string;
    categoryId: string | null;
    subject: string;
    status: string;
    createdAt: string;
    updatedAt: string;
  }>;
  total: number;
  limit: number;
  offset: number;
}

export interface SupportAdminTicketResponse extends SupportTicketResponse {
  priority: string;
  aiPriority: string | null;
  assignedTo: string | null;
  resolvedAt: string | null;
  tags?: SupportTagResponse[];
}

export interface SupportAdminTicketListResult {
  tickets: Array<{
    ticketId: string;
    userId?: string;
    categoryId: string | null;
    subject: string;
    description?: string;
    status: string;
    priority: string;
    aiPriority: string | null;
    assignedTo?: string | null;
    createdAt: string;
    updatedAt: string;
    resolvedAt?: string | null;
    tags?: SupportTagResponse[];
  }>;
  total: number;
  limit: number;
  offset: number;
}

export interface SupportMessageResponse {
  messageId: string;
  senderId: string | null;
  senderType: string;
  content: string;
  createdAt: string;
}

export interface SupportAdminMessageResponse extends SupportMessageResponse {
  isInternal: boolean;
  deletedAt: string | null;
}

export interface SupportFaqResponse {
  faqId: string;
  categoryId: string | null;
  question: string;
  answer: string;
  displayOrder: number;
}

export interface SupportFaqAdminResponse extends SupportFaqResponse {
  isPublished: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface SupportCategoryResponse {
  categoryId: string;
  name: string;
  description: string | null;
}

export interface SupportCategoryAdminResponse extends SupportCategoryResponse {
  displayOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface SupportCannedResponseResponse {
  responseId: string;
  title: string;
  content: string;
  categoryId: string | null;
  createdBy?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface SupportTagResponse {
  tagId: string;
  name: string;
  color: string;
  description: string | null;
  isSystem: boolean;
  createdAt?: string;
}

export interface SupportKbArticleResponse {
  kbId: string;
  categoryId: string | null;
  title: string;
  content: string;
  keywords: string[];
  isPublished?: boolean;
  createdBy?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface SupportAnalyticsResponse {
  totalTickets: number;
  openTickets: number;
  inProgressTickets: number;
  resolvedTickets: number;
  closedTickets: number;
  avgResolutionTimeHours: number | null;
  categoryBreakdown: Array<{ categoryId: string; categoryName: string; count: number }>;
  priorityBreakdown: Array<{ priority: string; count: number }>;
}

// ==========================================================================
// Document Sign Types
//
// These shapes MUST stay in sync with `packages/db-types/src/document-sign.ts`.
// The webapp-client package can't depend on `@fias/db-types` directly (that
// package ships Node-only deps), so the shapes + constants are duplicated
// here. When you change one, change both — the backend tests and frontend
// tests both assert on the wire shape.
// ==========================================================================

/** MUST match DOCUMENT_SIGN_MAX_FILE_SIZE_BYTES in @fias/db-types. */
export const DOCUMENT_SIGN_MAX_FILE_SIZE_BYTES = 25 * 1024 * 1024;
/** MUST match DOCUMENT_SIGN_MAX_SOURCE_FILES in @fias/db-types. */
export const DOCUMENT_SIGN_MAX_SOURCE_FILES = 5;
/** MUST match DOCUMENT_SIGN_SOURCE_MIME_TYPE in @fias/db-types. */
export const DOCUMENT_SIGN_SOURCE_MIME_TYPE = 'application/pdf';
/** MUST match DOCUMENT_SIGN_MAX_RECIPIENTS in @fias/db-types. */
export const DOCUMENT_SIGN_MAX_RECIPIENTS = 20;
/** MUST match DOCUMENT_SIGN_MAX_FIELDS_PER_FILE in @fias/db-types. */
export const DOCUMENT_SIGN_MAX_FIELDS_PER_FILE = 200;
/** MUST match DOCUMENT_SIGN_VOID_REASON_MAX_LENGTH in @fias/db-types. */
export const DOCUMENT_SIGN_VOID_REASON_MAX_LENGTH = 500;

export type DocumentSignEnvelopeStatus =
  | 'draft'
  | 'sent'
  | 'in_progress'
  | 'completed'
  | 'declined'
  | 'voided'
  | 'expired';

export type DocumentSignSigningOrder = 'parallel' | 'sequential';

export interface DocumentSignEnvelopeResponse {
  envelopeId: string;
  ownerUserId: string;
  title: string;
  message: string | null;
  status: DocumentSignEnvelopeStatus;
  signingOrder: DocumentSignSigningOrder;
  expiresAt: string | null;
  sentAt: string | null;
  completedAt: string | null;
  voidedAt: string | null;
  voidedReason: string | null;
  declinedAt: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export interface DocumentSignFileResponse {
  fileId: string;
  envelopeId: string;
  kind: 'source' | 'sealed' | 'certificate';
  s3Key: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  pageCount: number | null;
  sha256Hex: string;
  sortOrder: number;
  createdAt: string;
}

export interface DocumentSignRecipientResponse {
  recipientId: string;
  envelopeId: string;
  role: 'signer' | 'viewer' | 'approver';
  routingOrder: number;
  name: string | null;
  email: string | null;
  userId: string | null;
  status: 'pending' | 'notified' | 'viewed' | 'signed' | 'declined' | 'expired';
  notifiedAt: string | null;
  viewedAt: string | null;
  signedAt: string | null;
  declinedAt: string | null;
  declineReason: string | null;
  createdAt: string;
}

export interface DocumentSignFieldResponse {
  fieldId: string;
  envelopeId: string;
  fileId: string;
  recipientId: string;
  type: 'signature' | 'initial' | 'date_signed' | 'signer_name' | 'text' | 'checkbox';
  page: number;
  x: number;
  y: number;
  w: number;
  h: number;
  required: boolean;
  value: string | null;
  signedAt: string | null;
  createdAt: string;
}

export interface DocumentSignEnvelopeDetailResponse extends DocumentSignEnvelopeResponse {
  files: DocumentSignFileResponse[];
  recipients: DocumentSignRecipientResponse[];
  fields: DocumentSignFieldResponse[];
}

export interface DocumentSignEnvelopeListResponse {
  envelopes: DocumentSignEnvelopeResponse[];
  total: number;
  limit: number;
  offset: number;
}

export interface DocumentSignUploadUrlResponse {
  fileId: string;
  s3Key: string;
  uploadUrl: string;
  expiresAt: string;
}

export interface DocumentSignRecipientInput {
  name: string;
  email: string;
  role: 'signer' | 'viewer' | 'approver';
  routingOrder?: number;
}

export interface DocumentSignFieldInput {
  recipientId: string;
  type: 'signature' | 'initial' | 'date_signed' | 'signer_name' | 'text' | 'checkbox';
  page: number;
  x: number;
  y: number;
  w: number;
  h: number;
  required?: boolean;
}

export interface DocumentSignSendEnvelopeResponse {
  envelope: DocumentSignEnvelopeResponse;
  recipients: DocumentSignRecipientResponse[];
  notifiedRecipientIds: string[];
}

export interface DocumentSignSigningEnvelopeView {
  envelopeId: string;
  title: string;
  message: string | null;
  status: DocumentSignEnvelopeStatus;
  signingOrder: DocumentSignSigningOrder;
  expiresAt: string | null;
  ownerName: string;
}

export interface DocumentSignSigningRecipientView {
  recipientId: string;
  name: string | null;
  email: string | null;
  role: 'signer' | 'viewer' | 'approver';
  status: 'pending' | 'notified' | 'viewed' | 'signed' | 'declined' | 'expired';
}

export interface DocumentSignSigningFileView {
  fileId: string;
  fileName: string;
  pageCount: number | null;
  sortOrder: number;
  downloadUrl: string;
  expiresAt: string;
}

export interface DocumentSignSigningSessionResponse {
  envelope: DocumentSignSigningEnvelopeView;
  recipient: DocumentSignSigningRecipientView;
  fields: DocumentSignFieldResponse[];
  files: DocumentSignSigningFileView[];
  consentDisclosure: string;
}

export interface DocumentSignSigningFieldSubmission {
  fieldId: string;
  value?: string | null;
  signatureDataUrl?: string | null;
}

export type DocumentSignLedgerStatus =
  | 'disabled'
  | 'pending'
  | 'submitted'
  | 'confirmed'
  | 'failed';

export interface DocumentSignEventResponse {
  eventId: string;
  envelopeId: string;
  recipientId: string | null;
  actorUserId: string | null;
  actorKind: 'owner' | 'recipient' | 'system';
  type: string;
  payload: Record<string, unknown>;
  ip: string | null;
  userAgent: string | null;
  createdAt: string;
  ledgerStatus: DocumentSignLedgerStatus;
  ledgerRef: string | null;
  ledgerAttempts: number;
  ledgerLastError: string | null;
  ledgerSubmittedAt: string | null;
  ledgerConfirmedAt: string | null;
}

// ==========================================================================
// Leads Types
// ==========================================================================

export interface LeadsCompany {
  id: string;
  userId: string;
  teamId: string | null;
  name: string;
  domain: string | null;
  industry: string | null;
  size: string | null;
  phone: string | null;
  email: string | null;
  website: string | null;
  address: Record<string, unknown>;
  notes: string | null;
  tags: string[];
  metadata: Record<string, unknown>;
  healthScore: number | null;
  aiSummary: string | null;
  status: 'active' | 'archived' | 'deleted';
  createdAt: string;
  updatedAt: string;
  contactCount?: number;
  dealCount?: number;
  totalDealValue?: number;
}

export interface LeadsCreateCompanyInput {
  name: string;
  domain?: string;
  industry?: string;
  size?: string;
  phone?: string;
  email?: string;
  website?: string;
  address?: Record<string, unknown>;
  notes?: string;
  tags?: string[];
  metadata?: Record<string, unknown>;
}

export interface LeadsListCompaniesResult {
  companies: LeadsCompany[];
  total: number;
  limit: number;
  offset: number;
}

export interface LeadsContact {
  id: string;
  userId: string;
  teamId: string | null;
  companyId: string | null;
  name: string;
  email: string | null;
  phone: string | null;
  title: string | null;
  department: string | null;
  source: string | null;
  lifecycleStage: string;
  leadScore: number | null;
  aiSummary: string | null;
  notes: string | null;
  tags: string[];
  metadata: Record<string, unknown>;
  lastContactedAt: string | null;
  status: 'active' | 'archived' | 'deleted';
  createdAt: string;
  updatedAt: string;
  companyName?: string;
  dealCount?: number;
}

export interface LeadsCreateContactInput {
  companyId?: string;
  name: string;
  email?: string;
  phone?: string;
  title?: string;
  department?: string;
  source?: string;
  lifecycleStage?: string;
  notes?: string;
  tags?: string[];
  metadata?: Record<string, unknown>;
}

export interface LeadsListContactsResult {
  contacts: LeadsContact[];
  total: number;
  limit: number;
  offset: number;
}

export interface LeadsPipelineStage {
  id: string;
  pipelineId: string;
  name: string;
  displayOrder: number;
  stageType: 'open' | 'won' | 'lost';
  color: string;
  createdAt: string;
  dealCount?: number;
  dealValue?: number;
}

export interface LeadsPipeline {
  id: string;
  userId: string;
  name: string;
  isDefault: boolean;
  stages: LeadsPipelineStage[];
  createdAt: string;
  updatedAt: string;
  totalDeals?: number;
  totalValue?: number;
}

export interface LeadsDeal {
  id: string;
  userId: string;
  teamId: string | null;
  pipelineId: string;
  stageId: string;
  contactId: string | null;
  companyId: string | null;
  name: string;
  value: number;
  currency: string;
  expectedCloseDate: string | null;
  actualCloseDate: string | null;
  winLikelihood: number | null;
  lostReason: string | null;
  notes: string | null;
  tags: string[];
  metadata: Record<string, unknown>;
  status: 'active' | 'archived' | 'deleted';
  createdAt: string;
  updatedAt: string;
  stageName?: string;
  stageType?: string;
  stageColor?: string;
  pipelineName?: string;
  contactName?: string;
  companyName?: string;
}

export interface LeadsCreateDealInput {
  pipelineId: string;
  stageId: string;
  contactId?: string;
  companyId?: string;
  name: string;
  value?: number;
  currency?: string;
  expectedCloseDate?: string;
  notes?: string;
  tags?: string[];
  metadata?: Record<string, unknown>;
}

export interface LeadsListDealsResult {
  deals: LeadsDeal[];
  total: number;
  limit: number;
  offset: number;
}

export interface LeadsPipelineSummary {
  pipelineId: string;
  pipelineName: string;
  stages: Array<{
    stageId: string;
    stageName: string;
    stageType: string;
    stageColor: string;
    displayOrder: number;
    dealCount: number;
    totalValue: number;
    deals: LeadsDeal[];
  }>;
  totalDeals: number;
  totalValue: number;
}

// ==========================================================================
// Leads Activity Types
// ==========================================================================

export type LeadsActivityType =
  | 'call'
  | 'email'
  | 'meeting'
  | 'note'
  | 'task_completed'
  | 'stage_change'
  | 'deal_created'
  | 'custom';

export interface LeadsActivity {
  id: string;
  userId: string;
  contactId: string | null;
  companyId: string | null;
  dealId: string | null;
  activityType: LeadsActivityType;
  subject: string;
  description: string | null;
  occurredAt: string;
  durationMinutes: number | null;
  metadata: Record<string, unknown>;
  createdAt: string;
  contactName?: string;
  companyName?: string;
  dealName?: string;
}

export interface LeadsCreateActivityInput {
  contactId?: string;
  companyId?: string;
  dealId?: string;
  activityType: LeadsActivityType;
  subject: string;
  description?: string;
  occurredAt?: string;
  durationMinutes?: number;
  metadata?: Record<string, unknown>;
}

export interface LeadsListActivitiesResult {
  activities: LeadsActivity[];
  total: number;
  limit: number;
  offset: number;
}

// ==========================================================================
// Leads Task Types
// ==========================================================================

export type LeadsTaskPriority = 'low' | 'medium' | 'high';
export type LeadsTaskStatus = 'pending' | 'in_progress' | 'completed' | 'cancelled';

export interface LeadsTask {
  id: string;
  userId: string;
  contactId: string | null;
  companyId: string | null;
  dealId: string | null;
  title: string;
  description: string | null;
  dueDate: string | null;
  reminderAt: string | null;
  priority: LeadsTaskPriority;
  status: LeadsTaskStatus;
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
  contactName?: string;
  companyName?: string;
  dealName?: string;
}

export interface LeadsCreateTaskInput {
  contactId?: string;
  companyId?: string;
  dealId?: string;
  title: string;
  description?: string;
  dueDate?: string;
  reminderAt?: string;
  priority?: LeadsTaskPriority;
}

export interface LeadsListTasksResult {
  tasks: LeadsTask[];
  total: number;
  limit: number;
  offset: number;
}

// ==========================================================================
// Leads Automation Types
// ==========================================================================

export type LeadsAutomationTriggerType =
  | 'deal_stage_changed'
  | 'deal_created'
  | 'contact_created'
  | 'task_overdue'
  | 'field_updated';
export type LeadsAutomationActionType =
  | 'create_task'
  | 'update_field'
  | 'add_tag'
  | 'send_notification';

export interface LeadsAutomationRule {
  id: string;
  userId: string;
  name: string;
  isActive: boolean;
  triggerType: LeadsAutomationTriggerType;
  triggerConfig: Record<string, unknown>;
  actionType: LeadsAutomationActionType;
  actionConfig: Record<string, unknown>;
  executionCount: number;
  lastExecutedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface LeadsCreateAutomationInput {
  name: string;
  triggerType: LeadsAutomationTriggerType;
  triggerConfig: Record<string, unknown>;
  actionType: LeadsAutomationActionType;
  actionConfig: Record<string, unknown>;
  isActive?: boolean;
}

export interface LeadsAutomationExecution {
  id: string;
  ruleId: string;
  triggerEntityType: string;
  triggerEntityId: string;
  status: 'success' | 'failed';
  errorMessage: string | null;
  executedAt: string;
}

// ==========================================================================
// Leads Custom Field Types
// ==========================================================================

export type LeadsCustomFieldEntityType = 'contact' | 'company' | 'deal';
export type LeadsCustomFieldType =
  | 'text'
  | 'number'
  | 'date'
  | 'boolean'
  | 'select'
  | 'multi_select'
  | 'url'
  | 'email'
  | 'phone';

export interface LeadsCustomFieldDefinition {
  id: string;
  userId: string;
  entityType: LeadsCustomFieldEntityType;
  fieldName: string;
  fieldLabel: string;
  fieldType: LeadsCustomFieldType;
  options: string[] | null;
  isRequired: boolean;
  displayOrder: number;
  createdAt: string;
}

export interface LeadsCreateCustomFieldInput {
  entityType: LeadsCustomFieldEntityType;
  fieldName: string;
  fieldLabel: string;
  fieldType: LeadsCustomFieldType;
  options?: string[];
  isRequired?: boolean;
  displayOrder?: number;
}

// ==========================================================================
// Leads Email Template Types
// ==========================================================================

export interface LeadsEmailTemplate {
  id: string;
  userId: string;
  name: string;
  subject: string;
  bodyHtml: string | null;
  bodyText: string | null;
  variables: string[];
  createdAt: string;
  updatedAt: string;
}

export interface LeadsCreateEmailTemplateInput {
  name: string;
  subject: string;
  bodyHtml?: string;
  bodyText?: string;
  variables?: string[];
}

export interface LeadsListEmailTemplatesResult {
  templates: LeadsEmailTemplate[];
  total: number;
  limit: number;
  offset: number;
}

export interface LeadsRenderedTemplate {
  subject: string;
  bodyHtml: string | null;
  bodyText: string | null;
}

// ==========================================================================
// Leads Timeline Types
// ==========================================================================

export type LeadsTimelineEventType =
  | 'call'
  | 'email'
  | 'meeting'
  | 'note'
  | 'task_completed'
  | 'stage_change'
  | 'deal_created'
  | 'custom'
  | 'task_created'
  | 'task_due';

export interface LeadsTimelineEvent {
  id: string;
  eventType: LeadsTimelineEventType;
  title: string;
  description: string | null;
  occurredAt: string;
  entityType: 'activity' | 'task';
  entityId: string;
  contactId: string | null;
  companyId: string | null;
  dealId: string | null;
  contactName?: string;
  companyName?: string;
  dealName?: string;
  metadata: Record<string, unknown>;
}

export interface LeadsTimelineResult {
  events: LeadsTimelineEvent[];
  total: number;
  limit: number;
  offset: number;
}

// ==========================================================================
// Leads Reporting Types
// ==========================================================================

export interface LeadsPipelineVelocityStage {
  stageId: string;
  stageName: string;
  stageColor: string;
  displayOrder: number;
  avgDaysInStage: number;
  dealCount: number;
  conversionRate: number;
}

export interface LeadsPipelineVelocityResult {
  stages: LeadsPipelineVelocityStage[];
}

export interface LeadsMonthlyProjection {
  month: string;
  projectedValue: number;
  dealCount: number;
}

export interface LeadsSalesForecastResult {
  weightedPipelineValue: number;
  monthlyProjections: LeadsMonthlyProjection[];
  summary: {
    totalOpenValue: number;
    totalWonValue: number;
    avgDealSize: number;
    winRate: number;
  };
}

export interface LeadsActivityTrendPeriod {
  label: string;
  counts: Record<string, number>;
  total: number;
}

export interface LeadsActivityTrendsResult {
  periods: LeadsActivityTrendPeriod[];
}

export interface LeadsLostDealByReason {
  reason: string;
  count: number;
  totalValue: number;
}

export interface LeadsLostDealByStage {
  stageId: string;
  stageName: string;
  stageColor: string;
  count: number;
  totalValue: number;
}

export interface LeadsRecentLostDeal {
  id: string;
  name: string;
  value: number;
  lostReason: string | null;
  contactName: string | null;
  companyName: string | null;
  actualCloseDate: string | null;
}

export interface LeadsLostDealAnalysisResult {
  totalLost: number;
  totalLostValue: number;
  byReason: LeadsLostDealByReason[];
  byStage: LeadsLostDealByStage[];
  recentLostDeals: LeadsRecentLostDeal[];
}

// ==========================================================================
// Leads Import/Export Types
// ==========================================================================

export interface LeadsFieldMapping {
  csvHeader: string;
  leadsField: string | null;
  sampleValues: string[];
}

export interface LeadsImportPreviewResult {
  headers: string[];
  mappedFields: LeadsFieldMapping[];
  previewRows: Record<string, string>[];
  totalRows: number;
  errors: string[];
}

export interface LeadsImportExecuteResult {
  totalRows: number;
  successCount: number;
  errorCount: number;
  errors: Array<{ row: number; message: string }>;
}

// ==========================================================================
// Leads Dashboard Types
// ==========================================================================

export interface LeadsDashboardMetrics {
  counts: {
    contacts: number;
    companies: number;
    deals: number;
    openDeals: number;
    wonDeals: number;
    lostDeals: number;
    tasks: number;
    overdueTasks: number;
  };
  revenue: {
    totalPipelineValue: number;
    wonValue: number;
    lostValue: number;
    avgDealValue: number;
  };
  pipelineBreakdown: Array<{
    stageId: string;
    stageName: string;
    stageType: string;
    stageColor: string;
    displayOrder: number;
    dealCount: number;
    totalValue: number;
  }>;
  recentActivities: Array<{
    id: string;
    activityType: string;
    subject: string;
    occurredAt: string;
    contactName: string | null;
    companyName: string | null;
  }>;
  upcomingTasks: Array<{
    id: string;
    title: string;
    dueDate: string | null;
    priority: string;
    contactName: string | null;
  }>;
  activityCounts: Record<string, number>;
  dealRiskSummary: {
    high: number;
    medium: number;
    low: number;
    topRisks: LeadsDealRiskAlert[];
  };
}

// ==========================================================================
// Builder Types
// ==========================================================================

export interface BuilderSessionSummary {
  sessionId: string;
  archId: string | null;
  currentName: string | null;
  status: 'active' | 'paused' | 'completed' | 'abandoned';
  updatedAt: string;
  totalCostCents: number;
}

export interface BuilderMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
  artifacts?: BuilderArtifact[];
}

export interface BuilderArtifact {
  type: 'ui_definition' | 'workflow' | 'entity_selection' | 'settings';
  content: unknown;
  description: string;
}

export interface BuilderSession {
  sessionId: string;
  userId: string;
  archId: string | null;
  templateId: string | null;
  conversationHistory: BuilderMessage[];
  currentName: string | null;
  currentDescription: string | null;
  currentUiDefinition: unknown | null;
  currentWorkflow: unknown | null;
  currentSettings: unknown | null;
  status: 'active' | 'paused' | 'completed' | 'abandoned';
  lastClaudeResponseAt: string | null;
  totalInputTokens: number;
  totalOutputTokens: number;
  totalCostCents: number;
  createdAt: string;
  updatedAt: string;
}

export interface BuilderTemplate {
  templateId: string;
  name: string;
  description: string | null;
  category: string | null;
  thumbnailRef: string | null;
  uiDefinition: unknown;
  workflow: unknown | null;
  defaultSettings: unknown | null;
  displayOrder: number;
  isFeatured: boolean;
}

export interface ArcheReviewSummary {
  reviewId: string;
  version: number;
  reviewerType: 'ai' | 'human';
  status: 'pending' | 'in_progress' | 'approved' | 'rejected';
  riskScore: number | null;
  createdAt: string;
  completedAt: string | null;
}

export interface ReviewFindingSummary {
  total: number;
  critical: number;
  high: number;
  medium: number;
  low: number;
}

export interface ReviewFinding {
  id: string;
  severity: 'critical' | 'high' | 'medium' | 'low';
  category: string;
  message: string;
  location?: string;
  suggestion?: string;
}

export interface ArcheReviewDetail {
  reviewId: string;
  archId: string;
  version: number;
  reviewerType: 'ai' | 'human';
  status: 'pending' | 'in_progress' | 'approved' | 'rejected';
  riskScore: number | null;
  securityFindings: {
    findings: ReviewFinding[];
    summary: ReviewFindingSummary;
  } | null;
  policyFindings: {
    findings: ReviewFinding[];
    summary: ReviewFindingSummary;
  } | null;
  qualityFindings: {
    findings: ReviewFinding[];
    summary: ReviewFindingSummary;
  } | null;
  reviewedBy: string | null;
  humanNotes: string | null;
  reviewCostCents: number | null;
  createdAt: string;
  completedAt: string | null;
}

export interface ClaudeMessageResponse {
  message: string;
  artifacts: BuilderArtifact[];
  tokensUsed: {
    input: number;
    output: number;
  };
  suggestedNextSteps?: string[];
  creditsCost: number;
  validationWarnings?: string[];
}

// ==========================================================================
// Leads AI Types
// ==========================================================================

export interface LeadsLeadScoreResult {
  contactId: string;
  score: number;
  reasoning: string;
}

export interface LeadsAISummaryResult {
  entityId: string;
  entityType: 'contact' | 'company';
  summary: string;
}

export interface LeadsWinLikelihoodResult {
  dealId: string;
  likelihood: number;
  reasoning: string;
}

export interface LeadsSemanticSearchResult {
  id: string;
  name: string;
  similarity: number;
  metadata: Record<string, unknown>;
}

// ==========================================================================
// Leads Deal Risk Types
// ==========================================================================

export interface LeadsDealRiskAlert {
  id: string;
  name: string;
  value: number;
  currency: string;
  stageName: string;
  stageType: string;
  contactName: string | null;
  companyName: string | null;
  expectedCloseDate: string | null;
  daysInStage: number;
  lastActivityAt: string | null;
  daysSinceActivity: number | null;
  winLikelihood: number | null;
  riskLevel: 'low' | 'medium' | 'high';
  riskFlags: string[];
}

export interface LeadsDealRiskAssessment {
  deals: LeadsDealRiskAlert[];
  summary: { high: number; medium: number; low: number };
}

// ==========================================================================
// Leads Bulk Action Types
// ==========================================================================

export interface LeadsBulkContactUpdates {
  addTags?: string[];
  removeTags?: string[];
  lifecycleStage?: string;
  companyId?: string | null;
}

export interface LeadsBulkCompanyUpdates {
  addTags?: string[];
  removeTags?: string[];
  industry?: string;
}

// ==========================================================================
// Leads Deduplication Types
// ==========================================================================

export interface LeadsDuplicateGroup {
  matchType: 'email' | 'phone' | 'name';
  confidence: 'high' | 'medium';
  contacts: Array<{
    id: string;
    name: string;
    email: string | null;
    phone: string | null;
    companyName: string | null;
    lifecycleStage: string;
    createdAt: string;
  }>;
}

export interface LeadsMergeResult {
  primaryId: string;
  mergedCount: number;
  activitiesMoved: number;
  tasksMoved: number;
  dealsMoved: number;
}

// ==========================================================================
// Leads Saved Views Types
// ==========================================================================

export interface LeadsSavedView {
  id: string;
  entityType: string;
  name: string;
  filters: Record<string, unknown>;
  sortBy: string | null;
  sortDir: string;
  isDefault: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface LeadsCreateSavedViewInput {
  entityType: string;
  name: string;
  filters: Record<string, unknown>;
  sortBy?: string;
  sortDir?: string;
  isDefault?: boolean;
}

// ==========================================================================
// Leads Contact Relationship Types
// ==========================================================================

export type LeadsRelationshipType =
  | 'colleague'
  | 'manager'
  | 'reports_to'
  | 'referral'
  | 'spouse'
  | 'other';

export interface LeadsContactRelationship {
  id: string;
  fromContactId: string;
  fromContactName: string;
  toContactId: string;
  toContactName: string;
  relationshipType: LeadsRelationshipType;
  notes: string | null;
  createdAt: string;
}

export interface LeadsCreateRelationshipInput {
  toContactId: string;
  relationshipType: LeadsRelationshipType;
  notes?: string;
}

// ==========================================================================
// Email Types
// ==========================================================================

export interface EmailAccount {
  id: string;
  userId: string;
  emailAddress: string;
  displayName: string | null;
  isPrimary: boolean;
  status: 'active' | 'suspended' | 'deleted';
  createdAt: string;
  updatedAt: string;
}

export interface EmailCreateAccountInput {
  username: string;
  displayName?: string;
}

export interface EmailAccountSettings {
  id: string;
  accountId: string;
  theme: 'light' | 'dark' | 'system';
  readingPane: 'right' | 'bottom' | 'off';
  messagesPerPage: number;
  defaultSignatureId: string | null;
  undoSendDelaySeconds: number;
  conversationView: boolean;
  autoAdvance: 'next' | 'previous' | 'list';
  notificationMode: 'all' | 'important' | 'none';
  digestFrequency: 'realtime' | 'hourly' | 'daily' | 'off';
  timeFormat: '12h' | '24h';
  createdAt: string;
  updatedAt: string;
}

export interface EmailAccountSettingsInput {
  theme?: 'light' | 'dark' | 'system';
  readingPane?: 'right' | 'bottom' | 'off';
  messagesPerPage?: number;
  defaultSignatureId?: string | null;
  undoSendDelaySeconds?: number;
  conversationView?: boolean;
  autoAdvance?: 'next' | 'previous' | 'list';
  notificationMode?: 'all' | 'important' | 'none';
  digestFrequency?: 'realtime' | 'hourly' | 'daily' | 'off';
  timeFormat?: '12h' | '24h';
}

export interface EmailAccountModule {
  id: string;
  accountId: string;
  moduleName: string;
  enabled: boolean;
  config: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface EmailAddress {
  email: string;
  name?: string;
}

export interface EmailMessage {
  id: string;
  accountId: string;
  threadId: string;
  folderId: string;
  fromAddress: string;
  fromName: string | null;
  toAddresses: EmailAddress[];
  ccAddresses: EmailAddress[];
  bccAddresses: EmailAddress[];
  subject: string;
  bodyText: string | null;
  bodyHtml: string | null;
  snippet: string | null;
  isRead: boolean;
  isStarred: boolean;
  isDraft: boolean;
  importance: 'low' | 'normal' | 'high';
  inReplyTo: string | null;
  referencesChain: string[];
  hasAttachments: boolean;
  sentAt: string | null;
  receivedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface EmailAttachment {
  id: string;
  filename: string;
  mimeType: string;
  sizeBytes: number;
  contentId: string | null;
  isInline: boolean;
  createdAt: string;
}

export interface EmailLabelRef {
  id: string;
  name: string;
  color: string;
}

export interface EmailMessageDetail extends EmailMessage {
  attachments: EmailAttachment[];
  labels: EmailLabelRef[];
}

export interface EmailMessageListOptions {
  accountId: string;
  folderId?: string;
  labelId?: string;
  threadId?: string;
  isRead?: boolean;
  isStarred?: boolean;
  importance?: 'low' | 'normal' | 'high';
  limit?: number;
  offset?: number;
  sort?: string;
}

export interface EmailMessageListResult {
  messages: EmailMessage[];
  total: number;
  limit: number;
  offset: number;
}

export interface EmailComposeInput {
  accountId: string;
  threadId?: string;
  folderId?: string;
  toAddresses: EmailAddress[];
  ccAddresses?: EmailAddress[];
  bccAddresses?: EmailAddress[];
  subject: string;
  bodyText?: string;
  bodyHtml?: string;
  isDraft?: boolean;
  importance?: 'low' | 'normal' | 'high';
  scheduledFor?: string;
}

export interface EmailMessageUpdateInput {
  isRead?: boolean;
  isStarred?: boolean;
  folderId?: string;
  importance?: 'low' | 'normal' | 'high';
}

export interface EmailReplyInput {
  accountId: string;
  bodyText?: string;
  bodyHtml?: string;
  ccAddresses?: EmailAddress[];
  bccAddresses?: EmailAddress[];
  replyAll?: boolean;
}

export interface EmailForwardInput {
  accountId: string;
  toAddresses: EmailAddress[];
  bodyText?: string;
  bodyHtml?: string;
}

export interface EmailBulkActionInput {
  accountId: string;
  action:
    | 'read'
    | 'unread'
    | 'star'
    | 'unstar'
    | 'archive'
    | 'trash'
    | 'move'
    | 'label'
    | 'unlabel';
  messageIds: string[];
  folderId?: string;
  labelId?: string;
}

export interface EmailThread {
  id: string;
  accountId: string;
  subject: string;
  lastMessageAt: string | null;
  messageCount: number;
  isRead: boolean;
  isStarred: boolean;
  snippet: string | null;
  smartCategory: 'primary' | 'feed' | 'paper_trail' | 'notifications' | null;
  createdAt: string;
  updatedAt: string;
}

export interface EmailThreadDetail extends EmailThread {
  messages: (EmailMessage & { attachments: EmailAttachment[] })[];
}

export interface EmailThreadListOptions {
  accountId: string;
  folderId?: string;
  labelId?: string;
  isRead?: boolean;
  isStarred?: boolean;
  smartCategory?: string;
  limit?: number;
  offset?: number;
  sort?: string;
}

export interface EmailThreadListResult {
  threads: EmailThread[];
  total: number;
  limit: number;
  offset: number;
}

export interface EmailThreadUpdateInput {
  isRead?: boolean;
  isStarred?: boolean;
  smartCategory?: 'primary' | 'feed' | 'paper_trail' | 'notifications' | null;
}

export interface EmailBulkThreadActionInput {
  accountId: string;
  action: 'read' | 'unread' | 'star' | 'unstar' | 'archive' | 'trash';
  threadIds: string[];
}

export interface EmailFolderBase {
  id: string;
  accountId: string;
  name: string;
  folderType: 'system' | 'custom';
  systemRole: 'inbox' | 'sent' | 'drafts' | 'trash' | 'spam' | 'archive' | null;
  color: string | null;
  sortOrder: number;
  parentFolderId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface EmailFolder extends EmailFolderBase {
  unreadCount: number;
  totalCount: number;
}

export interface EmailCreateFolderInput {
  accountId: string;
  name: string;
  color?: string;
  sortOrder?: number;
  parentFolderId?: string;
}

export interface EmailUpdateFolderInput {
  name?: string;
  color?: string;
  sortOrder?: number;
}

export interface EmailLabelBase {
  id: string;
  accountId: string;
  name: string;
  color: string;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface EmailLabel extends EmailLabelBase {
  messageCount: number;
}

export interface EmailCreateLabelInput {
  accountId: string;
  name: string;
  color?: string;
  sortOrder?: number;
}

export interface EmailUpdateLabelInput {
  name?: string;
  color?: string;
  sortOrder?: number;
}

// Email Contacts

export interface EmailContact {
  id: string;
  accountId: string;
  emailAddress: string;
  displayName: string | null;
  firstName: string | null;
  lastName: string | null;
  company: string | null;
  phone: string | null;
  notes: string | null;
  avatarUrl: string | null;
  isFavorite: boolean;
  lastContactedAt: string | null;
  contactCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface EmailContactListOptions {
  accountId: string;
  search?: string;
  isFavorite?: boolean;
  limit?: number;
  offset?: number;
  sort?: 'name' | 'lastContacted' | 'contactCount';
}

export interface EmailContactListResult {
  contacts: EmailContact[];
  total: number;
  limit: number;
  offset: number;
}

export interface EmailCreateContactInput {
  accountId: string;
  emailAddress: string;
  displayName?: string;
  firstName?: string;
  lastName?: string;
  company?: string;
  phone?: string;
  notes?: string;
  avatarUrl?: string;
  isFavorite?: boolean;
}

export interface EmailUpdateContactInput {
  emailAddress?: string;
  displayName?: string;
  firstName?: string;
  lastName?: string;
  company?: string;
  phone?: string;
  notes?: string;
  avatarUrl?: string;
  isFavorite?: boolean;
}

export interface EmailContactGroup {
  id: string;
  accountId: string;
  name: string;
  description: string | null;
  memberCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface EmailCreateContactGroupInput {
  accountId: string;
  name: string;
  description?: string;
}

export interface EmailUpdateContactGroupInput {
  name?: string;
  description?: string;
}

export interface EmailContactGroupMember extends EmailContact {
  addedAt: string;
}

// Email Filters

export interface EmailFilterCondition {
  field: 'from' | 'to' | 'subject' | 'body' | 'has_attachment';
  operator: 'contains' | 'not_contains' | 'equals' | 'starts_with' | 'ends_with' | 'matches_regex';
  value: string;
}

export interface EmailFilterAction {
  type:
    | 'move_to_folder'
    | 'add_label'
    | 'mark_read'
    | 'mark_starred'
    | 'archive'
    | 'trash'
    | 'forward_to'
    | 'set_importance'
    | 'set_category';
  value: string;
}

export interface EmailFilter {
  id: string;
  accountId: string;
  name: string;
  conditions: EmailFilterCondition[];
  actions: EmailFilterAction[];
  isEnabled: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface EmailCreateFilterInput {
  accountId: string;
  name: string;
  conditions: EmailFilterCondition[];
  actions: EmailFilterAction[];
  isEnabled?: boolean;
  sortOrder?: number;
}

export interface EmailUpdateFilterInput {
  name?: string;
  conditions?: EmailFilterCondition[];
  actions?: EmailFilterAction[];
  isEnabled?: boolean;
  sortOrder?: number;
}

// Email Templates

export interface EmailTemplate {
  id: string;
  accountId: string;
  name: string;
  subject: string | null;
  bodyHtml: string;
  category: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface EmailCreateTemplateInput {
  accountId: string;
  name: string;
  subject?: string;
  bodyHtml: string;
  category?: string;
}

export interface EmailUpdateTemplateInput {
  name?: string;
  subject?: string;
  bodyHtml?: string;
  category?: string;
}

// Email Snippets

export interface EmailSnippet {
  id: string;
  accountId: string;
  trigger: string;
  body: string;
  createdAt: string;
  updatedAt: string;
}

export interface EmailCreateSnippetInput {
  accountId: string;
  trigger: string;
  body: string;
}

export interface EmailUpdateSnippetInput {
  trigger?: string;
  body?: string;
}

// Email Signatures

export interface EmailSignature {
  id: string;
  accountId: string;
  name: string;
  bodyHtml: string;
  isDefault: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface EmailCreateSignatureInput {
  accountId: string;
  name: string;
  bodyHtml: string;
  isDefault?: boolean;
  sortOrder?: number;
}

export interface EmailUpdateSignatureInput {
  name?: string;
  bodyHtml?: string;
  isDefault?: boolean;
  sortOrder?: number;
}

// Email Search

export interface EmailSearchOptions {
  accountId: string;
  q?: string;
  from?: string;
  to?: string;
  subject?: string;
  hasAttachment?: boolean;
  isRead?: boolean;
  isStarred?: boolean;
  folder?: string;
  label?: string;
  dateFrom?: string;
  dateTo?: string;
  importance?: 'low' | 'normal' | 'high';
  limit?: number;
  offset?: number;
}

export interface EmailSearchResult {
  messages: EmailMessage[];
  total: number;
  limit: number;
  offset: number;
}

export interface EmailNaturalSearchResult extends EmailSearchResult {
  parsedFilters: Record<string, string>;
}

// Email Screener

export interface EmailScreenedSender {
  id: string;
  accountId: string;
  senderAddress: string;
  senderName: string | null;
  status: 'pending' | 'approved' | 'rejected';
  screenedAt: string | null;
  decidedAt: string | null;
  createdAt: string;
}

export interface EmailScreenedSenderTrigger {
  id: string;
  subject: string;
  snippet: string | null;
  fromAddress: string;
  fromName: string | null;
  createdAt: string;
}

export interface EmailScreenedSenderWithMessage extends EmailScreenedSender {
  triggeringMessage: EmailScreenedSenderTrigger | null;
}

export interface EmailScreenerApproveResult {
  sender: EmailScreenedSender;
  movedMessages: number;
}

export interface EmailScreenerRejectResult {
  sender: EmailScreenedSender;
  deletedMessages: number;
}

// Email Scheduling

export interface EmailScheduledMessage {
  id: string;
  messageId: string;
  scheduledFor: string;
  status: 'pending' | 'sent' | 'cancelled';
  createdAt: string;
  updatedAt: string;
}

export interface EmailScheduleInput {
  accountId: string;
  messageId: string;
  scheduledFor: string;
}

export interface EmailSnoozedMessage {
  id: string;
  messageId: string;
  snoozeUntil: string;
  originalFolderId: string | null;
  status: 'snoozed' | 'returned' | 'cancelled';
  createdAt: string;
  updatedAt: string;
}

export interface EmailSnoozeInput {
  accountId: string;
  messageId: string;
  snoozeUntil: string;
}

// Email AI

export interface EmailAIComposeInput {
  accountId: string;
  prompt: string;
  context?: {
    replyToMessageId?: string;
    tone?: 'formal' | 'casual' | 'friendly' | 'professional';
    length?: 'short' | 'medium' | 'long';
  };
}

export interface EmailAIComposeResult {
  subject: string;
  bodyHtml: string;
}

export interface EmailAIReplySuggestion {
  label: string;
  bodyHtml: string;
}

export interface EmailAISummarizeResult {
  summary: string;
  actionItems: string[];
  keyDecisions: string[];
}

export interface EmailAICategoryResult {
  messageId: string;
  category: 'primary' | 'feed' | 'paper_trail' | 'notifications';
}

export interface EmailAIPhishingResult {
  risk: 'low' | 'medium' | 'high';
  signals: string[];
}

// Email Attachments

export interface EmailAttachmentMeta {
  id: string;
  messageId: string | null;
  filename: string;
  mimeType: string;
  sizeBytes: number;
  storageKey: string;
  contentId: string | null;
  isInline: boolean;
  createdAt: string;
}

export interface EmailAttachmentUploadInput {
  accountId: string;
  messageId?: string;
  filename: string;
  mimeType: string;
  sizeBytes: number;
  contentId?: string;
  isInline?: boolean;
}

export interface EmailAttachmentUploadResult {
  attachment: EmailAttachmentMeta;
  uploadUrl: string;
  expiresAt: string;
}

export interface EmailAttachmentDownloadResult {
  downloadUrl: string;
  expiresAt: string;
}
